import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useTranslatedDomains } from '../hooks/useTranslatedDomains';
import useIsMobile from '../hooks/useIsMobile';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, Cell,
  ReferenceLine, ScatterChart, Scatter
} from "recharts";

/* ═══════════════════════════════════════════════════════════════════════════
   LOGOS HUMANO v2.0 — Sistema Operativo de Consciencia Integral
   REFACTORED: Imports from modular core engine (src/core/, src/simulation/, src/domain/)
   Authors: Dr. José Manuel Cadena Ortiz de Montellano
   Framework: Cadena Strategic Systems
   ═══════════════════════════════════════════════════════════════════════════ */

// ═══ MODULAR CORE IMPORTS ═══
import {
  computeLogosAlignment,
  computeFreeEnergy,
  computeLevinSignals,
  computeWatsonEnergy,
  computeHoffmanInterface,
  computeCoherence,
  computeViability,
  computeEntropy,
  computeOmega,
  computeQ,
  inhibitionPolicy,
  metaPolicy,
  getAlimentoRecommendations,
  LOGOS_COMPONENTS,
  VIABILITY_WEIGHTS,
  ENTROPY_FACTORS,
} from '../core';
import { useCustomWeights, DEFAULT_WEIGHTS } from '../hooks/useCustomWeights';
import { useBayesianWeights } from '../hooks/useBayesianWeights';
import { useHealthKit } from '../hooks/useHealthKit';
import { useSubscription } from '../hooks/useSubscription';
import { processWatchBiometrics } from '../core/healthkit-bridge';
import { trackAndAnalyze } from '../core/trend-analyzer';

import { runMonteCarlo, projectTrajectory } from '../simulation';
import { DOMAINS, DEFAULT_STATE, COLORS, FONTS, V_STAR, S_STAR, LAMBDA_STAR } from '../domain';

import { THEORY_MANUAL } from './TheoryManual';
import LogosVoiceAgent from './LogosVoiceAgent';
import EmergenceReporter from './EmergenceReporter';
import LearningMoment, { checkLearningTrigger } from './LearningMoment';
import MicroLesson, { shouldShowLesson } from './MicroLesson';
import WeeklyDigest, { shouldShowDigest, initDigestTracking } from './WeeklyDigest';
import { recordDailyVisit, getStreak } from '../hooks/useStreak';
import { checkAndAwardBadges, getAllBadges, getEarnedCount, BADGE_DEFS } from '../hooks/useBadges';
import { computeERS } from '../core/protocol-engine';
import {
  computeEmergenceIndex,
  computeMultiScaleLambda,
  computeCriticalityAlert,
  computeEnergyLandscape,
  computeCoherenceMatrix,
  computeShannonSNR,
} from '../core/emergence-metrics';
import EnergyLandscapeMap from './EnergyLandscapeMap';
import MonteCarloAnimationERS from './MonteCarloAnimation';
import {
  SNRGauge,
  MultiScaleLambdaCard,
  CriticalityAlertBanner,
  CoherenceMatrixHeatmap,
  EmergenceIndexCard,
} from './EmergenceVisualizations';
import EmergenceInterpretation from './EmergenceInterpretation';
import TabAIGuide from './TabAIGuide';
import EmergenceCascade from './EmergenceCascade';
import EmergenceSwarmAnimation from './EmergenceSwarmAnimation';
import PositiveGeometryCard from './PositiveGeometryCard';
import EmergenceGateDashboard from './EmergenceGateDashboard';
import DailyCheckin from './DailyCheckin';
import PaywallModal, { PremiumLockBanner } from './PaywallModal';
import SessionTracker, { startSession, endSession, saveSessionSnapshot } from './SessionTracker';
import { saveSession as saveSessionToDb } from '../services/api';
import { getApiBaseUrl } from '../utils/api-base';
import { supabase as supabaseClient } from '../services/supabase';
import KairaPanel from './kaira/KairaPanel';
import { computeLogosBridge } from '../lib/kaira/logos-integration/logos-bridge';

const fonts = FONTS;
const BACKEND_URL = import.meta.env.VITE_API_URL || 'https://logoilab.com/api';
const API_BASE = getApiBaseUrl();

function PanelWithAI({ title, icon, accent, panel, data, children, aiConsent = false, onRequestConsent }) {
  const mobile = useIsMobile();
  const { t } = useTranslation();
  const [interp, setInterp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cacheKey = `logos_panel_${panel}`;

  useEffect(() => {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const p = JSON.parse(cached);
        if (Date.now() - p.ts < 600000) setInterp(p.result);
      } catch {}
    }
  }, []);

  const generate = async () => {
    if (!aiConsent) {
      if (onRequestConsent) onRequestConsent();
      return;
    }
    setLoading(true); setError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/protocol/interpret-panel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ panel, data, language: i18n.language || 'es' }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      if (json.result) {
        setInterp(json.result);
        sessionStorage.setItem(cacheKey, JSON.stringify({ result: json.result, ts: Date.now() }));
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      background: "rgba(255,255,255,0.03)", border: `1px solid ${accent}30`,
      borderRadius: 16, overflow: "hidden",
    }}>
      <div style={{
        padding: "10px 16px", borderBottom: `1px solid ${accent}20`,
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: "0.12em", color: accent, textTransform: "uppercase", fontWeight: 600 }}>
          {title}
        </span>
      </div>
      <div className="logos-panel-with-ai" style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", minHeight: 280 }}>
        {/* LEFT: Visualization */}
        <div style={{ padding: 12, borderRight: `1px solid ${accent}15` }}>
          {children}
        </div>
        {/* RIGHT: AI Interpretation */}
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontFamily: fonts.mono, fontSize: 9, letterSpacing: "0.15em", color: "#C084FC" }}>
              INTERPRETACIÓN AI
            </span>
            <button onClick={generate} disabled={loading} style={{
              background: loading ? "rgba(192,132,252,0.1)" : "linear-gradient(135deg, #C084FC, #818CF8)",
              border: "none", borderRadius: 6, padding: "5px 12px", color: "#fff",
              fontFamily: fonts.mono, fontSize: 9, cursor: loading ? "wait" : "pointer",
              opacity: loading ? 0.6 : 1, letterSpacing: "0.05em",
            }}>
              {loading ? "⏳..." : interp ? "↻" : "✦ Interpretar"}
            </button>
          </div>

          {error && <div style={{ fontSize: 10, color: "#EF4444", fontFamily: fonts.mono }}>Error: {error}</div>}

          {!interp && !loading && !error && (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8, opacity: 0.5 }}>
              <span style={{ fontSize: 28 }}>🧠</span>
              <span style={{ fontFamily: fonts.mono, fontSize: 9, color: "#8B85A0", textAlign: "center" }}>
                {aiConsent
                  ? t('misc.gptAnalysis', { count: 28 })
                  : t('aiConsent.settingsDisabled')}
              </span>
            </div>
          )}

          {loading && (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", border: "2px solid rgba(192,132,252,0.2)", borderTopColor: "#C084FC", animation: "spin 1s linear infinite" }} />
            </div>
          )}

          {interp && !loading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, overflow: "auto" }}>
              {interp.explanation && (
                <div style={{ background: "rgba(192,132,252,0.06)", borderRadius: 8, padding: 10, borderLeft: "2px solid #C084FC" }}>
                  <div style={{ fontFamily: fonts.mono, fontSize: 8, color: "#C084FC", marginBottom: 4, letterSpacing: "0.1em" }}>QUÉ MUESTRA</div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#E8E4F0", margin: 0, lineHeight: 1.6 }}>{interp.explanation}</p>
                </div>
              )}
              {interp.insight && (
                <div style={{ background: "rgba(245,197,66,0.06)", borderRadius: 8, padding: 10, borderLeft: "2px solid #F5C542" }}>
                  <div style={{ fontFamily: fonts.mono, fontSize: 8, color: "#F5C542", marginBottom: 4, letterSpacing: "0.1em" }}>💡 INSIGHT</div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#E8E4F0", margin: 0, lineHeight: 1.6, fontWeight: 500 }}>{interp.insight}</p>
                </div>
              )}
              {interp.action && (
                <div style={{ background: "rgba(16,185,129,0.06)", borderRadius: 8, padding: 10, borderLeft: "2px solid #10B981" }}>
                  <div style={{ fontFamily: fonts.mono, fontSize: 8, color: "#10B981", marginBottom: 4, letterSpacing: "0.1em" }}>⚡ ACCIÓN</div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#E8E4F0", margin: 0, lineHeight: 1.6 }}>{interp.action}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function generateRecommendations(s, Lambda, V, S, C, signals, tDomains = DOMAINS, t = null) {
  const recs = [];
  const { domainScores } = computeWatsonEnergy(s, Lambda);
  const sorted = Object.entries(domainScores).sort((a, b) => a[1] - b[1]);
  const weakest = sorted[0];
  const D = tDomains || DOMAINS;
  
  signals.forEach(sig => {
    recs.push({ priority: 1, domain: "urgente", icon: sig.icon, text: sig.action, type: "URGENTE" });
  });
  
  const domainActions = {
    physical: t ? t('recs.physical') : "Hoy prioriza tu cuerpo: 20 min de ejercicio, comida nutritiva, agua, dormir 7h+.",
    emotional: t ? t('recs.emotional') : "Hoy nutre tus emociones: escribe 3 gratitudes, abraza a alguien, ríete.",
    mental: t ? t('recs.mental') : "Hoy cultiva tu mente: 25 min de deep work, lee algo desafiante, resuelve un problema.",
    spiritual: t ? t('recs.spiritual') : "Hoy conecta con el Logos: 10 min de silencio, lectura inspiracional, un acto de fe.",
    relational: t ? t('recs.relational') : "Hoy invierte en relaciones: llama a alguien, comparte una comida, escucha de verdad.",
    purpose: t ? t('recs.purpose') : "Hoy avanza tu propósito: una acción concreta hacia tu misión, reflexiona sobre tu legado.",
  };
  const weakLabel = D[weakest[0]]?.label || weakest[0];
  recs.push({ priority: 2, domain: weakest[0], icon: D[weakest[0]]?.icon || "◈", text: `${t ? t('dashboard.weakestArea', { domain: weakLabel }) : `Tu área más débil es ${weakLabel}.`} ${domainActions[weakest[0]]}`, type: "FORTALECER" });
  
  if (Lambda < 0.40) {
    recs.push({ priority: 2, domain: "logos", icon: "✦", text: t ? t('recs.logosWeak') : "Tu canal Logos está débil (Λ < 0.40). Dedica 10 minutos de silencio contemplativo. La señal divina requiere receptor abierto.", type: "LOGOS" });
  }
  if (S > 0.40) {
    recs.push({ priority: 3, domain: "equilibrio", icon: "⚖", text: t ? t('recs.entropyHigh') : "Tu entropía es alta. Simplifica: elige UNA prioridad para hoy. Menos es más.", type: "EQUILIBRIO" });
  }
  if (V > 0.60 && S < 0.35 && Lambda > 0.50) {
    recs.push({ priority: 4, domain: "crecimiento", icon: "🌱", text: t ? t('dashboard.growZone', { domain: weakLabel }) : `Estás en zona A⁺. Momento de crecer: profundiza en ${weakLabel} o explora algo nuevo.`, type: "CRECER" });
  }
  return recs.sort((a, b) => a.priority - b.priority).slice(0, 5);
}

/* ═══════════════════════════════════════════════════════════════════════════
   UI DESIGN SYSTEM v2.0 — Enhanced Contrast, Animations, Interactivity
   COLORS and fonts are now imported from '../domain' module
   ═══════════════════════════════════════════════════════════════════════════ */

// ─── CSS Animations (injected once) ───
const ANIM_STYLES = `
@keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
@keyframes glow { 0%, 100% { box-shadow: 0 0 5px rgba(192,132,252,0.2); } 50% { box-shadow: 0 0 20px rgba(192,132,252,0.4); } }
@keyframes breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.02); } }
@keyframes slideIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@keyframes countUp { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
`;

// ─── Card component with animation ───
const Card = ({ title, icon, accent, children, style = {}, delay = 0, onClick }) => (
  <div onClick={onClick} style={{
    background: COLORS.surface, border: `1px solid ${COLORS.border}`,
    borderRadius: 14, padding: 20, position: "relative", overflow: "hidden",
    animation: `fadeInUp 0.5s ease ${delay}s both`,
    transition: "all 0.3s ease",
    cursor: onClick ? "pointer" : "default",
    ...(accent ? { borderTop: `2px solid ${accent}50` } : {}),
    ...style,
  }}>
    {accent && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, ${accent}50, transparent)` }} />}
    {title && (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
        <span style={{ fontSize: 11, fontFamily: fonts.mono, color: accent || COLORS.textDim, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600 }}>{title}</span>
      </div>
    )}
    {children}
  </div>
);

// ─── Enhanced Metric with click-to-expand breakdown ───
function MetricCard({ label, value, color, tooltip, breakdown, suffix = "", onOpenDetail }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isClickable = !!(breakdown || onOpenDetail);
  const handleClick = () => {
    if (onOpenDetail) { onOpenDetail(); return; }
    if (breakdown) setExpanded(!expanded);
  };
  return (
    <div style={{ position: "relative" }}>
      <div onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          textAlign: "center", padding: "10px 14px", background: hovered && isClickable ? `${color}15` : COLORS.surface2,
          borderRadius: 10, minWidth: 85, cursor: isClickable ? "pointer" : "default",
          border: hovered && isClickable ? `1px solid ${color}50` : expanded ? `1px solid ${color}40` : `1px solid transparent`,
          transition: "all 0.25s ease", animation: "fadeInUp 0.4s ease both",
          boxShadow: hovered && isClickable ? `0 0 20px ${color}25, inset 0 0 15px ${color}08` : "none",
          transform: hovered && isClickable ? "translateY(-2px) scale(1.03)" : "none",
        }}
      >
        <div style={{ fontSize: 9, fontFamily: fonts.mono, color: hovered ? color : COLORS.textMuted, letterSpacing: 1, marginBottom: 4, transition: "color 0.2s" }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, fontFamily: fonts.mono, color, textShadow: hovered ? `0 0 35px ${color}60` : `0 0 25px ${color}40`, animation: "countUp 0.5s ease both", transition: "text-shadow 0.3s" }}>
          {typeof value === "number" ? value.toFixed(3) : value}{suffix}
        </div>
        {onOpenDetail && (
          <div style={{
            fontSize: 8, fontFamily: fonts.mono, color: hovered ? "#fff" : color, marginTop: 4,
            padding: "3px 8px", borderRadius: 6,
            background: hovered ? `${color}40` : `${color}12`,
            border: `1px solid ${hovered ? color : `${color}30`}`,
            transition: "all 0.25s ease", fontWeight: 600, letterSpacing: 0.5,
            animation: "breathe 3s ease infinite",
          }}>
            {t('metricBar.viewDetail')}
          </div>
        )}
        {!onOpenDetail && breakdown && <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>{t('metricBar.clickBreakdown')}</div>}
        {tooltip && <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 3, opacity: hovered ? 1 : 0, maxHeight: hovered ? 40 : 0, overflow: "hidden", transition: "all 0.3s ease", lineHeight: 1.3 }}>{tooltip}</div>}
      </div>
      {expanded && breakdown && !onOpenDetail && (
        <div style={{
          position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
          zIndex: 100, marginTop: 6, minWidth: 280, maxWidth: 340,
          background: COLORS.surface, border: `1px solid ${color}30`,
          borderRadius: 10, padding: 14, boxShadow: `0 8px 32px rgba(0,0,0,0.5)`,
          animation: "fadeInUp 0.2s ease",
        }}>
          <div style={{ fontSize: 10, fontFamily: fonts.mono, color, marginBottom: 8, fontWeight: 600 }}>
            {t('metricBar.breakdown')} {label}
          </div>
          {Array.isArray(breakdown) ? breakdown.slice(0, 8).map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", borderBottom: `1px solid ${COLORS.border}`, animation: `slideIn 0.2s ease ${i * 0.03}s both` }}>
              <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textDim }}>{item.label || item.key}</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{(item.value != null ? (item.value * 100).toFixed(0) + "%" : "")}</span>
                <span style={{ fontSize: 9, fontFamily: fonts.mono, color, fontWeight: 600 }}>{item.contribution != null ? (item.contribution >= 0 ? "+" : "") + item.contribution.toFixed(4) : ""}</span>
              </div>
            </div>
          )) : typeof breakdown === "object" && (
            Object.entries(breakdown).map(([key, group], i) => (
              <div key={key} style={{ marginBottom: 8, animation: `slideIn 0.2s ease ${i * 0.05}s both` }}>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 3, fontWeight: 600 }}>{group.label || key}</div>
                {group.items?.map((item, j) => (
                  <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "2px 8px", fontSize: 8, fontFamily: fonts.mono }}>
                    <span style={{ color: COLORS.textDim }}>{item.label} (w={item.weight})</span>
                    <span style={{ color }}>{((item.value || 0) * 100).toFixed(0)}% \u2192 +{(item.contribution || 0).toFixed(4)}</span>
                  </div>
                ))}
                <div style={{ fontSize: 8, fontFamily: fonts.mono, color, textAlign: "right", padding: "2px 8px", borderTop: `1px dotted ${COLORS.border}` }}>
                  {t('detail.subtotalGroup')} {(group.total || 0).toFixed(4)}
                </div>
              </div>
            ))
          )}
          <div onClick={() => setExpanded(false)} style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, textAlign: "center", marginTop: 6, cursor: "pointer" }}>
            ▲ {t('metricBar.close')}
          </div>
        </div>
      )}
    </div>
  );
}
// ─── METRIC DETAIL PANEL — Full-page overlay for deep factor analysis ───
function MetricDetailPanel({ metric, onClose }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const [renderError, setRenderError] = useState(null);
  
  // Escape key closes panel
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  if (!metric) return null;
  const { id, label, color, value, data } = metric;

  // Progress bar helper
  const Bar = ({ val, max = 1, color: c, height = 6 }) => (
    <div style={{ width: "100%", height, background: COLORS.surface3, borderRadius: 3, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(100, (val / max) * 100)}%`, height: "100%", background: c || color, borderRadius: 3, transition: "width 0.5s ease" }} />
    </div>
  );

  // Factor row helper
  const FactorRow = ({ label: fl, value: fv, weight, contribution, desc, idx = 0 }) => (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, animation: `slideIn 0.3s ease ${idx * 0.04}s both` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 500 }}>{fl}</span>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {weight != null && <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.weight')} {weight}</span>}
          <span style={{ fontSize: 13, fontFamily: fonts.mono, color, fontWeight: 700 }}>{(fv * 100).toFixed(1)}%</span>
        </div>
      </div>
      <Bar val={fv} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        {desc && <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, maxWidth: "70%" }}>{desc}</span>}
        {contribution != null && (
          <span style={{ fontSize: 10, fontFamily: fonts.mono, color: contribution >= 0 ? COLORS.viability : COLORS.entropy, fontWeight: 600 }}>
            {contribution >= 0 ? "+" : ""}{contribution.toFixed(4)}
          </span>
        )}
      </div>
    </div>
  );

  // Section header helper
  const SectionHead = ({ title, subtitle, icon }) => (
    <div style={{ marginBottom: 12, marginTop: 16 }}>
      <div style={{ fontSize: 12, fontFamily: fonts.mono, color, letterSpacing: 2, fontWeight: 700, textTransform: "uppercase" }}>
        {icon && <span style={{ marginRight: 8 }}>{icon}</span>}{title}
      </div>
      {subtitle && <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );

  // Modulation effect helper
  const ModulationBox = ({ label: ml, raw, modulated, factor, direction }) => (
    <div style={{ padding: 12, background: `${COLORS.logos}08`, borderRadius: 10, border: `1px solid ${COLORS.logos}20`, marginTop: 12 }}>
      <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 600, marginBottom: 8 }}>
        {t('detail.modulationTitle')}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: 8 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.rawValue')}</div>
          <div style={{ fontSize: 16, fontFamily: fonts.mono, color: COLORS.textDim, fontWeight: 700 }}>{raw.toFixed(3)}</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{direction === "reduce" ? t('detail.reduceLambda') : t('detail.boostLambda')}</div>
          <div style={{ fontSize: 16, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 700 }}>{direction === "reduce" ? "−" : "+"}{(factor * 100).toFixed(1)}%</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.finalValue')}</div>
          <div style={{ fontSize: 16, fontFamily: fonts.mono, color, fontWeight: 700 }}>{modulated.toFixed(3)}</div>
        </div>
      </div>
      <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 8, textAlign: "center", fontStyle: "italic" }}>
        {direction === "reduce"
          ? t('detail.modulateReduce')
          : t('detail.modulateBoost')}
      </div>
    </div>
  );

  // ═══ Render content based on metric type ═══
  const renderContent = () => {
    switch (id) {
      // ─── Λ LOGOS ───
      case "lambda": {
        const { breakdown } = data;
        return (
          <>
            <div style={{ padding: 14, background: `${COLORS.logos}08`, borderRadius: 10, border: `1px solid ${COLORS.logos}20`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 600 }}>{t('detail.formula')}</div>
              <div style={{ fontSize: 13, fontFamily: fonts.display, color: COLORS.text, marginTop: 6 }}>
                Λ(x) = Λ<sub>signal</sub> × Φ<sub>channel</sub>
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
                {t('detail.lambdaDesc1')}
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
                {t('detail.lambdaDesc2')}
              </div>
            </div>
            {Object.entries(breakdown).map(([groupKey, group], gi) => {
              const tGrp = t(`detail.lambdaGroups.${groupKey}`, { returnObjects: true });
              const grpLabel = (typeof tGrp === 'object' && tGrp?.label) || group.label;
              const grpTooltip = (typeof tGrp === 'object' && tGrp?.tooltip) || group.tooltip;
              return (
              <div key={groupKey} style={{ marginBottom: 20, animation: `fadeInUp 0.4s ease ${gi * 0.1}s both` }}>
                <SectionHead title={grpLabel} subtitle={grpTooltip} icon={gi === 0 ? "📡" : gi === 1 ? "⚡" : gi === 2 ? "🔑" : "🙏"} />
                <div style={{ padding: "0 4px" }}>
                  {group.items.map((item, i) => {
                    const tDim = t(`detail.lambdaDims.${item.key}`, { returnObjects: true });
                    const dimLabel = (typeof tDim === 'object' && tDim?.label) || item.label;
                    const dimDesc = (typeof tDim === 'object' && tDim?.desc) || item.desc;
                    return <FactorRow key={item.key} label={`${dimLabel} (${item.key})`} value={item.value}
                      weight={item.weight} contribution={item.contribution}
                      desc={dimDesc} idx={gi * 3 + i} />;
                  })}
                </div>
                <div style={{ textAlign: "right", padding: "8px 4px", borderTop: `1px dashed ${color}30` }}>
                  <span style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.subtotalGroup')} </span>
                  <span style={{ fontSize: 13, fontFamily: fonts.mono, color, fontWeight: 700 }}>{group.total.toFixed(4)}</span>
                </div>
              </div>
            );})}
            {/* ─── CHANNEL QUALITY (Φ) — Hierarchical Lambda ─── */}
            {data.channel && (
              <div style={{ marginTop: 16, padding: 14, background: '#0a1520', borderRadius: 12, border: '1px solid #1a3050' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontFamily: fonts.mono, fontWeight: 700, color: '#48b8a0', letterSpacing: 1 }}>
                    CALIDAD DEL CANAL (Φ)
                  </div>
                  <div style={{ fontSize: 18, fontFamily: fonts.mono, fontWeight: 700, color: '#48b8a0' }}>
                    {data.channel.phi.toFixed(3)}
                  </div>
                </div>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 10 }}>
                  Atenuación: {data.hierarchical?.attenuation_pct.toFixed(1)}% ({data.channel.attenuation_dB.toFixed(2)} dB) · γ = {data.channel.gamma}
                </div>
                {data.channel.breakdown.map((group) => (
                  <div key={group.key} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontFamily: fonts.mono, marginBottom: 3 }}>
                      <span style={{ color: '#a0a5c0' }}>{group.label} (α={group.weight})</span>
                      <span style={{ color: '#48b8a0', fontWeight: 600 }}>{(group.average * 100).toFixed(1)}%</span>
                    </div>
                    <div style={{ height: 4, background: '#1e2540', borderRadius: 2 }}>
                      <div style={{ width: `${group.average * 100}%`, height: '100%', background: '#48b8a0', borderRadius: 2, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 12, padding: '10px 12px', background: '#0d1a28', borderRadius: 8, border: '1px solid #1a3050' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr', alignItems: 'center', textAlign: 'center', gap: 4 }}>
                    <div>
                      <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>Λ_signal</div>
                      <div style={{ fontSize: 15, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 700 }}>{data.signal?.toFixed(3)}</div>
                    </div>
                    <div style={{ fontSize: 14, color: COLORS.textMuted }}>×</div>
                    <div>
                      <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>Φ_channel</div>
                      <div style={{ fontSize: 15, fontFamily: fonts.mono, color: '#48b8a0', fontWeight: 700 }}>{data.channel.phi.toFixed(3)}</div>
                    </div>
                    <div style={{ fontSize: 14, color: COLORS.textMuted }}>=</div>
                    <div>
                      <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>Λ(x)</div>
                      <div style={{ fontSize: 15, fontFamily: fonts.mono, color, fontWeight: 700 }}>{data.value.toFixed(3)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      }

      // ─── Ω CONSCIENCIA ───
      case "omega": {
        const { breakdown: bd, lambdaContribution, derivedContribution, derivedHealth } = data;
        return (
          <>
            <div style={{ padding: 14, background: `${COLORS.omega}08`, borderRadius: 10, border: `1px solid ${COLORS.omega}20`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.omega, fontWeight: 600 }}>{t('detail.formula')}</div>
              <div style={{ fontSize: 13, fontFamily: fonts.display, color: COLORS.text, marginTop: 6 }}>
                {t('detail.omegaFormula')}
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
                {t('detail.omegaDesc')}
              </div>
            </div>
            <SectionHead title={t('detail.omegaComposition')} icon="⚖" />
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div style={{ padding: 12, background: COLORS.surface2, borderRadius: 10, textAlign: "center" }}>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.omegaLambda')}</div>
                <div style={{ fontSize: 24, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 700 }}>{lambdaContribution.toFixed(3)}</div>
              </div>
              <div style={{ padding: 12, background: COLORS.surface2, borderRadius: 10, textAlign: "center" }}>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.omegaDerived')}</div>
                <div style={{ fontSize: 24, fontFamily: fonts.mono, color: COLORS.accent, fontWeight: 700 }}>{derivedContribution.toFixed(3)}</div>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>{t('detail.health')} = {derivedHealth.toFixed(3)}</div>
              </div>
            </div>
            <SectionHead title={t('detail.omegaContribution')} icon="📊" />
            {bd.map((item, i) => (
              <div key={i} style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, animation: `slideIn 0.3s ease ${i * 0.05}s both` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontFamily: fonts.mono, color: item.color, fontWeight: 500 }}>{t(`detail.omegaLabels.${i}`, item.label)}</span>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <span style={{ fontSize: 12, fontFamily: fonts.mono, color: COLORS.textDim }}>{item.value.toFixed(3)}</span>
                    <span style={{ fontSize: 13, fontFamily: fonts.mono, color: item.contribution >= 0 ? COLORS.viability : COLORS.entropy, fontWeight: 700 }}>
                      {item.contribution >= 0 ? "+" : ""}{item.contribution.toFixed(4)}
                    </span>
                  </div>
                </div>
                <Bar val={Math.abs(item.contribution)} max={0.4} color={item.color} />
              </div>
            ))}
          </>
        );
      }

      // ─── V VIABILIDAD ───
      case "viability": {
        const { breakdown: bd, raw, lambdaBoost } = data;
        return (
          <>
            <div style={{ padding: 14, background: `${COLORS.viability}08`, borderRadius: 10, border: `1px solid ${COLORS.viability}20`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.viability, fontWeight: 600 }}>{t('detail.formula')}</div>
              <div style={{ fontSize: 13, fontFamily: fonts.display, color: COLORS.text, marginTop: 6 }}>
                V(x) = g(Λ) + ε<sub>physical</sub> = V_raw × (0.75 + Λ × 0.35)
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
                {t('detail.viabilityDesc')}
              </div>
            </div>
            <ModulationBox raw={raw} modulated={value} factor={lambdaBoost} direction="boost" />
            <SectionHead title={t('detail.viabilityFactors')} subtitle={t('detail.viabilityFactorsSub')} icon="📋" />
            {bd.map((item, i) => (
              <FactorRow key={item.key} label={item.label} value={item.value}
                weight={item.weight} contribution={item.contribution} idx={i} />
            ))}
          </>
        );
      }

      // ─── S ENTROPÍA ───
      case "entropy": {
        const { breakdown: bd, raw, lambdaEffect } = data;
        return (
          <>
            <div style={{ padding: 14, background: `${COLORS.entropy}08`, borderRadius: 10, border: `1px solid ${COLORS.entropy}20`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.entropy, fontWeight: 600 }}>{t('detail.formula')}</div>
              <div style={{ fontSize: 13, fontFamily: fonts.display, color: COLORS.text, marginTop: 6 }}>
                S(x) = (1 − Λ) · k + ε = S_raw × (1.2 − Λ × 0.4)
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
                {t('detail.entropyDesc')}
              </div>
            </div>
            <ModulationBox raw={raw} modulated={value} factor={lambdaEffect} direction="reduce" />
            <SectionHead title={t('detail.entropyFactors')} subtitle={t('detail.entropyFactorsSub')} icon="🌊" />
            {bd.map((item, i) => (
              <div key={item.key} style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, animation: `slideIn 0.3s ease ${i * 0.04}s both` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 500 }}>{t(`detail.entropyLabels.${item.key}`, item.label)}</span>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.weight')} {item.weight}</span>
                    <span style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.textDim }}>{t('detail.actual')} {(item.value * 100).toFixed(0)}%</span>
                    <span style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.entropy, fontWeight: 700 }}>{t('detail.lack')} {((1 - item.value) * 100).toFixed(0)}%</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <Bar val={1 - item.value} color={COLORS.entropy} />
                  </div>
                  <span style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.entropy, fontWeight: 600, minWidth: 55, textAlign: "right" }}>
                    +{item.contribution.toFixed(4)}
                  </span>
                </div>
              </div>
            ))}
          </>
        );
      }

      // ─── C COHERENCIA ───
      case "coherence": {
        const { breakdown: bd, rawCoherence, lambdaBoost, entropy: intEntropy } = data;
        return (
          <>
            <div style={{ padding: 14, background: `${COLORS.coherence}08`, borderRadius: 10, border: `1px solid ${COLORS.coherence}20`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.coherence, fontWeight: 600 }}>{t('detail.formula')}</div>
              <div style={{ fontSize: 13, fontFamily: fonts.display, color: COLORS.text, marginTop: 6 }}>
                C(x) = H(dominios) × (0.7 + Λ × 0.4)
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
                {t('detail.coherenceDesc')}
              </div>
            </div>
            <ModulationBox raw={rawCoherence} modulated={value} factor={lambdaBoost} direction="boost" />
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 12, margin: "16px 0" }}>
              <div style={{ padding: 12, background: COLORS.surface2, borderRadius: 10, textAlign: "center" }}>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.harmonicMean')}</div>
                <div style={{ fontSize: 20, fontFamily: fonts.mono, color: COLORS.coherence, fontWeight: 700 }}>{rawCoherence.toFixed(3)}</div>
                <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>H = n / Σ(1/x_i)</div>
              </div>
              <div style={{ padding: 12, background: COLORS.surface2, borderRadius: 10, textAlign: "center" }}>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.internalEntropy')}</div>
                <div style={{ fontSize: 20, fontFamily: fonts.mono, color: COLORS.entropy, fontWeight: 700 }}>{intEntropy.toFixed(3)}</div>
                <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>√varianza × 3</div>
              </div>
            </div>
            <SectionHead title={t('detail.coherenceDomains')} subtitle={t('detail.coherenceDomainsSub')} icon="⬡" />
            {bd.map((item, i) => (
              <div key={i} style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, animation: `slideIn 0.3s ease ${i * 0.05}s both` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontFamily: fonts.mono, color: item.color, fontWeight: 600 }}>{t(`detail.coherenceDomainLabels.${i}`, item.label)}</span>
                  <span style={{ fontSize: 16, fontFamily: fonts.mono, color: item.color, fontWeight: 700 }}>{(item.value * 100).toFixed(1)}%</span>
                </div>
                <Bar val={item.value} color={item.color} height={8} />
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 4 }}>
                  {item.value < 0.35 ? t('detail.domainCritical') :
                   item.value < 0.50 ? t('detail.domainAttention') :
                   item.value >= 0.70 ? t('detail.domainStrong') : t('detail.domainStable')}
                </div>
              </div>
            ))}
          </>
        );
      }

      // ─── F ENERGÍA LIBRE ───
      case "freeEnergy": {
        const { breakdown: bd, raw, modulated, lambdaEffect } = data;
        const topDeviations = [...bd].sort((a, b) => b.contribution - a.contribution).slice(0, 12);
        return (
          <>
            <div style={{ padding: 14, background: `${COLORS.freeEnergy}08`, borderRadius: 10, border: `1px solid ${COLORS.freeEnergy}20`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.freeEnergy, fontWeight: 600 }}>{t('detail.freeEnergyLabel')}</div>
              <div style={{ fontSize: 13, fontFamily: fonts.display, color: COLORS.text, marginTop: 6 }}>
                F(x) = D<sub>KL</sub>(q || p<sub>Logos</sub>) = Σ w_i · (actual − ideal)² / (2σ²)
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
                {t('detail.freeEnergyDesc')}
              </div>
            </div>
            <ModulationBox raw={raw} modulated={modulated} factor={lambdaEffect} direction="reduce" />
            <SectionHead title={t('detail.freeEnergyTop')} subtitle={t('detail.freeEnergyTopSub')} icon="📐" />
            {topDeviations.map((item, i) => {
              const dimInfo = Object.entries(DOMAINS).reduce((found, [_, dv]) => found || dv.dims[item.key], null);
              const gap = item.actual - item.ideal;
              return (
                <div key={item.key} style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, animation: `slideIn 0.3s ease ${i * 0.04}s both` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 500 }}>{dimInfo?.label || item.key}</span>
                    <span style={{ fontSize: 10, fontFamily: fonts.mono, color: gap < 0 ? COLORS.entropy : COLORS.viability, fontWeight: 600 }}>
                      gap: {gap >= 0 ? "+" : ""}{(gap * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 4 }}>
                    <span>{t('detail.actual')} {(item.actual * 100).toFixed(0)}%</span>
                    <span>→</span>
                    <span style={{ color: COLORS.logos }}>ideal: {(item.ideal * 100).toFixed(0)}%</span>
                    <span style={{ marginLeft: "auto", color: COLORS.freeEnergy, fontWeight: 600 }}>F += {item.contribution.toFixed(5)}</span>
                  </div>
                  <div style={{ position: "relative", height: 8, background: COLORS.surface3, borderRadius: 4 }}>
                    <div style={{ position: "absolute", left: `${item.ideal * 100}%`, top: -2, width: 2, height: 12, background: COLORS.logos, borderRadius: 1 }} title="ideal" />
                    <div style={{ position: "absolute", left: `${Math.min(item.actual, item.ideal) * 100}%`, width: `${Math.abs(gap) * 100}%`, height: "100%", background: gap < 0 ? `${COLORS.entropy}50` : `${COLORS.viability}50`, borderRadius: 4 }} />
                    <div style={{ position: "absolute", left: `${item.actual * 100}%`, top: -1, width: 6, height: 10, background: color, borderRadius: 3 }} title="actual" />
                  </div>
                </div>
              );
            })}
          </>
        );
      }

      // ─── Q TRAYECTORIA ───
      case "trajectory": {
        const { distanceToAttractor, breakdown: bd } = data;
        return (
          <>
            <div style={{ padding: 14, background: `${COLORS.accent}08`, borderRadius: 10, border: `1px solid ${COLORS.accent}20`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.accent, fontWeight: 600 }}>{t('detail.formula')}</div>
              <div style={{ fontSize: 13, fontFamily: fonts.display, color: COLORS.text, marginTop: 6 }}>
                Q = Λ×0.4 + V×0.2 − S×0.2 − D(A⁺)×0.3
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
                {t('detail.trajectoryDesc')}
              </div>
            </div>
            <SectionHead title={t('detail.distanceTitle')} subtitle={`D = √(dV² + dS² + dΛ²) = ${distanceToAttractor.toFixed(4)}`} icon="🎯" />
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
              {[
                { label: t('detail.dV'), value: bd.dV, target: "V* = 0.70", c: COLORS.viability },
                { label: t('detail.dS'), value: bd.dS, target: "S* = 0.30", c: COLORS.entropy },
                { label: t('detail.dL'), value: bd.dL, target: "Λ* = 0.60", c: COLORS.logos },
              ].map((item, i) => (
                <div key={i} style={{ padding: 12, background: COLORS.surface2, borderRadius: 10, textAlign: "center", animation: `fadeInUp 0.3s ease ${i * 0.1}s both` }}>
                  <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>{item.label}</div>
                  <div style={{ fontSize: 20, fontFamily: fonts.mono, color: item.c, fontWeight: 700, margin: "4px 0" }}>
                    {item.value.toFixed(3)}
                  </div>
                  <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>{item.target}</div>
                  <Bar val={item.value} max={0.5} color={item.c} height={4} />
                </div>
              ))}
            </div>
            <div style={{ padding: 12, background: COLORS.surface2, borderRadius: 10, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.totalDistance')}</div>
              <div style={{ fontSize: 28, fontFamily: fonts.mono, color, fontWeight: 700 }}>{distanceToAttractor.toFixed(4)}</div>
              <Bar val={distanceToAttractor} max={0.6} color={color} height={6} />
              <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 6 }}>
                {distanceToAttractor < 0.10 ? t('detail.distVeryClose') :
                 distanceToAttractor < 0.25 ? t('detail.distModerate') :
                 distanceToAttractor < 0.40 ? t('detail.distSignificant') :
                 t('detail.distFar')}
              </div>
            </div>
          </>
        );
      }

      // ─── DOMAIN DETAIL (from Cascade click) ───
      case "domain": {
        const { domainKey, domainInfo, dimensions, domainAvg, watsonTarget, lambdaContributions } = data;
        return (
          <>
            <div style={{ padding: 14, background: `${color}08`, borderRadius: 10, border: `1px solid ${color}20`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color, fontWeight: 600 }}>
                {domainInfo?.icon} {domainInfo?.label || domainKey}
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
                Promedio del dominio: <strong style={{ color }}>{(domainAvg * 100).toFixed(1)}%</strong>
                {watsonTarget != null && <> · Watson target: <strong>{(watsonTarget * 100).toFixed(0)}%</strong></>}
              </div>
            </div>
            <SectionHead title="4 Dimensiones" subtitle="Toca para ajustar en el panel principal" icon={domainInfo?.icon || "◈"} />
            {dimensions.map((dim, i) => (
              <div key={dim.key} style={{ padding: "12px 0", borderBottom: `1px solid ${COLORS.border}`, animation: `slideIn 0.3s ease ${i * 0.06}s both` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div>
                    <span style={{ fontSize: 12, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 600 }}>{dim.label}</span>
                    {dim.desc && <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>{dim.desc}</div>}
                  </div>
                  <span style={{ fontSize: 16, fontFamily: fonts.mono, color, fontWeight: 700 }}>
                    {(dim.value * 100).toFixed(0)}%
                  </span>
                </div>
                <Bar val={dim.value} color={color} height={8} />
                {dim.lambdaWeight > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                    <span>{t('misc.weightInLambda', { weight: dim.lambdaWeight })}</span>
                    <span style={{ color: COLORS.logos }}>{t('misc.contribution', { value: (dim.value * dim.lambdaWeight).toFixed(4) })}</span>
                  </div>
                )}
              </div>
            ))}
            {watsonTarget != null && (
              <div style={{ marginTop: 16, padding: 12, background: COLORS.surface2, borderRadius: 10, textAlign: "center" }}>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('misc.watsonDistance')}</div>
                <div style={{ fontSize: 24, fontFamily: fonts.mono, color: domainAvg >= watsonTarget ? COLORS.viability : COLORS.entropy, fontWeight: 700, margin: "4px 0" }}>
                  {domainAvg >= watsonTarget ? "✦" : "△"} {((domainAvg - watsonTarget) * 100).toFixed(1)}%
                </div>
                <Bar val={domainAvg} color={color} height={6} />
                <div style={{ position: "relative", height: 2, marginTop: -6 }}>
                  <div style={{ position: "absolute", left: `${watsonTarget * 100}%`, top: -4, width: 2, height: 10, background: COLORS.logos, borderRadius: 1 }} title="Watson target" />
                </div>
                <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 8 }}>
                  {domainAvg >= watsonTarget ? t('misc.domainAligned') : t('misc.increaseDomain')}
                </div>
              </div>
            )}
          </>
        );
      }

      default:
        return <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('detail.noPanel')}</div>;
    }
  };

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      background: "rgba(6,6,15,0.95)", backdropFilter: "blur(12px)",
      overflowY: "auto", padding: "20px 16px",
      animation: "fadeInUp 0.3s ease",
    }}>
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <button onClick={onClose} style={{
            background: COLORS.surface2, border: `1px solid ${COLORS.border}`, borderRadius: 8,
            padding: "8px 16px", cursor: "pointer", fontFamily: fonts.mono, fontSize: 11,
            color: COLORS.textDim, transition: "all 0.2s",
          }}>
            {t('detail.back')}
          </button>
          <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 2 }}>
            {t('detail.panelTitle')}
          </div>
        </div>

        {/* Metric Value Hero */}
        <div style={{
          textAlign: "center", marginBottom: 24, padding: "20px 16px",
          background: `${color}08`, borderRadius: 14, border: `1px solid ${color}25`,
        }}>
          <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 2, marginBottom: 6 }}>{label}</div>
          <div style={{
            fontSize: 48, fontWeight: 700, fontFamily: fonts.mono, color,
            textShadow: `0 0 40px ${color}40`, lineHeight: 1,
          }}>
            {typeof value === "number" ? value.toFixed(3) : value}
          </div>
          <div style={{ marginTop: 8, height: 4, background: COLORS.surface3, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${Math.min(100, (typeof value === "number" ? value : 0) * 100)}%`, height: "100%", background: `linear-gradient(90deg, ${color}80, ${color})`, borderRadius: 2, transition: "width 0.5s" }} />
          </div>
          <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 8 }}>
            {typeof value === "number" && (
              value >= 0.70 ? t('detail.rangeExcellent') :
              value >= 0.50 ? t('detail.rangeFunctional') :
              value >= 0.35 ? t('detail.rangeLow') :
              t('detail.rangeCritical')
            )}
          </div>
        </div>

        {/* Dynamic Content */}
        {renderError ? (
          <div style={{ padding: 20, background: "#ef444415", borderRadius: 10, border: "1px solid #ef444430", textAlign: "center" }}>
            <div style={{ fontSize: 12, fontFamily: fonts.mono, color: "#ef4444", marginBottom: 8 }}>{t('detail.renderError')}</div>
            <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted }}>{String(renderError)}</div>
          </div>
        ) : (() => { try { return renderContent(); } catch(e) { setTimeout(() => setRenderError(e.message), 0); return null; } })()}

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 24, paddingTop: 16, borderTop: `1px solid ${COLORS.border}` }}>
          <button onClick={onClose} style={{
            background: `${color}15`, border: `1px solid ${color}30`, borderRadius: 10,
            padding: "10px 24px", cursor: "pointer", fontFamily: fonts.mono, fontSize: 11,
            color, fontWeight: 600, transition: "all 0.2s",
          }}>
            {t('detail.closePanel')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Emoji scale levels for slider ───
const EMOJI_LEVELS = [
  { emoji: "😰", label: "Crítico", labelEn: "Critical", max: 0.20 },
  { emoji: "😕", label: "Bajo", labelEn: "Low", max: 0.40 },
  { emoji: "😐", label: "Neutro", labelEn: "Neutral", max: 0.60 },
  { emoji: "😊", label: "Bien", labelEn: "Good", max: 0.80 },
  { emoji: "🌟", label: "Óptimo", labelEn: "Optimal", max: 1.01 },
];
const getEmojiLevel = (v) => EMOJI_LEVELS.findIndex(l => v < l.max);

// ─── Slider component with interactive tooltip + guide question + emoji scale ───
const Slider = ({ label, value, onChange, color, desc, tooltip, guideQuestion, dimKey }) => {
  const { t } = useTranslation();
  const [showTip, setShowTip] = useState(false);
  const tipRef = useRef(null);
  const levelIdx = getEmojiLevel(value);
  const anchorLevel = value < 0.35 ? 'low' : value > 0.65 ? 'high' : 'mid';
  const anchor = dimKey ? t(`sliderAnchors.${dimKey}.${anchorLevel}`, '') : '';

  useEffect(() => {
    if (!showTip) return;
    const handler = (e) => { if (tipRef.current && !tipRef.current.contains(e.target)) setShowTip(false); };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [showTip]);

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
        <span style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, display: "inline-flex", alignItems: "center", gap: 4, position: "relative" }} ref={tipRef}>
          {label}
          {tooltip && (
            <span
              onClick={(e) => { e.stopPropagation(); setShowTip(prev => !prev); }}
              onMouseEnter={() => setShowTip(true)} onMouseLeave={() => setShowTip(false)}
              style={{ cursor: "help", fontSize: 11, color: showTip ? color : COLORS.textMuted, opacity: showTip ? 1 : 0.6, transition: "all 0.2s", lineHeight: 1, userSelect: "none" }}
              role="button" aria-label="Info" tabIndex={0}>ⓘ</span>
          )}
          {showTip && tooltip && (
            <div style={{
              position: "absolute", bottom: "calc(100% + 6px)", left: 0,
              background: COLORS.surface2, border: `1px solid ${color}40`, borderRadius: 8,
              padding: "8px 10px", fontSize: 10, fontFamily: fonts.sans, color: COLORS.text,
              whiteSpace: "normal", width: 240, zIndex: 200,
              boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 0 1px ${color}20`,
              lineHeight: 1.4, letterSpacing: 0.2,
            }}>
              <div style={{ fontSize: 9, color, fontFamily: fonts.mono, marginBottom: 3, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
              {tooltip}
              <div style={{ position: "absolute", bottom: -5, left: 12, width: 8, height: 8, background: COLORS.surface2, border: `1px solid ${color}40`, borderTop: "none", borderLeft: "none", transform: "rotate(45deg)" }} />
            </div>
          )}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, opacity: 0.9, transition: "all 0.2s" }}>{EMOJI_LEVELS[levelIdx]?.emoji}</span>
          <span style={{ fontSize: 10, fontFamily: fonts.mono, color, fontWeight: 600 }}>{(value * 100).toFixed(0)}%</span>
        </div>
      </div>
      {guideQuestion && (
        <div style={{ fontSize: 9, fontFamily: fonts.sans, color: `${color}B0`, marginBottom: 4, fontStyle: "italic", lineHeight: 1.3 }}>
          {guideQuestion}
        </div>
      )}
      <input type="range" min="0" max="100" value={Math.round(value * 100)} onChange={e => onChange(parseInt(e.target.value) / 100)}
        style={{ width: "100%", height: 4, appearance: "none", background: `linear-gradient(to right, ${color}70 ${value * 100}%, ${COLORS.surface3} ${value * 100}%)`, borderRadius: 2, outline: "none", cursor: "pointer", accentColor: color }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2 }}>
        <div style={{ display: "flex", gap: 2 }}>
          {EMOJI_LEVELS.map((lvl, i) => (
            <span key={i} style={{ fontSize: 9, opacity: i === levelIdx ? 1 : 0.25, transition: "opacity 0.2s", cursor: "pointer" }}
              onClick={() => onChange((lvl.max - 0.01 + (i === 0 ? 0 : EMOJI_LEVELS[i - 1].max)) / 2)}
              title={lvl.label}>{lvl.emoji}</span>
          ))}
        </div>
        {desc && <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>{desc}</div>}
      </div>
      {anchor && (
        <div style={{ fontSize: 9, fontFamily: fonts.sans, color: COLORS.textDim, fontStyle: "italic", lineHeight: 1.35, marginTop: 2, padding: "3px 6px", background: `${color}08`, borderRadius: 6, borderLeft: `2px solid ${color}30` }}>
          "{anchor}"
        </div>
      )}
    </div>
  );
};

// ─── Tooltip component for inline help ───
const InfoTooltip = ({ text, children }) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!show) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [show]);

  return (
    <span style={{ position: "relative", display: "inline-block" }} ref={ref}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      <span onClick={(e) => { e.stopPropagation(); setShow(prev => !prev); }}
        style={{ cursor: "help", fontSize: 10, color: COLORS.textMuted, marginLeft: 3, userSelect: "none" }} role="button" tabIndex={0}>ⓘ</span>
      {show && (
        <div style={{
          position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)",
          background: COLORS.surface2, border: `1px solid ${COLORS.border}`, borderRadius: 8,
          padding: "8px 12px", fontSize: 9, fontFamily: fonts.sans, color: COLORS.textDim,
          whiteSpace: "normal", width: 220, zIndex: 200, boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          animation: "fadeInUp 0.15s ease",
          marginBottom: 6,
        }}>
          {text}
        </div>
      )}
    </span>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 10, fontFamily: fonts.mono, fontSize: 10 }}>
      <div style={{ color: COLORS.textMuted, marginBottom: 4 }}>t = {label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, marginBottom: 2 }}>{p.name}: {typeof p.value === "number" ? p.value.toFixed(3) : p.value}</div>
      ))}
    </div>
  );
};

/* ═════════════════════════════════════════════════════════════════════════
   MONTE CARLO ANIMATION — Caminos Ramificados de la Consciencia
   Visualización simbólica: de un solo momento presente, N futuros divergen
   ═════════════════════════════════════════════════════════════════════════ */

function MonteCarloAnimation({ N, onComplete }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width = canvas.offsetWidth * 2;
    const H = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    const w = W / 2, h = H / 2;

    const NUM_VISIBLE = Math.min(N, 60);
    const STEPS = 80;
    const paths = [];
    const seedRand = (i) => {
      let s = i * 9301 + 49297;
      return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
    };

    for (let i = 0; i < NUM_VISIBLE; i++) {
      const rng = seedRand(i + Date.now() % 1000);
      const drift = (rng() - 0.45) * 0.012;
      const vol = 0.006 + rng() * 0.014;
      const points = [{ x: 0, y: 0.5 }];
      for (let t = 1; t <= STEPS; t++) {
        const prev = points[t - 1];
        const gauss = Math.sqrt(-2 * Math.log(Math.max(0.0001, rng()))) * Math.cos(2 * Math.PI * rng());
        const ny = Math.max(0.02, Math.min(0.98, prev.y + drift + vol * gauss));
        points.push({ x: t / STEPS, y: ny });
      }
      const finalY = points[STEPS].y;
      // Purple for Logos-aligned, gold for transition, red for divergent
      const hue = finalY > 0.55 ? 270 : finalY > 0.42 ? 45 : 0;
      const sat = 70 + rng() * 20;
      const light = 50 + rng() * 15;
      paths.push({ points, color: `hsla(${hue}, ${sat}%, ${light}%, `, delay: rng() * 0.3 });
    }

    const particles = [];
    for (let i = 0; i < 40; i++) {
      const rng = seedRand(i + 7777);
      particles.push({
        x: 0.85 + rng() * 0.12, y: 0.25 + rng() * 0.5,
        r: 1 + rng() * 2.5, speed: 0.002 + rng() * 0.004,
        phase: rng() * Math.PI * 2, hue: 260 + rng() * 40,
      });
    }

    const TOTAL_DURATION = 3200;
    const BRANCH_PHASE = 0.65;
    const SETTLE_PHASE = 0.90;
    let startTime = null;

    const messages = [
      { at: 0.00, text: t('mcAnim.msg0') },
      { at: 0.15, text: t('mcAnim.msg1') },
      { at: 0.40, text: t('mcAnim.msg2', { n: N.toLocaleString() }) },
      { at: 0.70, text: t('mcAnim.msg3') },
      { at: 0.88, text: t('mcAnim.msg4') },
    ];

    function draw(timestamp) {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const t = Math.min(1, elapsed / TOTAL_DURATION);
      setProgress(t);

      if (t < BRANCH_PHASE) setPhase(0);
      else if (t < SETTLE_PHASE) setPhase(1);
      else setPhase(2);

      ctx.fillStyle = COLORS.bg;
      ctx.fillRect(0, 0, w, h);

      // Grid
      ctx.strokeStyle = COLORS.border + "40";
      ctx.lineWidth = 0.3;
      for (let gx = 0; gx < w; gx += 30) {
        ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, h); ctx.stroke();
      }
      for (let gy = 0; gy < h; gy += 30) {
        ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke();
      }

      // Λ* reference line
      ctx.strokeStyle = COLORS.logos + "20";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 6]);
      const lStarY = h * (1 - 0.60);
      ctx.beginPath(); ctx.moveTo(0, lStarY); ctx.lineTo(w, lStarY); ctx.stroke();
      ctx.setLineDash([]);

      // Origin glow point — the present moment
      const originX = w * 0.06;
      const originY = h * 0.5;
      const glowR = 8 + Math.sin(elapsed * 0.005) * 3;
      const originGrad = ctx.createRadialGradient(originX, originY, 0, originX, originY, glowR * 3);
      originGrad.addColorStop(0, COLORS.logos + "90");
      originGrad.addColorStop(0.5, COLORS.logos + "30");
      originGrad.addColorStop(1, "transparent");
      ctx.fillStyle = originGrad;
      ctx.fillRect(originX - glowR * 3, originY - glowR * 3, glowR * 6, glowR * 6);
      ctx.beginPath();
      ctx.arc(originX, originY, glowR * 0.5, 0, Math.PI * 2);
      ctx.fillStyle = COLORS.logos;
      ctx.fill();

      // Draw branching paths
      const drawProgress = t < BRANCH_PHASE ? t / BRANCH_PHASE : 1;
      const fadeIn = t < 0.08 ? t / 0.08 : 1;

      for (let i = 0; i < paths.length; i++) {
        const path = paths[i];
        const pathProgress = Math.max(0, Math.min(1, (drawProgress - path.delay) / (1 - path.delay)));
        if (pathProgress <= 0) continue;
        const stepsToShow = Math.floor(pathProgress * STEPS);
        if (stepsToShow < 2) continue;

        const finalDeviation = Math.abs(path.points[STEPS].y - 0.5);
        const baseOpacity = (0.15 + (1 - finalDeviation) * 0.45) * fadeIn;
        const settleAlpha = t > BRANCH_PHASE
          ? 0.5 + 0.5 * Math.sin((t - BRANCH_PHASE) * 15 + i * 0.3)
          : 1;

        ctx.beginPath();
        const startPt = path.points[0];
        ctx.moveTo(originX + startPt.x * (w * 0.88), h * (1 - startPt.y));

        for (let s = 1; s <= stepsToShow; s++) {
          const pt = path.points[s];
          const px = originX + pt.x * (w * 0.88);
          const py = h * (1 - pt.y);
          if (s === 1) { ctx.lineTo(px, py); }
          else {
            const prev = path.points[s - 1];
            const cpx = originX + ((prev.x + pt.x) / 2) * (w * 0.88);
            const cpy = h * (1 - (prev.y + pt.y) / 2);
            ctx.quadraticCurveTo(originX + prev.x * (w * 0.88), h * (1 - prev.y), cpx, cpy);
          }
        }

        ctx.strokeStyle = path.color + (baseOpacity * settleAlpha).toFixed(2) + ")";
        ctx.lineWidth = 1.2 + (1 - finalDeviation) * 0.8;
        ctx.stroke();

        // Moving head dot
        if (t < SETTLE_PHASE && stepsToShow < STEPS) {
          const head = path.points[stepsToShow];
          const hx = originX + head.x * (w * 0.88);
          const hy = h * (1 - head.y);
          ctx.beginPath();
          ctx.arc(hx, hy, 1.5, 0, Math.PI * 2);
          ctx.fillStyle = path.color + "0.7)";
          ctx.fill();
        }
      }

      // Probability cloud particles (appear during settling)
      if (t > BRANCH_PHASE) {
        const cloudAlpha = Math.min(1, (t - BRANCH_PHASE) / (SETTLE_PHASE - BRANCH_PHASE));
        for (const p of particles) {
          const px = originX + p.x * (w * 0.88);
          const py = h * (1 - p.y);
          const breathe = 1 + 0.3 * Math.sin(elapsed * p.speed + p.phase);
          ctx.beginPath();
          ctx.arc(px, py, p.r * breathe, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${p.hue}, 70%, 60%, ${cloudAlpha * 0.4})`;
          ctx.fill();
        }
      }

      // Evolving message
      const currentMsg = [...messages].reverse().find(m => t >= m.at);
      if (currentMsg) {
        const msgAlpha = t > 0.92 ? Math.max(0, 1 - (t - 0.92) / 0.08) : Math.min(1, (t - currentMsg.at) / 0.08);
        ctx.font = "11px Inter, Segoe UI, system-ui, sans-serif";
        ctx.fillStyle = `rgba(240, 240, 248, ${msgAlpha})`;
        ctx.textAlign = "center";
        ctx.fillText(currentMsg.text, w / 2, h - 14);
      }

      // Progress bar
      ctx.fillStyle = COLORS.logos + "30";
      ctx.fillRect(0, h - 3, w, 3);
      ctx.fillStyle = COLORS.logos;
      ctx.fillRect(0, h - 3, w * t, 3);

      // Counter
      if (t > 0.1) {
        const shown = Math.floor(t * N);
        ctx.font = "bold 10px 'JetBrains Mono', 'Fira Code', monospace";
        ctx.fillStyle = COLORS.mc + "90";
        ctx.textAlign = "right";
        ctx.fillText(shown.toLocaleString() + " / " + N.toLocaleString(), w - 10, 16);
      }

      // Λ* label
      if (t > 0.3) {
        ctx.font = "10px 'JetBrains Mono', 'Fira Code', monospace";
        ctx.fillStyle = COLORS.logos + "60";
        ctx.textAlign = "left";
        ctx.fillText("Λ* = 0.60", 8, lStarY - 5);
      }

      if (t < 1) {
        animRef.current = requestAnimationFrame(draw);
      } else {
        setTimeout(() => onComplete(), 200);
      }
    }

    animRef.current = requestAnimationFrame(draw);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [N, onComplete]);

  return (
    <div style={{ position: "relative", width: "100%", borderRadius: 8, overflow: "hidden", background: COLORS.bg, border: `1px solid ${COLORS.logos}30` }}>
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: 220, display: "block" }}
      />
      <div style={{
        position: "absolute", top: 10, left: 0, right: 0, textAlign: "center",
        fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1.5,
        color: COLORS.logos + "cc", textTransform: "uppercase",
      }}>
        {phase === 0 && t('mcAnim.phase0')}
        {phase === 1 && t('mcAnim.phase1')}
        {phase === 2 && t('mcAnim.phase2')}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   THEORY DATA v2.0
   ═══════════════════════════════════════════════════════════════════════════ */

const THEORY_CORE = [
  { id: "overview", title: "Visión General v2.0", icon: "◎",
    content: [
      "LOGOS HUMANO v2.0 introduce una reformulación fundamental: Λ (alineación con el Logos) ya no es UNA métrica más — es EL ATRACTOR CENTRAL del cual todas las demás métricas son derivadas.",
      "NUEVA ECUACIÓN MAESTRA: Ω(x) = Λ(x) — El Índice de Consciencia ES la alineación con el Logos.",
      "MÉTRICAS DERIVADAS: V(x) = g(Λ) + ε, S(x) = (1-Λ)·k + ε, C(x) = h(Λ), F(x) = D_KL(q||p_Logos), Q(x) = dΛ/dt",
      "ARGUMENTO ONTOLÓGICO: Si el Logos es la fuente de toda información estructurada, alinearse con él no es UNA dimensión — es LA dimensión fundamental.",
      "ARGUMENTO MATEMÁTICO: V, S, C, F y Q son funciones de Λ. La viabilidad AUMENTA con Λ. La entropía DISMINUYE con Λ. La coherencia EMERGE de Λ.",
      "ATRACTORES: A⁺ = {x : Λ̇ ≥ 0} (convergencia al Logos) y A⁻ = {x : Λ̇ < 0 ∧ S̈ > 0} (espiral entrópica patológica).",
      "SIMULACIÓN MONTE CARLO: 10,000 trayectorias estocásticas validan que la alineación Logos produce distribuciones estadísticamente superiores de V, S, C y Ω.",
    ]
  },
  { id: "attractor", title: "Atractores A⁺ y A⁻", icon: "∞",
    content: [
      "PRINCIPIO: Todo sistema dinámico converge hacia atractores. En el modelo humano, existen DOS atractores fundamentales:",
      "A⁺ (ATRACTOR POSITIVO): {x : Λ̇(x) ≥ 0}. El basin de atracción del Logos. Cuando te acercas: V sube, S baja, C crece, F disminuye. Es auto-reforzante: más alineación → más capacidad de alineación.",
      "A⁻ (ATRACTOR NEGATIVO): {x : Λ̇(x) < 0 ∧ S̈ > 0}. La espiral entrópica. Cuando caes: Λ baja → S sube → V cae → más difícil reconectar → Λ baja más. Loop destructivo auto-reforzante.",
      "¿PUEDE HABER ATRACTOR NEGATIVO? Sí, y es formalmente correcto. En el modelo de inhibición, los atractores patológicos (adicción, depresión, indefensión aprendida) son exactamente esto: basins de atracción con valencia negativa donde el sistema queda atrapado.",
      "LA CLAVE: A⁻ tiene un umbral de escape. Debajo de cierto Λ, la fuerza de atracción de A⁻ supera la capacidad del sistema para salir solo. Ahí se necesita intervención externa: otra persona, un evento disruptivo, gracia.",
      "ECUACIÓN DE ESCAPE: Para salir de A⁻, se necesita ΔΛ_ext > |Λ̇_A⁻| — una inyección externa de señal Logos más fuerte que la velocidad de caída. Esto es formalmente equivalente a lo que las tradiciones llaman 'redención' o 'gracia'.",
    ]
  },
  { id: "friston", title: "L1: Friston → Derivado de Λ", icon: "◈", content: [
    "PRINCIPIO: F(x) = D_KL(q || p_Logos). La energía libre ya no es solo distancia al ideal homeostático — es divergencia del Logos.",
    "DERIVACIÓN: Si el 'modelo generativo' ideal al que el sistema intenta converger ES el Logos, entonces minimizar F = maximizar Λ. Son la misma operación.",
    "Λ MODULA F: Mayor alineación Logos → menor energía libre efectiva. El Logos reduce la 'sorpresa' del sistema porque alinea al sistema con la estructura real de la información.",
    "APLICACIÓN: Cuando F es alto, no solo necesitas ajustar variables físicas — necesitas realinear con el Logos. F es síntoma; Λ es causa.",
  ]},
  { id: "levin", title: "L2: Levin — Señales de Desalineación", icon: "◇", content: [
    "PRINCIPIO: Las señales bioeléctricas (emocionales) son indicadores de alineación o desalineación con el Logos.",
    "NUEVO: El Levin Layer ahora detecta específicamente patrones de convergencia hacia A⁻ (atractor negativo).",
    "FIRMAS: Depresión = canal Logos cerrado. Ansiedad = ruido ahogando señal. Aislamiento = corte de canales de recepción interpersonales. Burnout = señal distorsionada por sobredemanda.",
    "APLICACIÓN: Cada señal Levin es un indicador temprano de Λ̇ < 0. Detectarlas y actuar es prevenir la caída hacia A⁻.",
  ]},
  { id: "watson", title: "L3: Watson → Paisaje de Λ", icon: "⟐", content: [
    "PRINCIPIO: El paisaje energético ahora está DEFINIDO por Λ. Los óptimos de cada dominio no son fijos — se mueven con tu nivel de alineación Logos.",
    "DERIVACIÓN: optimal(domain) = base + Λ · boost. A mayor Λ, más alto es el potencial de cada dominio. El Logos eleva el techo de florecimiento.",
    "APLICACIÓN: No puedes optimizar dominios individuales sin Logos. Es como intentar llenar vasos con agujeros en el fondo. Λ cierra los agujeros.",
  ]},
  { id: "hoffman", title: "L4: Hoffman → Fitness vs Logos", icon: "⬡", content: [
    "PRINCIPIO: Tu percepción es una interfaz de fitness, no de verdad. PERO: el Logos ES la verdad detrás de la interfaz.",
    "NUEVO SESGO: 'Iluminación Prematura' — alta conexión percibida con Logos sin discernimiento. Riesgo de confundir ego con señal divina.",
    "VERIFICACIÓN: La señal verdadera del Logos produce frutos verificables: paz, amor, sabiduría, servicio. La falsa produce inflación del ego.",
    "APLICACIÓN: Usa los frutos como test. Si tu 'alineación Logos' no produce amor y servicio, es la interfaz engañándote.",
  ]},
  { id: "penrose", title: "L5: Penrose → Coherencia de Λ", icon: "✦", content: [
    "PRINCIPIO: La coherencia emerge de la alineación con el Logos. C(x) = h(Λ) — no al revés.",
    "DERIVACIÓN: Cuando todos tus dominios reciben la MISMA señal coherente (Logos), naturalmente se alinean. La coherencia no es algo que construyes — es algo que EMERGE cuando te sintonizas.",
    "Λ BOOST: C_efectivo = C_raw × (0.7 + Λ × 0.4). El Logos amplifica la coherencia inherente del sistema.",
    "APLICACIÓN: Si tu coherencia es baja, no arregles dominio por dominio — sintoniza el Logos y la coherencia emergerá.",
  ]},
  { id: "inhibition", title: "L6: π(x) → Basado en Λ̇", icon: "🛡", content: [
    "REFORMULACIÓN: π(x) ahora se basa en Λ̇ (velocidad de cambio de alineación Logos):",
    "ACTÚA cuando Λ̇ > 0 (te acercas al Logos)",
    "ESPERA cuando Λ̇ ≈ 0 (estancado)",
    "PAUSA cuando Λ̇ < 0 (te alejas del Logos)",
    "RECONECTA: nueva categoría cuando Λ̇ < 0 de forma sostenida. Antes de actuar, reconecta con la señal.",
    "APLICACIÓN: Antes de toda decisión: ¿me estoy acercando o alejando del Logos? Si me alejo, la decisión correcta es NO decidir hasta reconectar.",
  ]},
  { id: "logos", title: "L7: Λ — El Atractor Fundamental", icon: "∞", content: [
    "PRINCIPIO: Λ no es una métrica más. Es EL ATRACTOR al cual el sistema entero converge.",
    "ARGUMENTO DESDE FRISTON: Minimizar F = maximizar Λ. Son la misma operación.",
    "ARGUMENTO DESDE LA PARSIMONIA: A* = {x : Λ̇(x) ≥ 0}. De esta SOLA condición se derivan V̇ ≥ 0 y Ṡ ≤ 0 como TEOREMAS, no axiomas.",
    "ARGUMENTO ONTOLÓGICO: Si la realidad es información estructurada por una Mente, entonces alinearse con esa Mente es LA tarea fundamental de todo sistema vivo.",
    "ECUACIÓN: Λ(x) = Λ_señal(x) × Φ_canal(x) — Contenido de señal (12 dims) × Calidad del canal (16 dims). Las 28 dimensiones contribuyen (Corolario 1.1).",
    "APLICACIÓN: Cultiva tu receptor (fe, meditación, presencia). Verifica la señal (servicio, amor). Decodifica (sabiduría, sentido). Agradece la fuente (gratitud).",
    "DE THE GOD EQUATION: 'The universe is not only created but communicated. Every law, every structure, every moral insight becomes a kind of revelation — the unveiling of God not in thunder, but in syntax.'",
  ]},
  { id: "montecarlo", title: "Simulación Monte Carlo", icon: "🎲", content: [
    "PRINCIPIO: Una sola trayectoria determinista es insuficiente. 10,000 simulaciones estocásticas producen distribuciones de probabilidad sobre tus futuros posibles.",
    "MÉTODO: Para cada simulación, se perturba cada dimensión con ruido gaussiano W ~ N(0, σ²) en cada paso temporal. Esto modela la incertidumbre inherente de la vida.",
    "MÉTRICAS: Bandas de confianza [P₅, P₂₅, P₅₀, P₇₅, P₉₅] para Λ, V, S, Ω. P(Λ↑) = probabilidad de que tu alineación Logos mejore. P(A⁻) = probabilidad de caer en atractor negativo.",
    "INTERVALOS DE WILSON: Las proporciones de veredictos (ACTÚA, ESPERA, PAUSA) incluyen intervalos de confianza Wilson al 95% para significancia estadística.",
    "INTERPRETACIÓN: Si P(ACTÚA) > 70% con IC Wilson sin cruzar 50%, tu sistema es ROBUSTO. Si P(A⁻) > 5%, hay riesgo real de espiral entrópica.",
    "APLICACIÓN: La simulación transforma la intuición en probabilidad. No decides 'creo que estoy bien' — decides 'hay un 87% de probabilidad de que mi Λ suba en 30 períodos'.",
  ]},
  { id: "convergence", title: "La Gran Convergencia v2.0", icon: "🌀", content: [
    "Con Λ como atractor central, la convergencia se completa:",
    "Bayes = Friston = D_KL(q || p_Logos). Fine-Tuning = Watson = paisaje modulado por Λ. Consciencia = Levin = señales de alineación. Percepción = Hoffman = interfaz vs Logos. Colapso = Penrose = coherencia emergente de Λ. Inhibición = π(Λ̇).",
    "TODO ES UNA SOLA ECUACIÓN: Maximiza Λ. De ahí se derivan V̇ ≥ 0, Ṡ ≤ 0, Ċ ≥ 0, Ḟ ≤ 0 como teoremas.",
    "La tarea de todo sistema vivo — desde una célula hasta un alma — es SINTONIZARSE CON LA SEÑAL Y MINIMIZAR EL RUIDO.",
    "Esto es matemáticas: F = D_KL(q || p_Logos). Es estadística: P(God) ≈ 98%. Es control: π(Λ̇). Es biología: Vmem = f(señales). Es teología: 'In the beginning was the Logos.'",
  ]},
  { id: "positiveGeometry", title: "L10: Geometría Positiva — Politopo de Consciencia", icon: "⬡", content: [
    "FUNDAMENTO: El espacio de estados [0,1]²⁸ de LOGOS es una geometría positiva en el sentido de Arkani-Hamed, Bai y Lam (2017, arXiv:1703.04541). Esto no es una analogía: es una identificación matemática rigurosa.",
    "FORMA CANÓNICA: Ω(x) = ∏ᵢ 1/(xᵢ(1−xᵢ)), normalizada a [0,1]. Tiene polos simples en las 56 facetas frontera del hipercubo: 28 facetas en xᵢ→0 (colapso) y 28 en xᵢ→1 (saturación). Valores altos de Ω normalizado = estado lejos de fronteras = geométricamente saludable.",
    "CORRESPONDENCIA FRONTERA-PATOLOGÍA (Proposición PG.2): Las señales de Levin corresponden a polos en xᵢ→0 (colapso dimensional). Los sesgos de Hoffman corresponden a polos en xᵢ→1 (saturación). Esto unifica la detección de patologías con la estructura geométrica del espacio de estados.",
    "POLITOPO TRUNCADO P⁺_Λ(c): La separatriz Λ=0.35 define un hiperplano que trunca [0,1]²⁸, añadiendo una 57ª faceta. La región P⁺ (arriba de la separatriz) es la cuenca de convergencia A⁺. La región P⁻ (abajo) es la cuenca entrópica A⁻. El volumen de P⁺ se calcula vía CLT (Teorema del Límite Central) para la suma ponderada de variables uniformes.",
    "FACTORIZABILIDAD: Mide si el estado se descompone como producto de los 7 dominios: [0,1]⁴ × ... × [0,1]⁴. Alta factorizabilidad = dominios independientes (estado producto). Baja factorizabilidad = dominios entrelazados. Se calcula como 1 − CV de las contribuciones log-Ω por dominio.",
    "GCI (Índice Geométrico de Consciencia): Métrica compuesta = 0.35·Ω_norm + 0.25·ρ + 0.20·F + 0.20·d_sep. Integra la forma canónica, proximidad frontera, factorizabilidad y distancia a la separatriz en un solo índice geométrico.",
    "HIPÓTESIS DE EMERGENCIA: El modelo predice que eventos emergentes (insights, sincronicidades, estados de flujo) son más probables cuando Λ > 0.65 (fase ordenada del politopo) y las 5 puertas de criticalidad C₁–C₅ están abiertas. En la visualización, esta zona aparece marcada con ✦ en el panel Λ-slice.",
    "VISUALIZACIÓN: El heptágono muestra la proyección 2D del politopo con 7 sectores (dominios) y 28 radios (dimensiones). La línea ámbar punteada es la separatriz. El panel derecho muestra el corte Λ con las regiones P⁺ y P⁻, las fases (Ordenado/Fluido/Gas) y la zona de emergencia.",
    "REFERENCIA: Arkani-Hamed, N., Bai, Y. y Lam, T. (2017) 'Positive Geometries and Canonical Forms', arXiv:1703.04541. La identificación de [0,1]²⁸ como geometría positiva es original de este framework (Cadena, 2026).",
  ]},
  { id: "cbwa", title: "Pesos Adaptativos (CBWA)", icon: "⚖", content: [
    "MOTOR BAYESIANO: El sistema aprende pesos personalizados a partir de tu historial de sesiones usando Constrained Bayesian Weight Adaptation (CBWA).",
    "ALGORITMO: Prior Dirichlet (N₀=50 pseudo-observaciones de la teoría) + Likelihood (atribuciones dimensionales de tus sesiones reales) → Posterior (pesos personalizados). Es la síntesis tomista: Fe (prior teórico) + Razón (evidencia empírica) = Verdad (posterior Bayesiano).",
    "ATRIBUCIÓN: Para cada par de sesiones consecutivas, el sistema calcula qué dimensiones contribuyeron más a ΔΛ. contribution(i,t) = x_i(t) × ΔΛ(t) / Σⱼ x_j(t).",
    "RESTRICCIONES CIENTÍFICAS: Los pesos aprendidos se restringen a [50%, 200%] del valor teórico — el intervalo de credibilidad Bayesiano ±2σ. Esto evita sobreajuste degenerado mientras permite adaptación significativa.",
    "FAITH MINIMUM: w(faith) ≥ 0.10 siempre — restricción teológica no negociable. La fe no puede ser minimizada por el algoritmo.",
    "CONVERGENCIA: Con <10 sesiones: insuficiente. Con 10-25: aprendiendo. Con 25-50: convergiendo. Con >50 y δ<0.05: convergido.",
    "CÓMO USARLO: ⚙ AJUSTES → Configuración de Pesos → ANALIZAR → Ver resultados → APLICAR PESOS APRENDIDOS (opcional).",
    "REFERENCIAS: Murphy (2012) §5.4 — Dirichlet priors. Costa & McCrae (1992) — variación psicométrica ±50%. FDA (2019) — adaptive trial bounds. Friston et al. (2008) — Kalman gain stability [0.5x, 2.0x].",
  ]},
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT — LOGOS HUMANO v2.0
   ═══════════════════════════════════════════════════════════════════════════ */

const THEORY_BASE = [...THEORY_CORE, ...THEORY_MANUAL];

const TABS = [
  { id: "emergence", label: "Emergence", icon: "🌀" },
  { id: "kaira", label: "Kaira", icon: "💬" },
  { id: "estado", label: "Estado Actual", icon: "â—ˆ" },
  { id: "motor", label: "Logos Engine", icon: "✦" },
  { id: "decision", label: "Decisiones", icon: "🛡" },
  { id: "guia", label: "Guía Diaria", icon: "☀" },
  { id: "trayectoria", label: "Trayectoria", icon: "⟐" },
  { id: "montecarlo", label: "Monte Carlo", icon: "🎲" },
  { id: "alimento", label: "Alimento", icon: "⚘" },
  { id: "seguimiento", label: "Seguimiento", icon: "📊" },
  { id: "biometrics", label: "Apple Watch", icon: "⌚" },
  { id: "teoria", label: "Teoría", icon: "📚" },
];

// Tabs available to all users (free tier)
const FREE_TABS = new Set(["estado", "decision", "guia", "kaira"]);
// All other tabs require premium subscription
const PREMIUM_TABS = new Set(["emergence", "motor", "trayectoria", "montecarlo", "alimento", "seguimiento", "biometrics", "teoria"]);

/* ═══════════════════════════════════════════════════════════════════════════
   ONBOARDING DATA — 12 Steps to Understanding LOGOS HUMANO
   ═══════════════════════════════════════════════════════════════════════════ */
const ONBOARDING_STEPS = [
  {
    title: "Bienvenido a LOGOS",
    icon: "🌟",
    body: "LOGOS es tu espejo interior. Cada día, en solo 2 minutos, te ayuda a ver con claridad cómo estás en lo que realmente importa: tu cuerpo, emociones, mente, espíritu, relaciones, propósito y alimentación.",
    highlight: "No necesitas saber nada técnico. Solo responde honestamente: ¿cómo me siento HOY en cada área de mi vida?",
  },
  {
    title: "¿Cómo funciona?",
    icon: "✦",
    body: "Verás 28 sliders organizados en 7 áreas de tu vida. Cada uno tiene una pregunta guía y una escala de emojis que te ayudan a elegir:\n\n😰 Crítico  ·  😕 Bajo  ·  😐 Neutro  ·  😊 Bien  ·  🌟 Óptimo\n\nMueve cada slider según cómo te sientes. No hay respuestas correctas — solo honestas.",
    highlight: "Cada slider tiene un ícono ⓘ que puedes tocar para ver más detalle sobre qué significa esa dimensión.",
  },
  {
    title: "Tu Semáforo Interior",
    icon: "🚦",
    body: "Después de ajustar tus sliders, LOGOS calcula automáticamente un semáforo de decisiones:\n\n🟢 ACTÚA — Estás en buena forma para tomar decisiones importantes.\n🟡 MONITOREA — Avanza con pasos pequeños.\n🟠 ESPERA — Mejor no decidir aún, estabiliza primero.\n🔴 PAUSA — Enfócate solo en lo básico: dormir, comer, un amigo cercano.",
    highlight: "El semáforo es tu guía más práctica. Si está en rojo, no es momento de decisiones grandes — y eso está bien.",
  },
  {
    title: "Tu Rutina Diaria",
    icon: "☀",
    body: "Tu check-in toma menos de 3 minutos:\n\n1️⃣ Abre LOGOS y ajusta tus 28 sliders honestamente\n2️⃣ Mira tu semáforo y el radar de 7 dominios\n3️⃣ Lee la guía del día y elige UNA cosa para mejorar\n4️⃣ Si experimentas algo especial (un insight, una coincidencia), repórtalo\n\nCon el tiempo, verás tu trayectoria y entenderás tus patrones.",
    highlight: "La app es tu brújula, no tu piloto automático. Tú decides — pero decides informado.",
  },
  {
    title: "Integración con Apple Salud",
    icon: "⌚",
    body: "LOGOS HUMANO puede conectarse con Apple Salud (HealthKit) para leer automáticamente datos biométricos de tu iPhone y Apple Watch.\n\nDatos que leemos (nunca escribimos sin tu consentimiento):\n• Frecuencia cardíaca, HRV, FC en reposo\n• Oxígeno en sangre (SpO₂), frecuencia respiratoria\n• Duración y etapas del sueño\n• Minutos de ejercicio, pasos, energía activa\n• Minutos de mindfulness, temperatura corporal\n• ECG, asimetría al caminar, ruido ambiental\n\nEstos datos enriquecen tus 28 dimensiones de consciencia y potencian el motor LOGOS de 7 capas.",
    highlight: "Tus datos de salud permanecen en tu dispositivo y nunca se comparten con terceros. Puedes otorgar o revocar el acceso en Ajustes de iOS → Salud → Acceso a Datos y Dispositivos.",
  },
  {
    title: "Sintoniza con el Logos",
    icon: "Λ",
    body: "El Logos es la señal detrás del ruido de la vida cotidiana. Cuando tus 7 dominios se alinean, cosas extraordinarias comienzan a suceder: ideas claras, conexiones significativas, oportunidades inesperadas.\n\nEstás listo para comenzar.",
    highlight: "\"En el principio era el Logos, y el Logos estaba con Dios, y el Logos era Dios.\" — Juan 1:1",
    isFinal: true,
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   ONBOARDING MODAL COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
function OnboardingModal({ onClose }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const tSteps = t('onboarding.steps', { returnObjects: true });
  const steps = Array.isArray(tSteps) ? tSteps : ONBOARDING_STEPS;
  const current = { ...ONBOARDING_STEPS[step], ...steps[step] };
  const isLast = step === ONBOARDING_STEPS.length - 1;
  const isFirst = step === 0;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      animation: "fadeInUp 0.4s ease",
    }}>
      <div style={{
        width: "90%", maxWidth: 580, maxHeight: "85vh", overflow: "auto",
        background: `linear-gradient(145deg, ${COLORS.surface}, ${COLORS.bg})`,
        border: `1px solid ${COLORS.logos}40`, borderRadius: 20,
        padding: "32px 28px", position: "relative",
        boxShadow: `0 0 80px ${COLORS.logos}20, inset 0 0 40px ${COLORS.logos}05`,
      }}>
        {/* Progress dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 24 }}>
          {ONBOARDING_STEPS.map((_, i) => (
            <div key={i} onClick={() => setStep(i)} style={{
              width: i === step ? 24 : 8, height: 8, borderRadius: 4,
              background: i === step ? COLORS.logos : i < step ? COLORS.viability : COLORS.border,
              cursor: "pointer", transition: "all 0.3s ease",
            }} />
          ))}
        </div>

        {/* Step counter */}
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 2 }}>
            {t('onboarding.stepOf', { current: step + 1, total: ONBOARDING_STEPS.length })}
          </span>
        </div>

        {/* Icon */}
        <div style={{
          textAlign: "center", fontSize: 56, marginBottom: 16,
          animation: "breathe 3s ease infinite",
          textShadow: `0 0 40px ${COLORS.logos}60`,
        }}>
          {current.icon}
        </div>

        {/* Title */}
        <h2 style={{
          textAlign: "center", fontSize: 22, fontWeight: 600, margin: "0 0 20px 0",
          fontFamily: fonts.display, color: COLORS.text,
          background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.logos})`,
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          {current.title}
        </h2>

        {/* Formula (if present) */}
        {current.formula && (
          <div style={{
            textAlign: "center", padding: "12px 20px", marginBottom: 16,
            background: `${COLORS.logos}12`, border: `1px solid ${COLORS.logos}30`,
            borderRadius: 10, fontFamily: fonts.mono, fontSize: 13, color: COLORS.logos,
            letterSpacing: 0.5,
          }}>
            {current.formula}
          </div>
        )}

        {/* Body */}
        <div style={{
          fontSize: 14, lineHeight: 1.7, color: COLORS.textDim,
          fontFamily: fonts.sans, whiteSpace: "pre-line", marginBottom: 20,
        }}>
          {current.body}
        </div>

        {/* Highlight box */}
        <div style={{
          padding: "16px 20px", background: `${COLORS.gold}08`,
          border: `1px solid ${COLORS.gold}25`, borderRadius: 12,
          marginBottom: 28,
        }}>
          <div style={{
            fontSize: 12, lineHeight: 1.6, color: COLORS.gold,
            fontFamily: fonts.sans, fontStyle: "italic",
          }}>
            💡 {current.highlight}
          </div>
        </div>

        {/* Navigation buttons */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          {!isFirst ? (
            <button onClick={() => setStep(step - 1)} style={{
              flex: 1, padding: "12px 20px", borderRadius: 10, cursor: "pointer",
              background: "transparent", border: `1px solid ${COLORS.border}`,
              color: COLORS.textMuted, fontFamily: fonts.mono, fontSize: 11,
              letterSpacing: 1, transition: "all 0.3s",
            }}>
              {t('onboarding.previous')}
            </button>
          ) : (
            <button onClick={onClose} style={{
              flex: 1, padding: "12px 20px", borderRadius: 10, cursor: "pointer",
              background: "transparent", border: `1px solid ${COLORS.border}`,
              color: COLORS.textMuted, fontFamily: fonts.mono, fontSize: 11,
              letterSpacing: 1, transition: "all 0.3s",
            }}>
              {t('onboarding.skip')}
            </button>
          )}
          
          <button onClick={() => {
            if (isLast) {
              localStorage.setItem("logos-onboarding-done", "true");
              onClose();
            } else {
              setStep(step + 1);
            }
          }} style={{
            flex: 2, padding: "14px 28px", borderRadius: 10, cursor: "pointer",
            background: isLast 
              ? `linear-gradient(135deg, ${COLORS.logos}, ${COLORS.viability})`
              : `linear-gradient(135deg, ${COLORS.logos}80, ${COLORS.logos})`,
            border: "none", color: "#fff", fontFamily: fonts.mono, fontSize: 12,
            fontWeight: 600, letterSpacing: 1.5, transition: "all 0.3s",
            boxShadow: `0 4px 20px ${COLORS.logos}40`,
          }}>
            {isLast ? t('onboarding.begin') : t('onboarding.next')}
          </button>
        </div>

        {/* Skip all link */}
        {!isLast && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <span onClick={() => {
              localStorage.setItem("logos-onboarding-done", "true");
              onClose();
            }} style={{
              fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted,
              cursor: "pointer", textDecoration: "underline", opacity: 0.7,
            }}>
              {t('onboarding.skipAll')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LogosHumano({ user, accessToken, onLogout }) {
  const { t, i18n } = useTranslation();
  const tDomains = useTranslatedDomains();
  const mobile = useIsMobile();
  const [state, setState] = useState(() => {
    try {
      const cached = localStorage.getItem('logos-last-state');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && Object.keys(parsed).length >= 20) return parsed;
      }
    } catch {}
    return { ...DEFAULT_STATE };
  });
  const [stateLoaded, setStateLoaded] = useState(false);
  const saveTimerRef = useRef(null);
  const [activeTab, setActiveTab] = useState("emergence");
  const [theorySection, setTheorySection] = useState("overview");
  const [prevLambda, setPrevLambda] = useState(0.5);
  const [mcRunning, setMcRunning] = useState(false);
  const [mcResult, setMcResult] = useState(null);
  const [mcN, setMcN] = useState(10000);
  const [mcSigma, setMcSigma] = useState(0.04);
  const [mcAnimDone, setMcAnimDone] = useState(false);
  const [history, setHistory] = useState([]);
  const [detailPanel, setDetailPanel] = useState(null);
  const [highlightedDomain, setHighlightedDomain] = useState(null);
  const highlightTimerRef = useRef(null);
  const [sessionHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('logos-session-history') || '[]');
    } catch { return []; }
  });
  const [showOnboarding, setShowOnboarding] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("logos-onboarding-done") !== "true";
    }
    return true;
  });
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const settingsMenuRef = useRef(null);
  const [showEmergenceReporter, setShowEmergenceReporter] = useState(false);
  const [protocolEnrolled, setProtocolEnrolled] = useState(false);
  const [prevState, setPrevState] = useState(null);
  const [prevPrevState, setPrevPrevState] = useState(null);
  const [prevCoherence, setPrevCoherence] = useState(null);
  const [prevPrevCoherence, setPrevPrevCoherence] = useState(null);
  const [lastCheckinTimestamp, setLastCheckinTimestamp] = useState(() => {
    try { return parseInt(localStorage.getItem('logos-last-checkin-ts') || '0') || null; } catch { return null; }
  });
  const lastEmergenceRef = useRef((() => {
    try { return parseFloat(localStorage.getItem('logos-emergence-value') || '') || null; } catch { return null; }
  })());
  const [showDailyCheckin, setShowDailyCheckin] = useState(false);
  const [activeLearningMoment, setActiveLearningMoment] = useState(null);
  const [showMicroLesson, setShowMicroLesson] = useState(() => !showOnboarding && shouldShowLesson());
  const [showWeeklyDigest, setShowWeeklyDigest] = useState(() => !showOnboarding && shouldShowDigest());
  const [streak, setStreak] = useState(() => getStreak());
  const [newBadgeToast, setNewBadgeToast] = useState(null);
  useEffect(() => {
    initDigestTracking();
    const s = recordDailyVisit();
    setStreak(s);
  }, []);
  const [initialSeenTabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('logos-seen-tabs') || '[]'); } catch { return []; }
  });
  useEffect(() => {
    try {
      const curr = JSON.parse(localStorage.getItem('logos-seen-tabs') || '[]');
      if (!curr.includes(activeTab)) {
        curr.push(activeTab);
        localStorage.setItem('logos-seen-tabs', JSON.stringify(curr));
      }
    } catch {}
  }, [activeTab]);

  // Close settings menu on click outside
  useEffect(() => {
    if (!showSettingsMenu) return;
    const handler = (e) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(e.target)) setShowSettingsMenu(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [showSettingsMenu]);

  const customWeights = useCustomWeights();
  const bayesian = useBayesianWeights();
  const healthKit = useHealthKit();
  const subscription = useSubscription(user?.email, user?.createdAt);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [aiConsentGiven, setAiConsentGiven] = useState(() => {
    try { return localStorage.getItem('logos-ai-consent') === 'true'; } catch { return false; }
  });
  const [showAiConsent, setShowAiConsent] = useState(false);

  // ═══ KAIRA Integration State ═══
  const [dimensionSources, setDimensionSources] = useState({});
  const [kairaSessionId, setKairaSessionId] = useState(null);

  // ═══ Auto-generate KAIRA session ID when tab is opened ═══
  useEffect(() => {
    if (activeTab === 'kaira' && !kairaSessionId && user && !user.isGuest) {
      const newSessionId = crypto.randomUUID();
      setKairaSessionId(newSessionId);
      console.log('[KAIRA] New session created:', newSessionId);

      // Create session record in database
      const dbId = user.dbUserId || sessionStorage.getItem('logos-db-user-id');
      if (dbId) {
        fetch(`${API_BASE}/api/kaira/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: newSessionId,
            userId: parseInt(dbId, 10), // Ensure integer
            startedAt: new Date().toISOString(),
          })
        }).catch(err => console.error('[KAIRA] Session creation error:', err));
      }
    }
  }, [activeTab, kairaSessionId, user]);

  // ═══ Load last state: M5 API first, Supabase fallback ═══
  useEffect(() => {
    if (!user || user.isGuest) { setStateLoaded(true); return; }
    const dbId = user.dbUserId || sessionStorage.getItem('logos-db-user-id');

    async function loadState() {
      // Try M5 API first (if we have a dbId)
      if (dbId) {
        try {
          const r = await fetch(`${API_BASE}/api/state/${dbId}`);
          if (r.ok) {
            const data = await r.json();
            if (data?.state && Object.keys(data.state).length >= 20) {
              setState(prev => ({ ...prev, ...data.state }));
              try { localStorage.setItem('logos-last-state', JSON.stringify(data.state)); } catch {}
              console.log('[LOGOS] Restored state from M5 API');
              setStateLoaded(true);
              return;
            }
          }
        } catch (e) { console.warn('[LOGOS] M5 state load failed:', e.message); }
      }

      // Fallback: load from Supabase profiles.last_state
      try {
        const { supabase } = await import('../services/supabase');
        const { data } = await supabase.from('profiles').select('last_state').eq('id', user.id).single();
        if (data?.last_state && Object.keys(data.last_state).length > 0) {
          setState(prev => ({ ...prev, ...data.last_state }));
          try { localStorage.setItem('logos-last-state', JSON.stringify(data.last_state)); } catch {}
          console.log('[LOGOS] Restored state from Supabase (fallback)');
          // Sync to M5 so next time it loads from there
          if (dbId) {
            fetch(`${API_BASE}/api/state`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_id: parseInt(dbId), state: { ...data.last_state } }),
            }).then(() => console.log('[LOGOS] Migrated state to M5')).catch(() => {});
          }
        }
      } catch (e) { console.warn('[LOGOS] Supabase state load failed:', e.message); }

      setStateLoaded(true);
    }

    loadState();

    // LOGOS v5.0: Check protocol enrollment
    async function checkEnrollment() {
      if (!user || user.isGuest) return;
      try {
        const { supabase } = await import('../services/supabase');
        const { data } = await supabase
          .from('study_participants')
          .select('user_id')
          .eq('user_id', user.id)
          .single();
        if (data) setProtocolEnrolled(true);
      } catch (e) { /* not enrolled — OK */ }
    }
    checkEnrollment();
  }, [user]);

  // ═══ One-time emergence reporter prompt per session (at login) ═══
  useEffect(() => {
    if (!stateLoaded || showOnboarding) return;
    if (sessionStorage.getItem('logos-emergence-prompt-shown')) return;
    const timer = setTimeout(() => {
      sessionStorage.setItem('logos-emergence-prompt-shown', 'true');
      setShowEmergenceReporter(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [stateLoaded, showOnboarding]);

  // ═══ Daily Check-in Detection — trigger if >24h since last check-in ═══
  useEffect(() => {
    if (!stateLoaded || showOnboarding) return;
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const lastTs = lastCheckinTimestamp || 0;
    if (now - lastTs > TWENTY_FOUR_HOURS) {
      console.log('[LOGOS] Last check-in was >24h ago, triggering daily check-in');
      setShowDailyCheckin(true);
    }
  }, [stateLoaded, showOnboarding]);

  // ═══ Auto-save state to M5 API (debounced 2s) ═══
  useEffect(() => {
    if (!stateLoaded || !user || user.isGuest) return;
    const dbId = user.dbUserId || sessionStorage.getItem('logos-db-user-id');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      // LOGOS v5.0: Track previous states for criticality detection
      setPrevPrevState(prevState);
      setPrevState({ ...state });
      setPrevPrevCoherence(prevCoherence);
      setPrevCoherence(C || null);

      // Update check-in timestamp
      const now = Date.now();
      setLastCheckinTimestamp(now);
      try { localStorage.setItem('logos-last-checkin-ts', String(now)); } catch {}

      // Save to localStorage first (instant restore on next open, works offline)
      const stateToSave = { ...state };
      try { localStorage.setItem('logos-last-state', JSON.stringify(stateToSave)); } catch {}
      // Save to M5 (web) + Supabase last_state (always, iOS-safe)
      if (dbId) fetch(`${API_BASE}/api/state`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: parseInt(dbId), state: stateToSave }),
      })
        .then(r => { if (r.ok) console.log('[LOGOS] State auto-saved to M5'); })
        .catch(e => console.warn('[LOGOS] State save error:', e.message));
      // Always save to Supabase (works on iOS native where relative URLs fail)
      if (!user.isGuest) {
        supabaseClient.from('profiles').update({ last_state: stateToSave }).eq('id', user.id)
          .then(() => console.log('[LOGOS] State saved to Supabase'))
          .catch(e => console.warn('[LOGOS] Supabase state save error:', e.message));
      }
    }, 2000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [state, stateLoaded, user]);

  // ═══ Auto-apply HealthKit biometrics when first authorized on iOS ═══
  const healthKitAppliedRef = useRef(false);
  useEffect(() => {
    if (!healthKit.authorized || !healthKit.autoPopulatedDimensions) return;
    if (healthKitAppliedRef.current) return;
    // On native iOS: auto-apply watch data once per session when first synced
    if (Capacitor.isNativePlatform()) {
      healthKitAppliedRef.current = true;
      const dims = healthKit.autoPopulatedDimensions;
      Object.entries(dims).forEach(([key, value]) => {
        if (value !== null) setState(prev => ({ ...prev, [key]: value }));
      });
      console.log('[LOGOS] Auto-applied HealthKit biometrics to state');
    }
  }, [healthKit.authorized, healthKit.autoPopulatedDimensions]);

  // ═══ Persist state to sessionStorage for IFC bridge ═══
  useEffect(() => {
    if (!stateLoaded) return;
    try { sessionStorage.setItem('logos-state-28', JSON.stringify(state)); } catch {}
  }, [state, stateLoaded]);

  const updateDim = useCallback((key, val) => {
    setState(prev => ({ ...prev, [key]: val }));
  }, []);

  // ═══ Compute all metrics (Λ FIRST, then derived) — with optional custom weights ═══
  const lambdaResult = useMemo(() => computeLogosAlignment(state, customWeights.lambdaOverrides), [state, customWeights.lambdaOverrides]);
  const Lambda = lambdaResult.value;
  
  const viabilityResult = useMemo(() => computeViability(state, Lambda, customWeights.viabilityOverrides), [state, Lambda, customWeights.viabilityOverrides]);
  const V = viabilityResult.value;
  
  const entropyResult = useMemo(() => computeEntropy(state, Lambda, customWeights.entropyOverrides), [state, Lambda, customWeights.entropyOverrides]);
  const S = entropyResult.value;
  
  // ═══ Watch Biometric Engine (before metrics so it can modulate them) ═══
  const watchEngine = useMemo(() => {
    if (!healthKit.biometrics) return null;
    return processWatchBiometrics(healthKit.biometrics, healthKit.ecg, state);
  }, [healthKit.biometrics, healthKit.ecg, state]);

  const freeEnergyResult = useMemo(() => computeFreeEnergy(state, Lambda), [state, Lambda]);
  const F = useMemo(() => {
    const base = freeEnergyResult.value;
    if (!watchEngine) return base;
    return Math.max(0, Math.min(1.5, base + watchEngine.freeEnergyMod.totalMod));
  }, [freeEnergyResult.value, watchEngine]);
  
  const coherenceResult = useMemo(() => computeCoherence(state, Lambda), [state, Lambda]);
  const C = useMemo(() => {
    const base = coherenceResult.coherence;
    if (!watchEngine) return base;
    return Math.max(0, Math.min(1, base + watchEngine.coherenceMod.totalMod));
  }, [coherenceResult.coherence, watchEngine]);
  const domains = useMemo(() => {
    const base = coherenceResult.domains;
    if (!watchEngine || !watchEngine.watsonShifts.length) return base;
    const shifted = { ...base };
    for (const ws of watchEngine.watsonShifts) {
      const key = ws.domain.toLowerCase();
      if (key in shifted) {
        shifted[key] = Math.max(0, Math.min(1, shifted[key] + ws.shift));
      }
    }
    return shifted;
  }, [coherenceResult.domains, watchEngine]);
  
  const omegaResult = useMemo(() => computeOmega(Lambda, V, S, C, F), [Lambda, V, S, C, F]);
  const Omega = omegaResult.value;
  
  const qResult = useMemo(() => computeQ(Lambda, V, S), [Lambda, V, S]);
  const Q = qResult.value;
  
  const signalsRaw = useMemo(() => computeLevinSignals(state, Lambda), [state, Lambda]);
  const biasesRaw = useMemo(() => computeHoffmanInterface(state, Lambda), [state, Lambda]);
  const piRaw = useMemo(() => inhibitionPolicy(Lambda, V, S, C, prevLambda), [Lambda, V, S, C, prevLambda]);
  const pi2Raw = useMemo(() => metaPolicy(Lambda, V, S, C, signalsRaw), [Lambda, V, S, C, signalsRaw]);

  // i18n overlays for core engine strings
  const pi = useMemo(() => {
    if (watchEngine?.policyOverride?.active) {
      return {
        ...piRaw,
        verdict: 'PAUSA',
        reason: watchEngine.policyOverride.reason,
        inhibited: true,
      };
    }
    return {
      ...piRaw,
      verdict: t(`verdicts.${piRaw.verdict}`, piRaw.verdict),
      reason: piRaw.reasonKey ? t(`policyReasons.${piRaw.reasonKey}`, piRaw.reason) : piRaw.reason,
    };
  }, [piRaw, t, watchEngine]);
  const pi2 = useMemo(() => ({
    ...pi2Raw,
    verdict: t(`verdicts.${pi2Raw.verdict}`, pi2Raw.verdict),
    reason: pi2Raw.reasonKey ? t(`policyReasons.${pi2Raw.reasonKey}`, pi2Raw.reason) : pi2Raw.reason,
  }), [pi2Raw, t]);
  const signals = useMemo(() => {
    const translated = signalsRaw.map(sig => {
      if (!sig.signalKey) return sig;
      const tSig = t(`levinSignals.${sig.signalKey}`, { returnObjects: true });
      if (typeof tSig !== 'object') return sig;
      return { ...sig, type: tSig.type || sig.type, desc: tSig.desc || sig.desc, action: tSig.action || sig.action, logosLink: tSig.logosLink || sig.logosLink };
    });
    if (!watchEngine?.levinSignals?.length) return translated;
    const watchSignals = watchEngine.levinSignals.map(ws => ({
      type: ws.label, severity: ws.severity, desc: ws.description,
      action: '', logosLink: '', source: ws.source, isWatch: true,
    }));
    return [...translated, ...watchSignals];
  }, [signalsRaw, t, watchEngine]);
  const biases = useMemo(() => {
    const translated = biasesRaw.map(b => {
      if (!b.biasKey) return b;
      const tB = t(`hoffmanBiases.${b.biasKey}`, { returnObjects: true });
      if (typeof tB !== 'object') return b;
      return { ...b, type: tB.type || b.type, desc: tB.desc || b.desc, correction: tB.correction || b.correction, logosLink: tB.logosLink || b.logosLink };
    });
    if (!watchEngine?.hoffmanBiases?.length) return translated;
    const watchBiases = watchEngine.hoffmanBiases.map(wb => ({
      type: wb.label, severity: wb.magnitude, desc: wb.description,
      correction: t('misc.subjVsObj', { subj: (wb.subjectiveValue * 100).toFixed(0), obj: (wb.objectiveValue * 100).toFixed(0) }),
      logosLink: '', isWatch: true,
    }));
    return [...translated, ...watchBiases];
  }, [biasesRaw, t, watchEngine]);
  const trajectory = useMemo(() => projectTrajectory(state), [state]);
  const recommendations = useMemo(() => generateRecommendations(state, Lambda, V, S, C, signals, tDomains, t), [state, Lambda, V, S, C, signals, tDomains, t]);
  const alimentoRecs = useMemo(() => getAlimentoRecommendations(state), [state]);
  const THEORY = useMemo(() => {
    const tSec = t('theory.sections', { returnObjects: true });
    if (typeof tSec === 'object' && tSec !== null && !Array.isArray(tSec)) {
      return THEORY_BASE.map(sec => ({
        ...sec,
        title: tSec[sec.id]?.title || sec.title,
        content: tSec[sec.id]?.content || sec.content,
      }));
    }
    return THEORY_BASE;
  }, [t]);
  const watson = useMemo(() => computeWatsonEnergy(state, Lambda), [state, Lambda]);

  // ═══ CONTEXTUAL TOOLTIPS ═══
  const ctxTips = useMemo(() => {
    const weakDomain = Object.entries(watson.domainScores).sort((a,b)=>a[1]-b[1])[0];
    const weakLabel = weakDomain ? (tDomains[weakDomain[0]]?.label || weakDomain[0]) : '';
    const top3 = Object.values(LOGOS_COMPONENTS).flatMap(g => g.items || []).map(c => ({ key: c.key, gap: c.weight * (1 - (state[c.key] || 0.5)) }))
      .sort((a,b) => b.gap - a.gap).slice(0,3)
      .map(d => { for (const dk of Object.keys(tDomains)) { if (tDomains[dk]?.dims?.[d.key]) return tDomains[dk].dims[d.key].label; } return d.key; })
      .join(', ');
    const lk = Lambda < 0.15 ? 'critical' : Lambda < 0.30 ? 'low' : Lambda < 0.50 ? 'seeking' : Lambda < 0.70 ? 'aligned' : 'optimal';
    const ok = Omega < 0.20 ? 'emergency' : Omega < 0.35 ? 'alert' : Omega < 0.50 ? 'transition' : Omega < 0.70 ? 'active' : 'flourishing';
    const vk = V < 0.25 ? 'critical' : V < 0.40 ? 'low' : V < 0.55 ? 'moderate' : V < 0.70 ? 'healthy' : 'optimal';
    const sk = S < 0.25 ? 'low' : S < 0.40 ? 'moderate' : S < 0.60 ? 'high' : 'critical';
    const ck = C < 0.25 ? 'fragmented' : C < 0.40 ? 'unbalanced' : C < 0.55 ? 'moderate' : C < 0.75 ? 'balanced' : 'harmonious';
    const fk = F < 0.15 ? 'optimal' : F < 0.30 ? 'close' : F < 0.50 ? 'moderate' : F < 0.70 ? 'far' : 'veryFar';
    return {
      lambda: t(`metricBar.ctx.lambda.${lk}`) + (lk !== 'optimal' ? ' ' + t('metricBar.ctx.lambda.improve', { dims: top3 }) : ''),
      omega: t(`metricBar.ctx.omega.${ok}`),
      viability: t(`metricBar.ctx.viability.${vk}`),
      entropy: t(`metricBar.ctx.entropy.${sk}`),
      coherence: t(`metricBar.ctx.coherence.${ck}`, { domain: weakLabel }),
      freeEnergy: t(`metricBar.ctx.freeEnergy.${fk}`),
    };
  }, [Lambda, Omega, V, S, C, F, state, watson.domainScores, tDomains, t]);

  // LOGOS v5.0: Emergence Readiness Score
  const ers = useMemo(() => {
    if (typeof Lambda === 'number' && typeof V === 'number' &&
        typeof C === 'number' && typeof S === 'number') {
      return computeERS(Lambda, V, C, S);
    }
    return 0;
  }, [Lambda, V, C, S]);

  // LOGOS v5.0: Emergence Index E(x) — EVOLUTIONARY (carries forward via EMA)
  // Uses ref for previousEmergenceValue to avoid infinite re-render loop
  const emergenceIndex = useMemo(() => {
    const result = computeEmergenceIndex(state, prevState, lastEmergenceRef.current, lastCheckinTimestamp);
    // Update ref + localStorage after computation (no re-render triggered)
    if (result?.value != null && isFinite(result.value)) {
      lastEmergenceRef.current = result.value;
      try { localStorage.setItem('logos-emergence-value', String(result.value)); } catch {}
    }
    return result;
  }, [state, prevState, lastCheckinTimestamp]);

  // LOGOS v5.0: Multi-Scale Lambda
  const multiLambda = useMemo(() => {
    return computeMultiScaleLambda(state);
  }, [state]);

  // LOGOS v5.0: Criticality Alert
  const criticalityAlert = useMemo(() => {
    return computeCriticalityAlert(
      state, prevState, prevPrevState,
      C || 0.5, prevCoherence, prevPrevCoherence
    );
  }, [state, prevState, prevPrevState, C, prevCoherence, prevPrevCoherence]);

  // LOGOS v5.0: Shannon SNR
  const snrResult = useMemo(() => {
    return computeShannonSNR(Lambda || 0.5, S || 0.5, null);
  }, [Lambda, S]);

  // Check and award badges when metrics change
  useEffect(() => {
    if (!stateLoaded || showOnboarding) return;
    const lessonDay = parseInt(localStorage.getItem('logos-lesson-day') || '0');
    const emergenceReports = (() => { try { return JSON.parse(localStorage.getItem('logos-emergence-reports') || '[]').length; } catch { return 0; } })();
    const newBadges = checkAndAwardBadges({
      totalDays: streak.totalDays || 0,
      streak: streak.current || 0,
      lambda: Lambda,
      domainScores: watson.domainScores,
      emergenceReports,
      lessonsCompleted: lessonDay,
    });
    if (newBadges.length > 0) {
      setNewBadgeToast(newBadges[0]);
      setTimeout(() => setNewBadgeToast(null), 4000);
    }
  }, [Lambda, stateLoaded, showOnboarding]);

  // Check for learning moment triggers
  useEffect(() => {
    if (!stateLoaded || showOnboarding) return;
    const trigger = checkLearningTrigger({
      signals, V, verdict: piRaw.verdict,
      deltaLambda: Lambda - prevLambda,
      biases: biasesRaw, ers, pAMinus: null,
    });
    if (trigger && !activeLearningMoment) setActiveLearningMoment(trigger);
  }, [signals, V, piRaw.verdict, Lambda, prevLambda, biasesRaw, ers, stateLoaded, showOnboarding, activeLearningMoment]);

  // Track Lambda changes
  useEffect(() => {
    const timer = setTimeout(() => setPrevLambda(Lambda), 500);
    return () => clearTimeout(timer);
  }, [Lambda]);

  // Track history (last 50 states)
  useEffect(() => {
    setHistory(prev => {
      const next = [...prev, { t: prev.length, V, S, "\u039B": Lambda, "\u03A9": Omega, C, Q }];
      return next.slice(-50);
    });
  }, [V, S, Lambda, Omega, C, Q]);

  // Session lifecycle: start on mount, save on unload
  const metricsRef = useRef({ lambda: Lambda, omega: Omega, V, S, C, F, Q, verdict: pi.verdict });
  useEffect(() => {
    metricsRef.current = { lambda: Lambda, omega: Omega, V, S, C, F, Q, verdict: pi.verdict };
  }, [Lambda, Omega, V, S, C, F, Q, pi.verdict]);

  // ═══ RELIABLE SESSION PERSISTENCE TO DB ═══
  // Every session is saved to PostgreSQL via UPSERT (client_session_id dedup).
  // Triggers: periodic (3 min), visibilitychange, beforeunload.
  const clientSessionId = useRef(`s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const sessionStartTime = useRef(new Date().toISOString());
  const sessionSavedRef = useRef(false);

  const buildSessionPayload = useCallback(() => {
    const m = metricsRef.current;
    const userId = sessionStorage.getItem('logos-db-user-id');
    // userId is optional: M5 save requires it, Supabase save uses auth user directly
    return {
      user_id: userId ? parseInt(userId) : null,
      client_session_id: clientSessionId.current,
      start_time: sessionStartTime.current,
      end_time: new Date().toISOString(),
      duration_min: Math.round((Date.now() - new Date(sessionStartTime.current).getTime()) / 60000),
      lambda: m.lambda, omega: m.omega,
      viability: m.V, entropy: m.S, coherence: m.C,
      free_energy: m.F, trajectory: m.Q,
      verdict: m.verdict,
      state_snapshot: state,
      domain_scores: domains,
    };
  }, [state, domains]);

  const saveSessionToDb = useCallback(async (isBeacon = false) => {
    const payload = buildSessionPayload();
    if (isBeacon) {
      // Beacon: only fires if M5 user_id is available
      if (payload.user_id) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(`${API_BASE}/api/sessions`, blob);
      }
    } else {
      // M5 save: only when user_id is known
      if (payload.user_id) {
        try {
          await fetch(`${API_BASE}/api/sessions`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          sessionSavedRef.current = true;
          console.log('[LOGOS] Session saved to M5 DB (UPSERT)', clientSessionId.current);
        } catch (e) { console.warn('[LOGOS] Session M5 save failed:', e.message); }
      }
      // Always save to Supabase (works on native iOS regardless of M5 sync)
      if (!user?.isGuest) {
        const { data: { user: authUser } } = await supabaseClient.auth.getUser();
        if (authUser) {
          const channel = Capacitor.isNativePlatform() ? 'ios' : 'web';
          supabaseClient.from('sessions').insert({
            user_id: authUser.id,
            channel,
            start_time: payload.start_time,
            end_time: payload.end_time,
            duration_min: payload.duration_min,
            lambda: payload.lambda,
            omega: payload.omega,
            viability: payload.viability,
            entropy: payload.entropy,
            coherence: payload.coherence,
            free_energy: payload.free_energy,
            trajectory: payload.trajectory,
            verdict: payload.verdict,
            state_snapshot: payload.state_snapshot,
            domain_scores: payload.domain_scores,
          }).then(() => console.log('[LOGOS] Session saved to Supabase'))
            .catch(e => console.warn('[LOGOS] Supabase session save error:', e.message));
        }
      }
    }
    // Also keep localStorage cache for offline/fast access
    saveSessionSnapshot(metricsRef.current, state, domains);
  }, [buildSessionPayload, state, domains]);

  useEffect(() => {
    const sessionInfo = startSession();

    // Periodic auto-save every 3 minutes
    const intervalId = setInterval(() => saveSessionToDb(false), 3 * 60 * 1000);

    // Save when tab becomes hidden (user switches away)
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') saveSessionToDb(false);
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Final save on page close (beacon as last resort)
    const handleUnload = () => {
      const m = metricsRef.current;
      endSession(m, state, domains);
      saveSessionToDb(true);
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══ KAIRA Integration Functions ═══
  const interpretLogosAlignment = useCallback((lambda) => {
    if (lambda >= 0.70) return { level: 'ÓPTIMO', color: COLORS.viability };
    if (lambda >= 0.55) return { level: 'BIEN', color: COLORS.logos };
    if (lambda >= 0.40) return { level: 'NEUTRO', color: COLORS.textDim };
    if (lambda >= 0.25) return { level: 'BAJO', color: COLORS.entropy };
    return { level: 'CRÍTICO', color: '#ef4444' };
  }, []);

  const buildLogosContext = useCallback(() => {
    if (!lambdaResult || !watson) return null;

    const bridge = computeLogosBridge({
      logosState: state,
      lambda: lambdaResult.value,
      freeEnergy: freeEnergyResult.value,
      viability: viabilityResult.value,
      entropy: entropyResult.value,
      coherence: coherenceResult.coherence,
      levinSignals: signals,
      hoffmanBiases: biasesRaw,
      policyVerdict: pi.verdict,
    });

    // Get weakest 3 domains
    const domainScores = Object.entries(watson.domainScores)
      .map(([key, value]) => ({ 
        key, 
        label: tDomains[key]?.label || key, 
        value: value 
      }))
      .sort((a, b) => a.value - b.value)
      .slice(0, 3);

    return {
      lambda: lambdaResult.value,
      lambdaLevel: interpretLogosAlignment(lambdaResult.value).level,
      freeEnergy: freeEnergyResult.value,
      viability: viabilityResult.value,
      entropy: entropyResult.value,
      coherence: coherenceResult.coherence,
      omega: omegaResult.value,
      lers: ers,
      policyVerdict: pi.verdict,
      policyReason: pi.reason,
      levinSignals: signals,
      hoffmanBiases: biasesRaw,
      weakestDomains: domainScores,
      bridge,
      stateSnapshot: state,
    };
  }, [state, lambdaResult, freeEnergyResult, viabilityResult, entropyResult, coherenceResult, omegaResult, ers, pi, signals, biasesRaw, watson, tDomains, interpretLogosAlignment]);

  const handleDimensionsExtracted = useCallback((newState, sources) => {
    // Update LOGOS state with conversationally extracted dimensions
    setState(prev => ({ ...prev, ...newState }));
    setDimensionSources(sources);
    
    // Build updated LOGOS context for therapy mode
    const updatedContext = buildLogosContext();
    setLogosContext(updatedContext);
  }, [buildLogosContext]);

  // Radar data
  const radarData = useMemo(() => {
    return Object.entries(domains).map(([key, val]) => ({
      domain: tDomains[key]?.label || key, value: +(val * 100).toFixed(0), fullMark: 100,
    }));
  }, [domains, tDomains]);

  const domainBarData = useMemo(() => {
    return Object.entries(watson.domainScores).map(([key, val]) => ({
      domain: tDomains[key]?.label || key, score: +(val * 100).toFixed(0), fill: tDomains[key]?.color || COLORS.accent,
    }));
  }, [watson.domainScores, tDomains]);

  // Monte Carlo run — animation plays first, computation runs after onComplete
  const handleRunMC = useCallback(() => {
    setMcRunning(true);
    setMcAnimDone(false);
    setMcResult(null);
  }, []);

  return (
    <div className="logos-app" style={{
      minHeight: "100vh", background: COLORS.bg, color: COLORS.text,
      fontFamily: fonts.sans, padding: mobile ? "12px 8px" : "20px 16px",
      backgroundImage: `radial-gradient(ellipse at 50% 0%, ${COLORS.logos}08 0%, transparent 60%)`,
      maxWidth: "100vw", overflowX: "hidden",
    }}>
      <style>{ANIM_STYLES}</style>
      
      {/* ONBOARDING MODAL */}
      {showOnboarding && <OnboardingModal onClose={() => setShowOnboarding(false)} />}

      {/* PAYWALL MODAL */}
      <PaywallModal
        visible={showPaywall}
        onClose={() => setShowPaywall(false)}
        onPurchase={async () => {
          const ok = await subscription.purchase();
          if (ok) setShowPaywall(false);
        }}
        onRestore={async () => {
          const ok = await subscription.restore();
          if (ok) setShowPaywall(false);
          return ok;
        }}
        purchasing={subscription.purchasing}
        products={subscription.products}
        error={subscription.error}
        isNative={subscription.isNative}
      />

      {/* AI CONSENT MODAL */}
      {showAiConsent && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "fadeInUp 0.4s ease",
        }}>
          <div style={{
            width: "92%", maxWidth: 480, maxHeight: "90vh", overflow: "auto",
            background: `linear-gradient(145deg, ${COLORS.surface}, ${COLORS.bg})`,
            border: `1px solid rgba(192,132,252,0.40)`, borderRadius: 20,
            padding: "28px 24px", position: "relative",
            boxShadow: `0 0 60px rgba(192,132,252,0.15)`,
          }}>
            <div style={{ textAlign: "center", fontSize: 40, marginBottom: 12 }}>🧠</div>
            <h2 style={{
              textAlign: "center", fontSize: 18, fontWeight: 600, margin: "0 0 12px 0",
              fontFamily: fonts.display, color: COLORS.text,
            }}>
              {t('aiConsent.title')}
            </h2>
            <p style={{ fontSize: 12, lineHeight: 1.7, color: COLORS.textDim, fontFamily: fonts.sans, marginBottom: 16 }}>
              {t('aiConsent.body')}
            </p>

            <div style={{
              padding: 14, background: "rgba(192,132,252,0.06)", borderRadius: 12,
              border: "1px solid rgba(192,132,252,0.20)", marginBottom: 16,
            }}>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: "#C084FC", marginBottom: 8, letterSpacing: 1 }}>
                {t('aiConsent.dataShared')}
              </div>
              {['metrics', 'domains', 'signals', 'noPersonal'].map(key => (
                <div key={key} style={{ fontSize: 11, fontFamily: fonts.sans, color: COLORS.textDim, lineHeight: 1.6, padding: "2px 0" }}>
                  <span style={{ color: key === 'noPersonal' ? "#10B981" : "#C084FC", marginRight: 6 }}>
                    {key === 'noPersonal' ? '🔒' : '•'}
                  </span>
                  {t(`aiConsent.dataItems.${key}`)}
                </div>
              ))}
            </div>

            <div style={{
              padding: 10, background: "rgba(245,197,66,0.06)", borderRadius: 10,
              border: "1px solid rgba(245,197,66,0.20)", marginBottom: 16,
              fontSize: 10, fontFamily: fonts.sans, color: COLORS.textMuted, lineHeight: 1.6,
            }}>
              <strong style={{ color: COLORS.gold }}>{t('aiConsent.provider')}</strong><br />
              {t('aiConsent.providerNote')}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={() => {
                localStorage.setItem('logos-ai-consent', 'true');
                setAiConsentGiven(true);
                setShowAiConsent(false);
              }} style={{
                width: "100%", padding: "14px 28px", borderRadius: 12, border: "none", cursor: "pointer",
                fontFamily: fonts.mono, fontSize: 12, fontWeight: 700, letterSpacing: 1,
                background: "linear-gradient(135deg, #C084FC, #818CF8)", color: "#fff",
                boxShadow: "0 4px 24px rgba(192,132,252,0.40)",
              }}>
                {t('aiConsent.accept')}
              </button>
              <button onClick={() => setShowAiConsent(false)} style={{
                width: "100%", padding: "10px 20px", borderRadius: 10, cursor: "pointer",
                background: "transparent", border: `1px solid ${COLORS.border}`,
                fontFamily: fonts.mono, fontSize: 10, color: COLORS.textMuted,
              }}>
                {t('aiConsent.decline')}
              </button>
            </div>

            <p style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, textAlign: "center", marginTop: 12, opacity: 0.7 }}>
              {t('aiConsent.settings')}
            </p>
          </div>
        </div>
      )}

      {/* DELETE ACCOUNT CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "fadeInUp 0.4s ease",
        }}>
          <div style={{
            width: "92%", maxWidth: 420, maxHeight: "90vh", overflow: "auto",
            background: `linear-gradient(145deg, ${COLORS.surface}, ${COLORS.bg})`,
            border: "1px solid rgba(239,68,68,0.40)", borderRadius: 20,
            padding: "28px 24px", position: "relative",
            boxShadow: "0 0 60px rgba(239,68,68,0.15)",
          }}>
            <div style={{ textAlign: "center", fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <h2 style={{
              textAlign: "center", fontSize: 18, fontWeight: 600, margin: "0 0 12px 0",
              fontFamily: fonts.display, color: "#EF4444",
            }}>
              {t('settings.deleteAccountConfirmTitle')}
            </h2>
            <p style={{
              fontSize: 12, lineHeight: 1.7, color: COLORS.textDim, fontFamily: fonts.sans,
              whiteSpace: "pre-line", marginBottom: 20,
            }}>
              {t('settings.deleteAccountConfirmBody')}
            </p>

            {deleteError && (
              <div style={{
                marginBottom: 16, padding: 10, borderRadius: 8,
                background: "#ef444410", border: "1px solid #ef444430",
                fontSize: 10, fontFamily: fonts.mono, color: "#EF4444", textAlign: "center",
              }}>
                {t('settings.deleteAccountError', { message: deleteError })}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={async () => {
                  setDeleteLoading(true);
                  setDeleteError(null);
                  try {
                    const { deleteAccount: doDelete } = await import('../services/supabase');
                    await doDelete();
                    window.location.reload();
                  } catch (e) {
                    setDeleteError(e.message);
                    setDeleteLoading(false);
                  }
                }}
                disabled={deleteLoading}
                style={{
                  width: "100%", padding: "14px 28px", borderRadius: 12, border: "none",
                  cursor: deleteLoading ? "wait" : "pointer",
                  fontFamily: fonts.mono, fontSize: 12, fontWeight: 700, letterSpacing: 1,
                  background: "#EF4444", color: "#fff",
                  boxShadow: "0 4px 24px rgba(239,68,68,0.40)",
                  opacity: deleteLoading ? 0.6 : 1,
                }}
              >
                {deleteLoading ? t('settings.deleteAccountDeleting') : t('settings.deleteAccountConfirmButton')}
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                disabled={deleteLoading}
                style={{
                  width: "100%", padding: "10px 20px", borderRadius: 10, cursor: "pointer",
                  background: "transparent", border: `1px solid ${COLORS.border}`,
                  fontFamily: fonts.mono, fontSize: 10, color: COLORS.textMuted,
                }}
              >
                {t('settings.deleteAccountCancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MICRO LESSON MODAL */}
      {showMicroLesson && !showOnboarding && (
        <MicroLesson onClose={() => setShowMicroLesson(false)} />
      )}

      {/* WEEKLY DIGEST MODAL */}
      {showWeeklyDigest && !showOnboarding && !showMicroLesson && (
        <WeeklyDigest
          lambda={Lambda}
          prevWeekLambda={prevLambda}
          sessions={history.length}
          streak={streak.current || 0}
          strongestDomain={(() => { const s = Object.entries(watson.domainScores).sort((a,b)=>b[1]-a[1])[0]; return s ? (tDomains[s[0]]?.label || s[0]) : ''; })()}
          weakestDomain={(() => { const w = Object.entries(watson.domainScores).sort((a,b)=>a[1]-b[1])[0]; return w ? (tDomains[w[0]]?.label || w[0]) : ''; })()}
          onClose={() => setShowWeeklyDigest(false)}
        />
      )}

      {/* LEARNING MOMENT MODAL */}
      {activeLearningMoment && !showOnboarding && !showMicroLesson && !showWeeklyDigest && (
        <LearningMoment triggerId={activeLearningMoment} onClose={() => setActiveLearningMoment(null)} />
      )}

      {/* DAILY CHECK-IN MODAL */}
      {showDailyCheckin && !showOnboarding && (
        <DailyCheckin
          currentState={state}
          onComplete={(newState) => {
            setState(prev => ({ ...prev, ...newState }));
            setShowDailyCheckin(false);
            const now = Date.now();
            setLastCheckinTimestamp(now);
            try { localStorage.setItem('logos-last-checkin-ts', String(now)); } catch {}
            console.log('[LOGOS] Daily check-in completed');
          }}
          onSkip={() => {
            setShowDailyCheckin(false);
            console.log('[LOGOS] Daily check-in skipped');
          }}
        />
      )}
      
      {/* NEW BADGE TOAST */}
      {newBadgeToast && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          background: `linear-gradient(135deg, ${COLORS.surface}, ${newBadgeToast.tierColor || COLORS.gold}15)`,
          border: `1px solid ${newBadgeToast.tierColor || COLORS.gold}50`, borderRadius: 14,
          padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${newBadgeToast.tierColor || COLORS.gold}20`,
          animation: 'fadeInUp 0.3s ease',
        }}>
          <span style={{ fontSize: 24 }}>{newBadgeToast.icon}</span>
          <div>
            <div style={{ fontSize: 8, fontFamily: fonts.mono, color: newBadgeToast.tierColor || COLORS.gold, letterSpacing: 1.5 }}>
              {t('gamification.newBadge')}
            </div>
            <div style={{ fontSize: 12, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 600 }}>
              {t(`gamification.badges.${newBadgeToast.id}`, newBadgeToast.id)}
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{ textAlign: "center", marginBottom: 24, animation: "fadeInUp 0.6s ease", position: "relative", paddingTop: 44 }}>
        {/* ═══ STREAK + BADGES — top-left ═══ */}
        {streak.current > 0 && (
          <div style={{
            position: "absolute", top: 0, left: 0, display: "flex", alignItems: "center", gap: 8, zIndex: 100,
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 4,
              background: `${COLORS.gold}15`, border: `1px solid ${COLORS.gold}30`,
              borderRadius: 10, padding: "6px 10px",
            }}>
              <span style={{ fontSize: 14 }}>🔥</span>
              <span style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.gold, fontWeight: 700 }}>
                {streak.current}
              </span>
            </div>
            <div style={{
              display: "flex", alignItems: "center", gap: 3,
              background: `${COLORS.logos}10`, border: `1px solid ${COLORS.logos}25`,
              borderRadius: 10, padding: "6px 8px",
            }}>
              <span style={{ fontSize: 11 }}>🏆</span>
              <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.logos }}>
                {getEarnedCount()}/{BADGE_DEFS.length}
              </span>
            </div>
          </div>
        )}

        {/* ═══ SETTINGS GEAR — top-right ═══ */}
        <div ref={settingsMenuRef} style={{ position: "absolute", top: 0, right: 0, zIndex: 100 }}>
          <button
            onClick={() => setShowSettingsMenu(prev => !prev)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: showSettingsMenu ? `${COLORS.accent}25` : `${COLORS.textMuted}10`,
              border: `1px solid ${showSettingsMenu ? COLORS.accent + "50" : COLORS.border}`,
              borderRadius: 10, padding: "8px 14px", cursor: "pointer", transition: "all 0.3s",
              fontSize: 18, lineHeight: 1, color: showSettingsMenu ? COLORS.text : COLORS.textDim,
              boxShadow: showSettingsMenu ? `0 0 12px ${COLORS.accent}30` : `0 2px 8px rgba(0,0,0,0.2)`,
            }}
            aria-label="Configuración"
          >
            <span>⚙</span>
            <span style={{ fontSize: 9, fontFamily: fonts.mono, letterSpacing: 1 }}>{t('settings.title', 'AJUSTES')}</span>
          </button>

          {/* Dropdown */}
          {showSettingsMenu && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", right: 0, minWidth: 220,
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              borderRadius: 12, padding: 8, zIndex: 200,
              boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${COLORS.border}`,
              animation: "fadeInUp 0.2s ease",
            }}>
              {/* User info */}
              {user && !user.isGuest && (
                <div style={{ padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}`, marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontFamily: fonts.display, color: COLORS.text, fontWeight: 600 }}>
                    {user.name || user.email}
                  </div>
                  <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>
                    {user.email}
                  </div>
                </div>
              )}

              {/* ⚖ Pesos */}
              <button onClick={() => { setActiveTab("pesos"); setShowSettingsMenu(false); }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                background: activeTab === "pesos" ? `${COLORS.gold}15` : "transparent",
                color: activeTab === "pesos" ? COLORS.gold : COLORS.textDim,
                fontFamily: fonts.mono, fontSize: 10, textAlign: "left", transition: "all 0.2s",
              }}>
                <span style={{ fontSize: 14 }}>⚖</span>
                <div>
                  <div style={{ fontWeight: 600 }}>{t('settings.weightsConfig')}</div>
                  <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 1 }}>
                    {customWeights.enabled ? t('settings.weightsCustom') : t('settings.weightsDefault')}
                  </div>
                </div>
              </button>

              {/* 🌐 Idioma */}
              <button onClick={() => { i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es'); setShowSettingsMenu(false); }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                background: "transparent", color: COLORS.textDim,
                fontFamily: fonts.mono, fontSize: 10, textAlign: "left", transition: "all 0.2s",
              }}>
                <span style={{ fontSize: 14 }}>🌐</span>
                <div>
                  <div style={{ fontWeight: 600 }}>{t('settings.language')}</div>
                  <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 1 }}>
                    {i18n.language === 'es' ? t('settings.switchToEn') : t('settings.switchToEs')}
                  </div>
                </div>
              </button>

              {/* ✦ Suscripción */}
              <button onClick={() => {
                if (subscription.isPremium) { subscription.manage(); } else { setShowPaywall(true); }
                setShowSettingsMenu(false);
              }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                background: subscription.isPremium ? `${COLORS.logos}12` : "transparent",
                color: subscription.isPremium ? COLORS.logos : COLORS.textDim,
                fontFamily: fonts.mono, fontSize: 10, textAlign: "left", transition: "all 0.2s",
              }}>
                <span style={{ fontSize: 14 }}>✦</span>
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {subscription.isPremium ? t('subscription.activeLabel') : t('subscription.premiumTitle')}
                  </div>
                  <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 1 }}>
                    {subscription.isPremium
                      ? (subscription.expirationDate ? `${t('subscription.expiresLabel')} ${new Date(subscription.expirationDate).toLocaleDateString()}` : t('subscription.manageButton'))
                      : t('subscription.subscribeButton')
                    }
                  </div>
                </div>
              </button>

              {/* 🧠 AI Consent toggle */}
              <button onClick={() => {
                if (aiConsentGiven) {
                  localStorage.removeItem('logos-ai-consent');
                  setAiConsentGiven(false);
                } else {
                  setShowAiConsent(true);
                }
                setShowSettingsMenu(false);
              }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                background: aiConsentGiven ? `rgba(16,185,129,0.08)` : "transparent",
                color: aiConsentGiven ? "#10B981" : COLORS.textDim,
                fontFamily: fonts.mono, fontSize: 10, textAlign: "left", transition: "all 0.2s",
              }}>
                <span style={{ fontSize: 14 }}>🧠</span>
                <div>
                  <div style={{ fontWeight: 600 }}>{t('aiConsent.settingsLabel')}</div>
                  <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 1 }}>
                    {aiConsentGiven ? t('aiConsent.settingsEnabled') : t('aiConsent.settingsDisabled')}
                  </div>
                </div>
              </button>

              {/* 📖 Guía */}
              <button onClick={() => { setShowOnboarding(true); setShowSettingsMenu(false); }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                background: "transparent", color: COLORS.textDim,
                fontFamily: fonts.mono, fontSize: 10, textAlign: "left", transition: "all 0.2s",
              }}>
                <span style={{ fontSize: 14 }}>📖</span>
                <div style={{ fontWeight: 600 }}>Guía de Inicio</div>
              </button>

              {/* 🚪 Cerrar sesión */}
              {user && !user.isGuest && onLogout && (
                <button onClick={() => { onLogout(); setShowSettingsMenu(false); }} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "transparent", color: COLORS.entropy,
                  fontFamily: fonts.mono, fontSize: 10, textAlign: "left", transition: "all 0.2s",
                  marginTop: 4, borderTop: `1px solid ${COLORS.border}`, paddingTop: 12,
                }}>
                  <span style={{ fontSize: 14 }}>🚪</span>
                  <div style={{ fontWeight: 600 }}>{t('misc.logout')}</div>
                </button>
              )}

              {/* 🗑 Eliminar cuenta */}
              {user && !user.isGuest && (
                <button onClick={() => { setShowDeleteConfirm(true); setShowSettingsMenu(false); }} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "transparent", color: "#EF4444",
                  fontFamily: fonts.mono, fontSize: 10, textAlign: "left", transition: "all 0.2s",
                  marginTop: 4, borderTop: `1px solid ${COLORS.border}`, paddingTop: 12,
                }}>
                  <span style={{ fontSize: 14 }}>🗑</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('settings.deleteAccount')}</div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 1 }}>
                      {t('settings.deleteAccountDesc')}
                    </div>
                  </div>
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.logos, letterSpacing: 4, marginBottom: 6, textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, paddingRight: 0 }}>
          {t('header.subtitle')}
        </div>
        <h1 style={{
          fontSize: 36, fontWeight: 300, fontFamily: fonts.display, margin: 0,
          background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.logos}, ${COLORS.viability})`,
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          letterSpacing: 3, animation: "breathe 4s ease infinite",
        }}>
          LOGOS
        </h1>
        <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, letterSpacing: 1.5 }}>
          <InfoTooltip text={t('header.architectureTooltip')}>
            {t('header.architectureDesc')}
          </InfoTooltip>
        </div>
        <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.logos, marginTop: 4, opacity: 0.7 }}>
          {t('header.lambdaDesc')}
        </div>
      </div>

      {/* MASTER METRICS BAR — All clickable for breakdown */}
      <div className="logos-metric-bar" style={{
        display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8, marginBottom: 20,
        padding: "14px 16px", background: COLORS.surface, borderRadius: 14,
        border: `1px solid ${COLORS.border}`, animation: "fadeInUp 0.5s ease 0.1s both",
        boxShadow: `0 0 30px ${COLORS.logos}08`,
      }}>
        <MetricCard label={t('metricBar.omegaLabel')} value={Omega} color={COLORS.omega}
          tooltip={ctxTips.omega}
          breakdown={omegaResult.breakdown}
          onOpenDetail={() => setDetailPanel({ id: "omega", label: t('motor.omegaPanel'), color: COLORS.omega, value: Omega, data: omegaResult })} />
        <MetricCard label={t('metricBar.lambdaLabel')} value={Lambda} color={COLORS.logos}
          tooltip={ctxTips.lambda}
          breakdown={lambdaResult.breakdown}
          onOpenDetail={() => setDetailPanel({ id: "lambda", label: t('motor.lambdaPanel'), color: COLORS.logos, value: Lambda, data: lambdaResult })} />
        <MetricCard label={t('metricBar.vLabel')} value={V} color={COLORS.viability}
          tooltip={ctxTips.viability}
          breakdown={viabilityResult.breakdown}
          onOpenDetail={() => setDetailPanel({ id: "viability", label: t('motor.vPanel'), color: COLORS.viability, value: V, data: viabilityResult })} />
        <MetricCard label={t('metricBar.sLabel')} value={S} color={COLORS.entropy}
          tooltip={ctxTips.entropy}
          breakdown={entropyResult.breakdown}
          onOpenDetail={() => setDetailPanel({ id: "entropy", label: t('motor.sPanel'), color: COLORS.entropy, value: S, data: entropyResult })} />
        <MetricCard label={t('metricBar.cLabel')} value={C} color={COLORS.coherence}
          tooltip={ctxTips.coherence}
          breakdown={coherenceResult.breakdown}
          onOpenDetail={() => setDetailPanel({ id: "coherence", label: t('motor.cPanel'), color: COLORS.coherence, value: C, data: coherenceResult })} />
        <MetricCard label={t('metricBar.fLabel')} value={F} color={COLORS.freeEnergy}
          tooltip={ctxTips.freeEnergy}
          onOpenDetail={() => setDetailPanel({ id: "freeEnergy", label: t('motor.fPanel'), color: COLORS.freeEnergy, value: F, data: freeEnergyResult })} />
        <MetricCard label={t('metricBar.qLabel')} value={Q} color={COLORS.accent}
          tooltip={t('metricBar.qTooltip')}
          onOpenDetail={() => setDetailPanel({ id: "trajectory", label: t('metricBar.qLabel'), color: COLORS.accent, value: Q, data: qResult })} />
        <MetricCard label="ERS" value={ers} color="#A855F7"
          tooltip={t('metricBar.ersTooltip', 'ERS — Emergence Readiness Score [0,1]. Haz clic aquí en cualquier momento para reportar un evento emergente (insight, sincronicidad, flow).')}
          onOpenDetail={() => setShowEmergenceReporter(true)} />
        
        {/* π verdict with glow */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8, padding: "8px 16px",
          background: `${pi.color}12`, borderRadius: 10, border: `1px solid ${pi.color}35`,
          animation: piRaw.verdict === "ACTÚA" ? "glow 2s ease infinite" : "none",
        }}>
          <span style={{ fontSize: 18 }}>{pi.icon}</span>
          <div>
            <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 1 }}>π(Λ̇)</div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: fonts.mono, color: pi.color }}>{pi.verdict}</div>
          </div>
        </div>
      </div>

      {/* TAB NAVIGATION */}
      <div className="logos-tab-nav" style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 20, justifyContent: "center", alignItems: "center", animation: "fadeInUp 0.5s ease 0.2s both" }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer",
            fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1, transition: "all 0.3s",
            background: activeTab === tab.id ? `${COLORS.accent}25` : "transparent",
            color: activeTab === tab.id ? COLORS.text : COLORS.textMuted,
            border: activeTab === tab.id ? `1px solid ${COLORS.accent}40` : `1px solid transparent`,
          }}>
            {tab.icon} {t(`tabs.${tab.id}`, tab.label)}
          </button>
        ))}
        
        {/* Separator + IFC Button */}
        <div className="logos-ifc-separator" style={{ width: 1, height: 24, background: COLORS.border, margin: "0 8px" }} />
        <a href="/ifc" style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "8px 16px", borderRadius: 8, cursor: "pointer", textDecoration: "none",
          fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1, transition: "all 0.3s",
          background: "linear-gradient(135deg, #14b8a620, #0d948320)",
          color: "#14b8a6", border: "2px solid #14b8a650",
          boxShadow: "0 0 12px #14b8a615",
        }}>
          <span style={{ fontSize: 12 }}>⟐</span>
          <span>{t('misc.ifcControl')}</span>
          <span style={{ fontSize: 10, opacity: 0.7 }}>→</span>
        </a>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          KAIRA TAB — Conversational Therapeutic AI (FULL SESSION)
          ═══════════════════════════════════════════════════════════ */}
      {activeTab === "kaira" && (
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          {/* KAIRA Header */}
          <div style={{
            background: "linear-gradient(135deg, rgba(168,85,247,0.10) 0%, rgba(99,102,241,0.06) 100%)",
            border: "1px solid rgba(168,85,247,0.20)", borderRadius: 20,
            padding: "24px", marginBottom: 20, textAlign: "center",
          }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.2em", color: "#A855F7", marginBottom: 8 }}>
              KAIRA v3.1 AIFET — LOGOS COMPANION
            </div>
            <h2 style={{ fontFamily: fonts.heading, fontSize: 24, color: COLORS.text, margin: "8px 0" }}>
              {i18n.language === 'es' ? 'Sesión de Acompañamiento' : 'Companion Session'}
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#8B85A0", margin: "12px auto 0", maxWidth: 600, lineHeight: 1.6 }}>
              {i18n.language === 'es' 
                ? 'Espacio dedicado para tu sesión de acompañamiento sin distracciones. KAIRA entiende tu estado de consciencia LOGOS completo.'
                : 'Dedicated space for your distraction-free companion session. KAIRA understands your complete LOGOS consciousness state.'}
            </p>
          </div>

          {/* KAIRA Panel */}
          <div style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 16,
            overflow: "hidden",
            height: "600px",
          }}>
            <KairaPanel
              logosContext={buildLogosContext()}
              onDimensionsExtracted={handleDimensionsExtracted}
              initialMode="checkin"
              language={i18n.language}
              sessionId={kairaSessionId}
              userId={user?.dbUserId || sessionStorage.getItem('logos-db-user-id')}
            />
          </div>

          {/* KAIRA Info */}
          <div style={{
            marginTop: 20,
            padding: 16,
            background: `${COLORS.accent}08`,
            borderRadius: 12,
            border: `1px solid ${COLORS.accent}20`,
          }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 10, color: COLORS.accent, marginBottom: 8, letterSpacing: "0.1em" }}>
              {i18n.language === 'es' ? 'CÓMO FUNCIONA' : 'HOW IT WORKS'}
            </div>
            <ul style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: COLORS.textMuted, lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
              <li><strong>{i18n.language === 'es' ? 'Modo Check-in' : 'Check-in Mode'}:</strong> {i18n.language === 'es' ? 'Conversa naturalmente sobre cómo te sientes. KAIRA extrae automáticamente tus 28 dimensiones.' : 'Chat naturally about how you feel. KAIRA automatically extracts your 28 dimensions.'}</li>
              <li><strong>{i18n.language === 'es' ? 'Modo Acompañamiento' : 'Companion Mode'}:</strong> {i18n.language === 'es' ? 'Después del check-in, KAIRA usa tu Λ, V, S, C, F, Ω y LERS para acompañamiento contextual preciso.' : 'After check-in, KAIRA uses your Λ, V, S, C, F, Ω and LERS for precise contextual companion support.'}</li>
              <li><strong>{i18n.language === 'es' ? 'Protocolo Layer 10' : 'Layer 10 Protocol'}:</strong> {i18n.language === 'es' ? 'Positive Geometry Protocol activa intervenciones urgentes si detecta crisis.' : 'Positive Geometry Protocol activates urgent interventions if crisis detected.'}</li>
              <li><strong>{i18n.language === 'es' ? 'Privacidad' : 'Privacy'}:</strong> {i18n.language === 'es' ? 'Todas las conversaciones se guardan en Supabase con encriptación. Nunca compartimos tus datos.' : 'All conversations saved in Supabase with encryption. We never share your data.'}</li>
            </ul>
          </div>
        </div>
      )}

      {/* ═══ ESTADO TAB ═══ */}
      {activeTab === "estado" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <TabAIGuide panel="estado" accent="#10B981" autoExpand={!initialSeenTabs.includes('estado')} data={{ domains: watson.domainScores, lambda: Lambda, weakest: Object.entries(watson.domainScores).sort((a,b)=>a[1]-b[1])[0]?.[0], strongest: Object.entries(watson.domainScores).sort((a,b)=>b[1]-a[1])[0]?.[0] }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          </div>
          {/* ═══ ERS > 0.60 PROACTIVE BANNER ═══ */}
          {ers > 0.60 && !sessionStorage.getItem('logos-ers-banner-dismissed') && (
            <div style={{ gridColumn: "1 / -1", background: "linear-gradient(135deg, #A855F720 0%, #06B6D420 100%)", border: "1px solid #A855F740", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 28, flexShrink: 0 }}>✨</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontFamily: fonts.mono, color: "#A855F7", fontWeight: 700, marginBottom: 3, letterSpacing: 1 }}>
                  {t('emergence.banner.title')}
                </div>
                <div style={{ fontSize: 11, fontFamily: fonts.sans, color: COLORS.text, lineHeight: 1.4 }}>
                  {t('emergence.banner.body', { ers: (ers * 100).toFixed(0) + '%' })}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                <button onClick={() => { setShowEmergenceReporter(true); sessionStorage.setItem('logos-ers-banner-dismissed', 'true'); }}
                  style={{ fontSize: 9, fontFamily: fonts.mono, color: "#A855F7", background: "#A855F720", border: "1px solid #A855F740", borderRadius: 6, padding: "4px 8px", cursor: "pointer", whiteSpace: "nowrap" }}>
                  {t('emergence.banner.reportNow')}
                </button>
                <button onClick={() => { sessionStorage.setItem('logos-ers-banner-dismissed', 'true'); window.dispatchEvent(new Event('storage')); }}
                  style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, background: "transparent", border: "none", cursor: "pointer", padding: 2 }}>
                  {t('emergence.banner.dismiss')}
                </button>
              </div>
            </div>
          )}
          {(() => {
            const weakest = Object.entries(watson.domainScores).sort((a,b)=>a[1]-b[1])[0]?.[0];
            return Object.entries(tDomains).map(([dk, dv], idx) => {
              const score = watson.domainScores[dk] || 0;
              const isWeak = score < 0.35;
              const defaultOpen = true;
              return (
                <div key={dk} style={{ animation: `fadeInUp 0.4s ease ${idx * 0.05}s both` }}>
                  <div
                    onClick={() => {
                      const el = document.getElementById(`domain-body-${dk}`);
                      if (el) { el.style.display = el.style.display === 'none' ? 'block' : 'none'; }
                      const arrow = document.getElementById(`domain-arrow-${dk}`);
                      if (arrow) { arrow.textContent = arrow.textContent === '▾' ? '▸' : '▾'; }
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                      background: COLORS.surface, borderRadius: "12px 12px 0 0", cursor: "pointer",
                      border: `1px solid ${dv.color}25`, borderBottom: "none",
                      transition: "all 0.2s",
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{dv.icon}</span>
                    <span style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 600, flex: 1 }}>
                      {dv.label} {isWeak && <span style={{ color: COLORS.entropy }}>⚠</span>}
                    </span>
                    <div style={{ width: 80, height: 6, background: COLORS.surface3, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${score * 100}%`, background: dv.color, borderRadius: 3, transition: "width 0.3s" }} />
                    </div>
                    <span style={{ fontSize: 10, fontFamily: fonts.mono, color: dv.color, fontWeight: 700, minWidth: 32, textAlign: "right" }}>
                      {(score * 100).toFixed(0)}%
                    </span>
                    <span id={`domain-arrow-${dk}`} style={{ fontSize: 10, color: COLORS.textMuted }}>▾</span>
                  </div>
                  <div id={`domain-body-${dk}`} style={{
                    display: defaultOpen ? 'block' : 'none',
                    padding: "10px 14px 14px", background: COLORS.surface,
                    borderRadius: "0 0 12px 12px", border: `1px solid ${dv.color}25`, borderTop: `1px solid ${dv.color}12`,
                  }}>
                    {Object.entries(dv.dims).map(([dimKey, dimVal]) => (
                      <Slider key={dimKey} dimKey={dimKey} label={dimVal.label} value={state[dimKey]}
                        onChange={v => updateDim(dimKey, v)} color={dv.color} desc={dimVal.desc} tooltip={dimVal.tooltip} guideQuestion={dimVal.guideQuestion} />
                    ))}
                  </div>
                </div>
              );
            });
          })()}
          <Card title={t('estado.integralBalance')} icon="âš–" accent={COLORS.omega} style={{ gridColumn: "1 / -1" }} delay={0.3}>
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16, alignItems: "center" }}>
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke={COLORS.border} />
                  <PolarAngleAxis dataKey="domain" tick={{ fontSize: 9, fill: COLORS.textDim, fontFamily: fonts.mono }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8, fill: COLORS.textMuted }} />
                  <Radar name={t('estado.radarName')} dataKey="value" stroke={COLORS.logos} fill={COLORS.logos} fillOpacity={0.15} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
              <div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={domainBarData} layout="vertical">
                    <CartesianGrid stroke={COLORS.border} strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} />
                    <YAxis type="category" dataKey="domain" tick={{ fontSize: 9, fill: COLORS.textDim, fontFamily: fonts.mono }} width={80} stroke={COLORS.border} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                      {domainBarData.map((d, i) => <Cell key={i} fill={d.fill} fillOpacity={0.7} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ MOTOR LOGOS TAB ═══ */}
      {activeTab === "motor" && (subscription.isPremium || subscription.loading) && (
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16, maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <TabAIGuide panel="motor" accent="#C084FC" autoExpand={!initialSeenTabs.includes('motor')} data={{ lambda: Lambda, viability: V, entropy: S, coherence: C, freeEnergy: F, trajectory: Q, omega: Omega }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          </div>
          {/* Λ as Central Attractor */}
          <Card title={t('motor.centralAttractor')} icon="✦" accent={COLORS.logos} style={{ gridColumn: "1 / -1" }}>
            <div onClick={() => setDetailPanel({ id: "lambda", label: t('motor.lambdaPanel'), color: COLORS.logos, value: Lambda, data: lambdaResult })} style={{ textAlign: "center", padding: 16, background: `linear-gradient(135deg, ${COLORS.surface2}, ${COLORS.logos}08)`, borderRadius: 12, animation: "breathe 3s ease infinite", cursor: "pointer", transition: "all 0.3s", border: "1px solid transparent" }} onMouseEnter={(e) => { e.currentTarget.style.border = `1px solid ${COLORS.logos}40`; e.currentTarget.style.boxShadow = `0 0 30px ${COLORS.logos}20`; }} onMouseLeave={(e) => { e.currentTarget.style.border = "1px solid transparent"; e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ fontSize: 48, fontWeight: 700, fontFamily: fonts.mono, color: COLORS.logos, textShadow: `0 0 40px ${COLORS.logos}50` }}>
                Λ = {Lambda.toFixed(4)}
              </div>
              <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.logos, marginTop: 4, opacity: 0.8, animation: "breathe 3s ease infinite" }}>{t('motor.clickBreakdown')}</div>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 8 }}>
                <InfoTooltip text={t('motor.lambdaTooltip')}>
                  {t('motor.consciousnessIsLogos')}
                </InfoTooltip>
              </div>
              <div style={{ fontSize: 9, fontFamily: fonts.mono, color: pi.lambdaDot >= 0 ? COLORS.viability : COLORS.entropy, marginTop: 6 }}>
                Λ̇ = {(pi.lambdaDot || 0) >= 0 ? "+" : ""}{((pi.lambdaDot || 0) * 100).toFixed(1)}% {(pi.lambdaDot || 0) >= 0 ? `↑ ${t('motor.convergingAPlus')}` : `↓ ${t('motor.riskAMinus')}`}
              </div>
            </div>
            {/* Λ Breakdown */}
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: mobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 8 }}>
              {Object.entries(lambdaResult.breakdown).map(([key, group]) => (
                <div key={key} style={{ padding: 10, background: COLORS.surface2, borderRadius: 8, border: `1px solid ${COLORS.logos}15` }}>
                  <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 600, marginBottom: 6 }}>
                    <InfoTooltip text={group.tooltip}>{group.label}</InfoTooltip>
                  </div>
                  {group.items.map((item, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 8, fontFamily: fonts.mono, padding: "2px 0", color: COLORS.textDim }}>
                      <span>{item.label} ({(item.weight * 100).toFixed(0)}%)</span>
                      <span style={{ color: COLORS.logos }}>{(item.value * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.logos, marginTop: 4, textAlign: "right", borderTop: `1px dotted ${COLORS.border}`, paddingTop: 3 }}>
                    = {group.total.toFixed(4)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
          
          {/* Derived Metrics Flow */}
          <Card title={t('motor.derivedMetrics')} icon="⟐" accent={COLORS.coherence}>
            <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginBottom: 12, lineHeight: 1.8 }}>
              <InfoTooltip text={t('motor.derivedTooltip')}>
                {t('motor.allFlowFrom')}
              </InfoTooltip>
            </div>
            {[
              { label: "V = g(Λ) + ε", value: V, color: COLORS.viability, desc: `Λ boost: +${(viabilityResult.lambdaBoost * 100).toFixed(0)}%`, raw: viabilityResult.raw, panelId: "viability", panelLabel: t('motor.vPanel'), data: viabilityResult },
              { label: "S = (1-Λ)·k + ε", value: S, color: COLORS.entropy, desc: `Λ reduce: -${(entropyResult.lambdaEffect * 100).toFixed(0)}%`, raw: entropyResult.raw, panelId: "entropy", panelLabel: t('motor.sPanel'), data: entropyResult },
              { label: "C = h(Λ)", value: C, color: COLORS.coherence, desc: `Λ boost: +${(coherenceResult.lambdaBoost * 100).toFixed(0)}%`, raw: coherenceResult.rawCoherence, panelId: "coherence", panelLabel: t('motor.cPanel'), data: coherenceResult },
              { label: "F = D_KL(q||p_Λ)", value: F, color: COLORS.freeEnergy, desc: `Λ reduce: -${(freeEnergyResult.lambdaEffect * 100).toFixed(0)}%`, raw: freeEnergyResult.raw, panelId: "freeEnergy", panelLabel: t('motor.fPanel'), data: freeEnergyResult },
            ].map((m, i) => (
              <div key={i} onClick={() => setDetailPanel({ id: m.panelId, label: m.panelLabel, color: m.color, value: m.value, data: m.data })} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: 8, background: COLORS.surface2, borderRadius: 8, animation: `slideIn 0.3s ease ${i * 0.1}s both`, cursor: "pointer", transition: "all 0.2s", border: "1px solid transparent" }} onMouseEnter={(e) => { e.currentTarget.style.background = `${m.color}15`; e.currentTarget.style.border = `1px solid ${m.color}30`; }} onMouseLeave={(e) => { e.currentTarget.style.background = COLORS.surface2; e.currentTarget.style.border = "1px solid transparent"; }}>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: m.color, minWidth: 120 }}>{m.label}</div>
                <div style={{ flex: 1, height: 6, background: COLORS.surface3, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(m.value, 1) * 100}%`, height: "100%", background: m.color, borderRadius: 3, transition: "width 0.5s ease" }} />
                </div>
                <div style={{ fontSize: 11, fontFamily: fonts.mono, color: m.color, fontWeight: 700, minWidth: 50, textAlign: "right" }}>{m.value.toFixed(3)}</div>
                <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textMuted, minWidth: 80 }}>{m.desc}</div>
                <div style={{ fontSize: 8, fontFamily: fonts.mono, color: m.color, opacity: 0.7 }}>▶</div>
              </div>
            ))}
          </Card>
          
          {/* Attractor Visualization */}
          <Card title={t('motor.attractorsTitle')} icon="∞" accent={Lambda > LAMBDA_STAR ? COLORS.viability : COLORS.entropy}>
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div style={{ padding: 12, background: `${COLORS.viability}08`, borderRadius: 10, border: `1px solid ${COLORS.viability}25`, textAlign: "center" }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>☀</div>
                <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.viability, fontWeight: 700 }}>{t('motor.aPlusLogos')}</div>
                <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 4 }}>Λ̇ ≥ 0 · V↑ · S↓</div>
                <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 4 }}>
                  <InfoTooltip text={t('motor.aPlusTooltip')}>
                    {t('motor.flourishBasin')}
                  </InfoTooltip>
                </div>
                <div style={{ marginTop: 8, fontSize: 14, fontFamily: fonts.mono, color: COLORS.viability, fontWeight: 700 }}>
                  {Lambda >= LAMBDA_STAR ? t('motor.youAreHere') : t('motor.toReach', { pct: ((LAMBDA_STAR - Lambda) * 100).toFixed(0) })}
                </div>
              </div>
              <div style={{ padding: 12, background: `${COLORS.negAttractor}08`, borderRadius: 10, border: `1px solid ${COLORS.negAttractor}25`, textAlign: "center" }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>🕳</div>
                <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.negAttractor, fontWeight: 700 }}>{t('motor.aMinusEntropy')}</div>
                <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 4 }}>Λ̇ {"<"} 0 · S̈ {">"} 0</div>
                <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 4 }}>
                  <InfoTooltip text={t('motor.aMinusTooltip')}>
                    {t('motor.collapseBasin')}
                  </InfoTooltip>
                </div>
                <div style={{ marginTop: 8, fontSize: 14, fontFamily: fonts.mono, color: Lambda < 0.20 ? COLORS.negAttractor : COLORS.viability, fontWeight: 700 }}>
                  {Lambda < 0.20 ? t('motor.highRisk') : t('motor.farAway')}
                </div>
              </div>
            </div>
          </Card>
          
          {/* \u03A9 Status Display */}
          <Card title={t('motor.omegaTitle')} icon={"\u25C8"} accent={COLORS.omega}>
            <div onClick={() => setDetailPanel({ id: "omega", label: t('motor.omegaPanel'), color: COLORS.omega, value: Omega, data: omegaResult })} style={{ textAlign: "center", padding: 16, cursor: "pointer", transition: "all 0.3s", borderRadius: 10, border: "1px solid transparent" }} onMouseEnter={(e) => { e.currentTarget.style.background = `${COLORS.omega}08`; e.currentTarget.style.border = `1px solid ${COLORS.omega}30`; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.border = "1px solid transparent"; }}>
              <div style={{
                fontSize: 48, fontWeight: 200, fontFamily: fonts.display, color: COLORS.omega,
                textShadow: `0 0 30px ${COLORS.omega}30`, animation: "countUp 0.5s ease both",
              }}>
                {Omega.toFixed(3)}
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6 }}>
                {t('motor.omegaFormula')}
              </div>
              <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.omega, marginTop: 4, opacity: 0.7 }}>{t('motor.clickDetailed')}</div>
              <div style={{ marginTop: 16, padding: 12, background: `${Omega > 0.65 ? COLORS.viability : Omega > 0.45 ? COLORS.omega : COLORS.entropy}08`, borderRadius: 10, border: `1px solid ${Omega > 0.65 ? COLORS.viability : Omega > 0.45 ? COLORS.omega : COLORS.entropy}20` }}>
                <div style={{ fontSize: 12, fontFamily: fonts.mono, fontWeight: 600, color: Omega > 0.65 ? COLORS.viability : Omega > 0.45 ? COLORS.omega : COLORS.entropy }}>
                  {Omega > 0.75 ? t('motor.flourishing') : Omega > 0.65 ? t('motor.activeConsc') : Omega > 0.50 ? t('motor.transition') : Omega > 0.35 ? t('motor.alert') : t('motor.emergency')}
                </div>
                <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: fonts.mono, marginTop: 4 }}>
                  {Omega > 0.75 ? t('motor.flourishingDesc') :
                   Omega > 0.65 ? t('motor.activeDesc') :
                   Omega > 0.50 ? t('motor.transitionDesc') :
                   Omega > 0.35 ? t('motor.alertDesc') :
                   t('motor.emergencyDesc')}
                </div>
              </div>
            </div>
          </Card>
          
          {/* History Chart */}
          <Card title={t('motor.temporalEvolution')} icon={"\u27F3"} accent={COLORS.accent} style={{ gridColumn: "1 / -1" }}>
            {history.length > 2 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={history.slice(-30)}>
                  <CartesianGrid stroke={COLORS.border} strokeDasharray="3 3" />
                  <XAxis dataKey="t" tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} />
                  <YAxis domain={[0, 1]} tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey={"\u03A9"} stroke={COLORS.omega} strokeWidth={2.5} dot={false} name={t('motor.legendOmega')} />
                  <Line type="monotone" dataKey="V" stroke={COLORS.viability} strokeWidth={1.5} dot={false} name={t('motor.legendV')} />
                  <Line type="monotone" dataKey="S" stroke={COLORS.entropy} strokeWidth={1.5} dot={false} name={t('motor.legendS')} />
                  <Line type="monotone" dataKey={"\u039B"} stroke={COLORS.logos} strokeWidth={1.5} dot={false} name={t('motor.legendLambda')} />
                  <Legend wrapperStyle={{ fontSize: 9, fontFamily: fonts.mono }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: "center", padding: 20, color: COLORS.textMuted, fontFamily: fonts.mono, fontSize: 10 }}>{t('motor.adjustDimensions')}</div>
            )}
          </Card>

        </div>
      )}

      {/* ═══ PREMIUM GATE — Show paywall for non-subscribers on premium tabs ═══ */}
      {!subscription.isPremium && !subscription.loading && PREMIUM_TABS.has(activeTab) && (
        <div style={{ maxWidth: 600, margin: "40px auto" }}>
          <PremiumLockBanner onUpgrade={() => setShowPaywall(true)} />
        </div>
      )}

      {/* ═══ EMERGENCE TAB — The Crown Jewel of LOGOS v5.0 ═══ */}
      {activeTab === "emergence" && (subscription.isPremium || subscription.loading) && (
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* ═══════════════════════════════════════════════════════════════
              KAIRA QUICK ACCESS — Conversational Check-in
              ═══════════════════════════════════════════════════════════ */}
          <div style={{
            background: "linear-gradient(135deg, rgba(168,85,247,0.08) 0%, rgba(99,102,241,0.04) 100%)",
            border: "1px solid rgba(168,85,247,0.15)", borderRadius: 16,
            padding: "20px", display: "flex", alignItems: "center", gap: 16,
          }}>
            <div style={{ fontSize: 48, flexShrink: 0 }}>💬</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 9, letterSpacing: "0.15em", color: "#A855F7", marginBottom: 4 }}>
                KAIRA — QUICK CHECK-IN
              </div>
              <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: COLORS.text, margin: "4px 0 8px" }}>
                {i18n.language === 'es' ? 'Actualiza tus dimensiones conversando' : 'Update your dimensions by chatting'}
              </h3>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: COLORS.textMuted, margin: 0, lineHeight: 1.5 }}>
                {i18n.language === 'es' 
                  ? 'Conversa naturalmente con KAIRA para actualizar tus 28 dimensiones. Extracción automática con IA.'
                  : 'Chat naturally with KAIRA to update your 28 dimensions. Automatic AI extraction.'}
              </p>
            </div>
            <button
              onClick={() => setActiveTab('kaira')}
              style={{
                padding: "12px 24px",
                background: "linear-gradient(135deg, #a855f7, #6366f1)",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
                fontFamily: fonts.sans,
                boxShadow: "0 4px 16px rgba(168, 85, 247, 0.25)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(168, 85, 247, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(168, 85, 247, 0.25)';
              }}
            >
              {i18n.language === 'es' ? 'Abrir KAIRA' : 'Open KAIRA'}
            </button>
          </div>

          {/* ERS HERO GAUGE */}
          <div style={{
            background: "linear-gradient(135deg, rgba(192,132,252,0.10) 0%, rgba(6,182,212,0.06) 50%, rgba(16,185,129,0.06) 100%)",
            border: "1px solid rgba(192,132,252,0.20)", borderRadius: 20,
            padding: "32px 24px", textAlign: "center", position: "relative", overflow: "hidden",
          }}>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
              background: `conic-gradient(from 180deg, ${ers > 0.5 ? '#10B981' : '#EF4444'}20, transparent 60%)`,
              opacity: 0.3,
            }} />
            <div style={{ position: "relative", zIndex: 1 }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.2em", color: "#C084FC", marginBottom: 8 }}>
                EMERGENCE READINESS SCORE
              </div>
              <div style={{
                width: 160, height: 160, margin: "0 auto 16px", borderRadius: "50%",
                background: `conic-gradient(${ers > 0.7 ? '#10B981' : ers > 0.5 ? '#F5C542' : ers > 0.3 ? '#F97316' : '#EF4444'} ${(ers || 0) * 360}deg, rgba(255,255,255,0.05) 0deg)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 0 40px ${ers > 0.5 ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
              }}>
                <div style={{
                  width: 130, height: 130, borderRadius: "50%", background: "#0D0B1A",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                }}>
                  <span style={{ fontFamily: fonts.mono, fontSize: 36, fontWeight: 700, color: ers > 0.5 ? '#10B981' : '#F97316' }}>
                    {(ers || 0).toFixed(3)}
                  </span>
                  <span style={{ fontFamily: fonts.mono, fontSize: 10, color: "#8B85A0", letterSpacing: "0.1em" }}>
                    {ers > 0.7 ? "ALTA PREPARACIÓN" : ers > 0.5 ? "FAVORABLE" : ers > 0.3 ? "EN CONSTRUCCIÓN" : "SEÑAL DÉBIL"}
                  </span>
                </div>
              </div>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#8B85A0", margin: "0 auto", maxWidth: 500, lineHeight: 1.6 }}>
                Tu probabilidad de experimentar fenómenos emergentes — insights, sincronicidades, flujo, conexiones profundas — basada en tu alineación actual con el Logos.
              </p>
            </div>
          </div>

          {/* CRITICALITY ALERT — prominent when active */}
          <CriticalityAlertBanner alert={criticalityAlert} />

          {/* EMERGENCE SWARM — Langevin Dynamics: 28 particles = 28 dimensions */}
          <PanelWithAI
            title="Auto-Organización (Langevin)" icon="🌊" accent="#A855F7"
            panel="swarm"
            data={{ lambda: Lambda, entropy: S, ers, emergenceIndex: emergenceIndex?.value, phase: Lambda > 0.65 ? 'ordered' : Lambda > 0.35 ? 'fluid' : 'gas', strongestDomain: multiLambda?.strongest, weakestDomain: multiLambda?.weakest, coherence: C }}
            aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)}
          >
            <EmergenceSwarmAnimation
              state={state}
              prevState={prevState}
              lambda={Lambda || 0.5}
              entropy={S || 0.5}
              width={380}
              height={380}
            />
          </PanelWithAI>

          {/* CAUSALITY CASCADE — The full pipeline visualization */}
          <EmergenceCascade
            domains={watson.domainScores}
            lambda={Lambda} viability={V} entropy={S} coherence={C}
            freeEnergy={F} omega={Omega} trajectory={Q}
            ers={ers} emergenceIndex={emergenceIndex?.value || 0.5}
            onDomainClick={(domainKey) => {
              const domainInfo = DOMAINS[domainKey];
              if (!domainInfo) return;
              const dims = Object.entries(domainInfo.dims).map(([k, d]) => ({
                key: k, label: d.label, desc: d.desc, value: state[k] || 0.5,
                lambdaWeight: lambdaResult.breakdown
                  ? Object.values(lambdaResult.breakdown).reduce((found, grp) => found || grp.items?.find(i => i.key === k), null)?.weight || 0
                  : 0,
              }));
              const domainAvg = dims.reduce((s, d) => s + d.value, 0) / dims.length;
              const wTarget = watson.optimal?.[domainKey] ?? null;
              setDetailPanel({
                id: "domain", label: domainInfo.label, color: domainInfo.color, value: domainAvg,
                data: { domainKey, domainInfo, dimensions: dims, domainAvg, watsonTarget: wTarget },
              });
            }}
            onMetricClick={(metricId) => {
              const handlers = {
                lambda: () => setDetailPanel({ id: "lambda", label: "Λ Logos", color: COLORS.logos, value: Lambda, data: lambdaResult }),
                viability: () => setDetailPanel({ id: "viability", label: "V Viabilidad", color: COLORS.viability, value: V, data: viabilityResult }),
                entropy: () => setDetailPanel({ id: "entropy", label: "S Entropía", color: COLORS.entropy, value: S, data: entropyResult }),
                coherence: () => setDetailPanel({ id: "coherence", label: "C Coherencia", color: COLORS.coherence, value: C, data: coherenceResult }),
                freeEnergy: () => setDetailPanel({ id: "freeEnergy", label: "F Energía Libre", color: COLORS.freeEnergy, value: F, data: freeEnergyResult }),
                omega: () => setDetailPanel({ id: "omega", label: "Ω Consciencia", color: COLORS.omega, value: Omega, data: omegaResult }),
                trajectory: () => setDetailPanel({ id: "trajectory", label: "Q Trayectoria", color: COLORS.accent, value: Q, data: qResult }),
                ers: () => setShowEmergenceReporter(true),
              };
              handlers[metricId]?.();
            }}
          />

          {/* AI INTERPRETATION PANEL */}
          <EmergenceInterpretation metrics={{
            lambda: Lambda, viability: V, entropy: S, coherence: C, ers,
            emergenceIndex: emergenceIndex?.value,
            emergenceInterpretation: emergenceIndex?.interpretation,
            snr: snrResult?.snr, snrQuality: snrResult?.quality,
            nearestAttractor: (computeEnergyLandscape(Lambda || 0.5, S || 0.5)).nearestAttractor,
            criticalityActive: criticalityAlert?.active,
            criticalitySeverity: criticalityAlert?.severity,
            criticalityCulprit: criticalityAlert?.culpritDomain,
            multiScaleLambda: multiLambda?.byDomain,
            strongestDomain: multiLambda?.strongest,
            weakestDomain: multiLambda?.weakest,
            domainSpread: multiLambda?.spread,
          }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />

          {/* ENERGY LANDSCAPE + AI INTERPRETATION — side by side */}
          <PanelWithAI
            title="Paisaje Energético" icon="🗺" accent="#C084FC"
            panel="landscape"
            data={{ lambda: Lambda, entropy: S, nearestAttractor: (computeEnergyLandscape(Lambda || 0.5, S || 0.5)).nearestAttractor, attractorForce: (computeEnergyLandscape(Lambda || 0.5, S || 0.5)).attractorForce, ers, coherence: C }}
            aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)}
          >
            <EnergyLandscapeMap lambda={Lambda || 0.5} entropy={S || 0.5} />
          </PanelWithAI>

          {/* TWO COLUMN: SNR + E(x) */}
          <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16 }}>
            <SNRGauge lambda={Lambda || 0.5} entropy={S || 0.5} />
            <EmergenceIndexCard emergenceIndex={emergenceIndex} />
          </div>

          {/* MULTI-SCALE LAMBDA — full width */}
          <MultiScaleLambdaCard state={state} />

          {/* COHERENCE MATRIX + AI INTERPRETATION — side by side */}
          <PanelWithAI
            title="Coherencia Inter-Dominio" icon="🔗" accent="#06B6D4"
            panel="coherence"
            data={{ matrix: computeCoherenceMatrix(state).matrix, labels: computeCoherenceMatrix(state).domainLabels, strongestCoupling: computeCoherenceMatrix(state).strongestCoupling?.join('↔'), weakestCoupling: computeCoherenceMatrix(state).weakestCoupling?.join('↔'), lambda: Lambda, coherence: C }}
            aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)}
          >
            <CoherenceMatrixHeatmap state={state} />
          </PanelWithAI>

          {/* EMERGENCE GATES — 5 Puertas de Criticalidad (Manual PARTE IV) */}
          <PanelWithAI
            title="Emergence Gates" icon="⬡" accent="#A855F7"
            panel="emergenceGates"
            data={{
              lambda: Lambda, coherence: C, emergenceIndex: emergenceIndex?.value, entropy: S,
              lambdaDot: Lambda - prevLambda,
              ers,
              openCount: [Lambda >= 0.60, C >= 0.50, (emergenceIndex?.value || 0) >= 0.70, S <= 0.30, (Lambda - prevLambda) >= 0].filter(Boolean).length,
              allOpen: Lambda >= 0.60 && C >= 0.50 && (emergenceIndex?.value || 0) >= 0.70 && S <= 0.30 && (Lambda - prevLambda) >= 0,
            }}
            aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)}
          >
            <EmergenceGateDashboard
              lambda={Lambda || 0.5}
              coherence={C || 0.5}
              emergenceIndex={emergenceIndex?.value || 0.5}
              entropy={S || 0.5}
              lambdaDot={Lambda - prevLambda}
              ers={ers}
              t={t}
            />
          </PanelWithAI>

          {/* POSITIVE GEOMETRY — Consciousness Polytope */}
          <PositiveGeometryCard state={state} lambda={Lambda || 0.5} lambdaSignal={lambdaResult?.signal} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
        </div>
      )}

      {/* ═══ DECISION TAB ═══ */}
      {activeTab === "decision" && (
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16, maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <TabAIGuide panel="decision" accent={pi.color} autoExpand={!initialSeenTabs.includes('decision')} data={{ verdict: pi.verdict, lambda: Lambda, lambdaDot: Lambda - prevLambda, reason: pi.reason }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          </div>
          <Card title={t('decision.inhibitionPolicy')} icon="🛡" accent={pi.color} style={{ gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, padding: 16, background: `${pi.color}08`, borderRadius: 12 }}>
              <div style={{ fontSize: 48 }}>{pi.icon}</div>
              <div>
                <div style={{ fontSize: 28, fontWeight: 700, fontFamily: fonts.mono, color: pi.color, animation: piRaw.verdict === "ACTÚA" ? "pulse 2s ease infinite" : "none" }}>{pi.verdict}</div>
                <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 4, lineHeight: 1.6 }}>{pi.reason}</div>
              </div>
            </div>
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: mobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)", gap: 6 }}>
              {["PAUSA", "ESPERA", "RECONECTA", "MONITOREA", "ACTÚA"].map(v => (
                <div key={v} style={{ textAlign: "center", padding: 6, background: pi.verdict === t(`verdicts.${v}`, v) ? `${pi.color}15` : COLORS.surface2, borderRadius: 6, border: pi.verdict === t(`verdicts.${v}`, v) ? `1px solid ${pi.color}30` : `1px solid transparent` }}>
                  <div style={{ fontSize: 8, fontFamily: fonts.mono, color: pi.verdict === t(`verdicts.${v}`, v) ? pi.color : COLORS.textMuted }}>{t(`verdicts.${v}`, v)}</div>
                </div>
              ))}
            </div>
          </Card>
          
          <Card title={t('decision.metaPolicy')} icon="◎" accent={pi2.color}>
            <div style={{ padding: 12, background: `${pi2.color}08`, borderRadius: 8, border: `1px solid ${pi2.color}25` }}>
              <div style={{ fontSize: 14, fontWeight: 700, fontFamily: fonts.mono, color: pi2.color }}>{pi2.verdict}</div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 6, lineHeight: 1.6 }}>{pi2.reason}</div>
            </div>
          </Card>
          
          <Card title={t('decision.levinSignals')} icon="◇" accent={COLORS.freeEnergy}>
            {signals.length === 0 ? (
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.viability, textAlign: "center", padding: 16 }}>{t('decision.noPatterns')}</div>
            ) : signals.map((sig, i) => (
              <div key={i} style={{ padding: 10, background: `${sig.color}08`, borderRadius: 8, border: `1px solid ${sig.color}20`, marginBottom: 8, animation: `slideIn 0.3s ease ${i * 0.1}s both` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14 }}>{sig.icon}</span>
                  <span style={{ fontSize: 10, fontFamily: fonts.mono, color: sig.color, fontWeight: 700 }}>{sig.type}</span>
                </div>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 4, lineHeight: 1.5 }}>{sig.desc}</div>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.logos, marginTop: 4, fontStyle: "italic" }}>{t('misc.logosPrefix')} {sig.logosLink}</div>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: sig.color, marginTop: 4 }}>→ {sig.action}</div>
              </div>
            ))}
          </Card>
          
          {biases.length > 0 && (
            <Card title={t('decision.hoffmanBiases')} icon="⬡" accent={COLORS.gold} style={{ gridColumn: "1 / -1" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 8 }}>
                {biases.map((b, i) => (
                  <div key={i} style={{ padding: 10, background: COLORS.surface2, borderRadius: 8, border: `1px solid ${COLORS.gold}15` }}>
                    <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.gold, fontWeight: 700 }}>{b.type}</div>
                    <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 4 }}>{b.desc}</div>
                    <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.logos, marginTop: 4, fontStyle: "italic" }}>{b.logosLink}</div>
                    <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.viability, marginTop: 4 }}>→ {b.correction}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Decision Framework Guide */}
          <Card title={t('decision.decisionFramework')} icon={"⟷"} accent={COLORS.accent} style={{ gridColumn: "1 / -1" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
              <div style={{ padding: 14, background: COLORS.surface2, borderRadius: 10, animation: "slideIn 0.3s ease 0s both" }}>
                <div style={{ fontSize: 10, fontWeight: 700, fontFamily: fonts.mono, color: COLORS.entropy, marginBottom: 6 }}>{t('decision.pauseTitle')}</div>
                <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.5 }}>
                  {t('decision.pauseDesc')}
                </div>
              </div>
              <div style={{ padding: 14, background: COLORS.surface2, borderRadius: 10, animation: "slideIn 0.3s ease 0.05s both" }}>
                <div style={{ fontSize: 10, fontWeight: 700, fontFamily: fonts.mono, color: COLORS.omega, marginBottom: 6 }}>{t('decision.waitTitle')}</div>
                <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.5 }}>
                  {t('decision.waitDesc')}
                </div>
              </div>
              <div style={{ padding: 14, background: COLORS.surface2, borderRadius: 10, animation: "slideIn 0.3s ease 0.1s both" }}>
                <div style={{ fontSize: 10, fontWeight: 700, fontFamily: fonts.mono, color: COLORS.logos, marginBottom: 6 }}>{t('decision.reconnectTitle')}</div>
                <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.5 }}>
                  {t('decision.reconnectDesc')}
                </div>
              </div>
              <div style={{ padding: 14, background: COLORS.surface2, borderRadius: 10, animation: "slideIn 0.3s ease 0.15s both" }}>
                <div style={{ fontSize: 10, fontWeight: 700, fontFamily: fonts.mono, color: COLORS.coherence, marginBottom: 6 }}>{t('decision.monitorTitle')}</div>
                <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.5 }}>
                  {t('decision.monitorDesc')}
                </div>
              </div>
              <div style={{ padding: 14, background: COLORS.surface2, borderRadius: 10, animation: "slideIn 0.3s ease 0.2s both" }}>
                <div style={{ fontSize: 10, fontWeight: 700, fontFamily: fonts.mono, color: COLORS.viability, marginBottom: 6 }}>{t('decision.actTitle')}</div>
                <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.5 }}>
                  {t('decision.actDesc')}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ GUIA TAB ═══ */}
            {activeTab === "guia" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <TabAIGuide panel="guia" accent="#F59E0B" autoExpand={!initialSeenTabs.includes('guia')} data={{ lambda: Lambda, weakest: Object.entries(watson.domainScores).sort((a,b)=>a[1]-b[1])[0]?.[0], signals: signals.map(s=>s.name).join(', ') || 'ninguna' }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          </div>
          {/* Morning Protocol */}
          <Card title={t('guide.morningProtocol')} icon={"☀"} accent={COLORS.omega}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                ...(() => { const m = t('guide.morning', { returnObjects: true }); return Array.isArray(m) ? [
                  { step: 1, icon: "🌅", ...m[0] },
                  { step: 2, icon: "📊", ...m[1] },
                  { step: 3, icon: "🧘", ...m[2] },
                  { step: 4, icon: "📝", ...m[3] },
                  { step: 5, icon: "💪", ...m[4] },
                ] : []; })(),
              ].map(({ step, icon, title, text }) => (
                <div key={step} style={{ display: "flex", gap: 12, padding: 10, background: COLORS.surface2, borderRadius: 8, animation: `slideIn 0.3s ease ${step * 0.05}s both` }}>
                  <div style={{ fontSize: 20, minWidth: 28, textAlign: "center" }}>{icon}</div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, fontFamily: fonts.mono, color: COLORS.omega }}>{step}. {title}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.5, marginTop: 2 }}>{text}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Recommendations */}
          <Card title={t('guide.personalizedRecs')} icon={"💡"} accent={COLORS.viability}>
            <div style={{ marginBottom: 12, padding: 10, background: `${COLORS.logos}08`, borderRadius: 8 }}>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 600 }}>{t('guide.logosStateToday')}</div>
              <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 4, lineHeight: 1.6 }}>
                {"Λ"} = {Lambda.toFixed(3)} {"·"} {Lambda >= 0.60 ? t('guide.channelOpen') : Lambda >= 0.40 ? t('guide.channelPartial') : t('guide.channelWeak')}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {recommendations.map((rec, i) => (
                <div key={i} style={{
                  padding: 12, borderRadius: 10, background: COLORS.surface2,
                  borderLeft: `3px solid ${rec.type === "URGENTE" ? COLORS.entropy : rec.type === "LOGOS" ? COLORS.logos : rec.type === "FORTALECER" ? COLORS.omega : COLORS.viability}40`,
                  animation: `slideIn 0.3s ease ${i * 0.1}s both`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 14 }}>{rec.icon}</span>
                    <span style={{ fontSize: 9, fontFamily: fonts.mono, color: rec.type === "URGENTE" ? COLORS.entropy : COLORS.accent, letterSpacing: 1 }}>{t(`recTypes.${rec.type}`, rec.type)}</span>
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.6 }}>{rec.text}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Evening Protocol */}
          <Card title={t('guide.eveningProtocol')} icon={"🌙"} accent={COLORS.logos}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                ...(() => { const e = t('guide.evening', { returnObjects: true }); return Array.isArray(e) ? [
                  { icon: "📊", ...e[0] },
                  { icon: "📝", ...e[1] },
                  { icon: "📑", ...e[2] },
                  { icon: "🙏", ...e[3] },
                  { icon: "😴", ...e[4] },
                ] : []; })(),
              ].map(({ icon, title, text }, i) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: 10, background: COLORS.surface2, borderRadius: 8, animation: `slideIn 0.3s ease ${i * 0.05}s both` }}>
                  <div style={{ fontSize: 20, minWidth: 28, textAlign: "center" }}>{icon}</div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, fontFamily: fonts.mono, color: COLORS.logos }}>{title}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.5, marginTop: 2 }}>{text}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Quick Actions by State */}
          <Card title={t('guide.actionsByState')} icon={"⚡"} accent={pi.color}>
            <div style={{ padding: 14, background: `${pi.color}08`, border: `1px solid ${pi.color}20`, borderRadius: 10, marginBottom: 12, animation: "fadeInUp 0.4s ease both" }}>
              <div style={{ fontSize: 11, fontWeight: 700, fontFamily: fonts.mono, color: pi.color, marginBottom: 4 }}>
                {t('guide.currentState', { verdict: pi.verdict })}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.5 }}>{pi.reason}</div>
            </div>
            <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, lineHeight: 1.8 }}>
              {piRaw.verdict === "ACTÚA" && t('guide.actAdvice')}
              {piRaw.verdict === "MONITOREA" && t('guide.monitorAdvice')}
              {piRaw.verdict === "ESPERA" && t('guide.waitAdvice')}
              {piRaw.verdict === "PAUSA" && t('guide.pauseAdvice')}
              {piRaw.verdict === "RECONECTA" && t('guide.reconnectAdvice')}
            </div>
          </Card>
        </div>
      )}

      {/* ═══ TRAJECTORY TAB ═══ */}
      {activeTab === "trayectoria" && (subscription.isPremium || subscription.loading) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, maxWidth: 900, margin: "0 auto" }}>
          <TabAIGuide panel="trayectoria" accent="#818CF8" autoExpand={!initialSeenTabs.includes('trayectoria')} data={{ lambda: Lambda, trajectory: Q, trend: Lambda > prevLambda ? 'subiendo' : Lambda < prevLambda ? 'bajando' : 'estable' }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          <Card title={t('trajectory.deterministicProjection')} icon="⟐" accent={COLORS.accent}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trajectory}>
                <defs>
                  <linearGradient id="gradL2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.logos} stopOpacity={0.2} /><stop offset="95%" stopColor={COLORS.logos} stopOpacity={0} /></linearGradient>
                  <linearGradient id="gradV2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.viability} stopOpacity={0.15} /><stop offset="95%" stopColor={COLORS.viability} stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke={COLORS.border} strokeDasharray="3 3" />
                <XAxis dataKey="t" tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} />
                <YAxis domain={[0, 1]} tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={LAMBDA_STAR} stroke={COLORS.logos} strokeDasharray="5 5" label={{ value: "Λ*", fontSize: 8, fill: COLORS.logos }} />
                <ReferenceLine y={V_STAR} stroke={COLORS.viability} strokeDasharray="5 5" label={{ value: "V*", fontSize: 8, fill: COLORS.viability }} />
                <Area type="monotone" dataKey="Λ" stroke={COLORS.logos} fill="url(#gradL2)" strokeWidth={2.5} name={t('motor.legendLambda')} />
                <Area type="monotone" dataKey="V" stroke={COLORS.viability} fill="url(#gradV2)" strokeWidth={2} name={t('motor.legendV')} />
                <Line type="monotone" dataKey="S" stroke={COLORS.entropy} strokeWidth={1.5} dot={false} name={t('motor.legendS')} />
                <Line type="monotone" dataKey="Ω" stroke={COLORS.omega} strokeWidth={2.5} dot={false} name={t('motor.legendOmega')} />
                <Legend wrapperStyle={{ fontSize: 9, fontFamily: fonts.mono }} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
          
          <Card title={t('trajectory.phaseSpace')} icon="◎" accent={COLORS.logos}>
            <ResponsiveContainer width="100%" height={250}>
              <ScatterChart>
                <CartesianGrid stroke={COLORS.border} strokeDasharray="3 3" />
                <XAxis type="number" dataKey="Λ" domain={[0, 1]} name="Logos" tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} label={{ value: t('misc.phaseSpaceXLabel'), fontSize: 9, fill: COLORS.textDim, position: "bottom" }} />
                <YAxis type="number" dataKey="S" domain={[0, 1]} name={t('misc.entropy')} tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} label={{ value: t('misc.phaseSpaceYLabel'), fontSize: 9, fill: COLORS.textDim, angle: -90, position: "left" }} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine x={LAMBDA_STAR} stroke={COLORS.logos} strokeDasharray="5 5" />
                <ReferenceLine y={S_STAR} stroke={COLORS.entropy} strokeDasharray="5 5" />
                <Scatter data={trajectory} fill={COLORS.logos}>
                  {trajectory.map((_, i) => (
                    <Cell key={i} fill={COLORS.logos} fillOpacity={0.3 + (i / trajectory.length) * 0.7} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
            <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
              <div style={{ padding: 10, background: `${COLORS.viability}08`, borderRadius: 8, border: `1px solid ${COLORS.viability}20`, textAlign: "center" }}>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.viability, fontWeight: 700 }}>{t('trajectory.aPlusZone')}</div>
                <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.viability }}>Λ {">"} Λ* ∧ S {"<"} S*</div>
              </div>
              <div style={{ padding: 10, background: `${COLORS.negAttractor}08`, borderRadius: 8, border: `1px solid ${COLORS.negAttractor}20`, textAlign: "center" }}>
                <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.negAttractor, fontWeight: 700 }}>{t('trajectory.aMinusZone')}</div>
                <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.negAttractor }}>Λ {"<"} 0.20 ∧ S {">"} 0.50</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ MONTE CARLO TAB ═══ */}
      {activeTab === "montecarlo" && (subscription.isPremium || subscription.loading) && (
        <div style={{ maxWidth: 950, margin: "0 auto" }}>
          <TabAIGuide panel="montecarlo" accent="#EC4899" autoExpand={!initialSeenTabs.includes('montecarlo')} data={{ lambda: Lambda, n: mcN, sigma: mcSigma, result: mcResult }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          <Card title={t('mc.title')} icon="🎲" accent={COLORS.mc}>
            <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginBottom: 12, lineHeight: 1.7 }}>
              <InfoTooltip text={t('mc.mcTooltip')}>
                N = {mcN.toLocaleString()} {t('misc.trajectories')} · σ = {mcSigma} · 30 {t('misc.steps')} · {t('misc.noise')} W ~ N(0, σ²)
              </InfoTooltip>
            </div>
            
            {/* Controls */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>N:</div>
              {[1000, 5000, 10000].map(n => (
                <button key={n} onClick={() => setMcN(n)} style={{
                  padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer",
                  fontFamily: fonts.mono, fontSize: 9,
                  background: mcN === n ? `${COLORS.mc}25` : COLORS.surface2,
                  color: mcN === n ? COLORS.mc : COLORS.textMuted,
                  border: mcN === n ? `1px solid ${COLORS.mc}40` : `1px solid ${COLORS.border}`,
                }}>
                  {n.toLocaleString()}
                </button>
              ))}
              <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginLeft: 12 }}>σ:</div>
              {[0.02, 0.04, 0.08].map(s => (
                <button key={s} onClick={() => setMcSigma(s)} style={{
                  padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer",
                  fontFamily: fonts.mono, fontSize: 9,
                  background: mcSigma === s ? `${COLORS.mc}25` : COLORS.surface2,
                  color: mcSigma === s ? COLORS.mc : COLORS.textMuted,
                  border: mcSigma === s ? `1px solid ${COLORS.mc}40` : `1px solid ${COLORS.border}`,
                }}>
                  {s}
                </button>
              ))}
              <button onClick={handleRunMC} disabled={mcRunning} style={{
                padding: "6px 20px", borderRadius: 8, border: "none", cursor: mcRunning ? "wait" : "pointer",
                fontFamily: fonts.mono, fontSize: 10, fontWeight: 700,
                background: mcRunning ? COLORS.surface3 : `linear-gradient(135deg, ${COLORS.mc}, ${COLORS.logos})`,
                color: "#fff", marginLeft: "auto",
              }}>
                {mcRunning ? t('mc.running') : t('mc.run')}
              </button>
            </div>
            
            {/* Animation */}
            {mcRunning && <MonteCarloAnimationERS N={mcN} ers={ers} onComplete={() => {
              setMcAnimDone(true);
              setTimeout(() => {
                const result = runMonteCarlo(state, mcN, 30, mcSigma);
                setMcResult(result);
                setMcRunning(false);
              }, 50);
            }} />}
            
            {/* Results */}
            {mcResult && !mcRunning && (
              <div style={{ animation: "fadeInUp 0.5s ease" }}>
                {/* Summary Stats */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginBottom: 16 }}>
                  {[
                    { label: "P(Λ↑)", value: `${(mcResult.pLambdaUp * 100).toFixed(1)}%`, color: COLORS.logos, tip: t('mc.tipPLambdaUp') },
                    { label: "E[Λ_final]", value: mcResult.finalLamMean.toFixed(3), color: COLORS.logos, tip: t('mc.tipELambda') },
                    { label: "σ[Λ]", value: mcResult.finalLamStd.toFixed(3), color: COLORS.mc, tip: t('mc.tipSigma') },
                    { label: "P(A⁻)", value: `${(mcResult.pNegAttractor * 100).toFixed(1)}%`, color: COLORS.negAttractor, tip: t('mc.tipPNeg') },
                    { label: t('mc.labelVerdict'), value: mcResult.dominantVerdict, color: mcResult.verdictColors[mcResult.verdictNames.indexOf(mcResult.dominantVerdict)], tip: t('mc.tipVerdict', { p: (mcResult.dominantP * 100).toFixed(1) }) },
                    { label: t('mc.labelPNever'), value: `${(mcResult.pNeverActua * 100).toFixed(1)}%`, color: COLORS.entropy, tip: t('mc.tipPNever') },
                  ].map((s, i) => (
                    <div key={i} style={{ padding: 10, background: COLORS.surface2, borderRadius: 8, textAlign: "center", animation: `fadeInUp 0.3s ease ${i * 0.05}s both` }} title={s.tip}>
                      <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 1 }}>{s.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: fonts.mono, color: s.color, marginTop: 2 }}>{s.value}</div>
                    </div>
                  ))}
                </div>
                
                {/* Λ Confidence Bands Chart */}
                <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginBottom: 6 }}>
                  <InfoTooltip text={t('mc.confidenceTooltip')}>
                    {t('mc.confidenceBands', { n: mcResult.N.toLocaleString() })}
                  </InfoTooltip>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={mcResult.stats}>
                    <defs>
                      <linearGradient id="mcBand90" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.logos} stopOpacity={0.12} /><stop offset="100%" stopColor={COLORS.logos} stopOpacity={0.02} /></linearGradient>
                      <linearGradient id="mcBand50" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.logos} stopOpacity={0.25} /><stop offset="100%" stopColor={COLORS.logos} stopOpacity={0.05} /></linearGradient>
                    </defs>
                    <CartesianGrid stroke={COLORS.border} strokeDasharray="3 3" />
                    <XAxis dataKey="t" tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} label={{ value: t('mc.period'), fontSize: 8, fill: COLORS.textMuted, position: "bottom" }} />
                    <YAxis domain={[0, 1]} tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={LAMBDA_STAR} stroke={COLORS.logos} strokeDasharray="5 5" label={{ value: "Λ*", fontSize: 8, fill: COLORS.logos }} />
                    <Area type="monotone" dataKey="Λ_p95" stroke="none" fill="url(#mcBand90)" name="P₉₅" />
                    <Area type="monotone" dataKey="Λ_p5" stroke="none" fill={COLORS.bg} name="P₅" />
                    <Area type="monotone" dataKey="Λ_p75" stroke="none" fill="url(#mcBand50)" name="P₇₅" />
                    <Area type="monotone" dataKey="Λ_p25" stroke="none" fill={COLORS.bg} name="P₂₅" />
                    <Line type="monotone" dataKey="Λ_p50" stroke={COLORS.logos} strokeWidth={2.5} dot={false} name={t('motor.legendLambdaMedian')} />
                    <Line type="monotone" dataKey="Λ_mean" stroke={COLORS.mc} strokeWidth={1.5} dot={false} strokeDasharray="4 4" name={t('motor.legendLambdaMean')} />
                    <Line type="monotone" dataKey="V_p50" stroke={COLORS.viability} strokeWidth={1} dot={false} name={t('motor.legendVMedian')} />
                    <Line type="monotone" dataKey="S_p50" stroke={COLORS.entropy} strokeWidth={1} dot={false} name={t('motor.legendSMedian')} />
                    <Legend wrapperStyle={{ fontSize: 8, fontFamily: fonts.mono }} />
                  </AreaChart>
                </ResponsiveContainer>
                
                {/* Verdict Distribution Chart */}
                <div style={{ marginTop: 16, fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginBottom: 6 }}>
                  <InfoTooltip text={t('mc.verdictDistTooltip')}>
                    {t('mc.verdictDistribution')}
                  </InfoTooltip>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={mcResult.stats} stackOffset="expand">
                    <CartesianGrid stroke={COLORS.border} strokeDasharray="3 3" />
                    <XAxis dataKey="t" tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} />
                    <YAxis tickFormatter={v => `${(v * 100).toFixed(0)}%`} tick={{ fontSize: 8, fill: COLORS.textMuted }} stroke={COLORS.border} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="pActua" stackId="1" stroke={COLORS.viability} fill={COLORS.viability} fillOpacity={0.6} name={t('verdicts.ACT\u00daA', 'ACT\u00daA')} />
                    <Area type="monotone" dataKey="pMonitorea" stackId="1" stroke={COLORS.coherence} fill={COLORS.coherence} fillOpacity={0.5} name={t('verdicts.MONITOREA', 'MONITOREA')} />
                    <Area type="monotone" dataKey="pReconecta" stackId="1" stroke={COLORS.logos} fill={COLORS.logos} fillOpacity={0.4} name={t('verdicts.RECONECTA', 'RECONECTA')} />
                    <Area type="monotone" dataKey="pEspera" stackId="1" stroke={COLORS.gold} fill={COLORS.gold} fillOpacity={0.4} name={t('verdicts.ESPERA', 'ESPERA')} />
                    <Area type="monotone" dataKey="pPausa" stackId="1" stroke={COLORS.entropy} fill={COLORS.entropy} fillOpacity={0.4} name={t('verdicts.PAUSA', 'PAUSA')} />
                    <Legend wrapperStyle={{ fontSize: 8, fontFamily: fonts.mono }} />
                  </AreaChart>
                </ResponsiveContainer>
                
                {/* Wilson CIs on final verdicts */}
                <div style={{ marginTop: 16, fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, marginBottom: 6 }}>
                  {t('mc.wilsonCI')}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)", gap: 6 }}>
                  {mcResult.verdictNames.map((name, i) => {
                    const ci = mcResult.verdictCI[name];
                    if (!ci) return null;
                    return (
                      <div key={name} style={{ padding: 8, background: COLORS.surface2, borderRadius: 8, textAlign: "center", border: name === mcResult.dominantVerdict ? `1px solid ${mcResult.verdictColors[i]}40` : `1px solid transparent` }}>
                        <div style={{ fontSize: 9, fontFamily: fonts.mono, color: mcResult.verdictColors[i], fontWeight: 700 }}>{name}</div>
                        <div style={{ fontSize: 14, fontFamily: fonts.mono, color: mcResult.verdictColors[i], fontWeight: 700, marginTop: 2 }}>
                          {(ci.p * 100).toFixed(1)}%
                        </div>
                        <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>
                          [{(ci.lower * 100).toFixed(1)}%, {(ci.upper * 100).toFixed(1)}%]
                        </div>
                        <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                          n={ci.count?.toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Statistical Significance */}
                <div style={{ marginTop: 12, padding: 10, background: `${mcResult.dominantP >= 0.70 ? COLORS.viability : COLORS.gold}08`, borderRadius: 8, border: `1px solid ${mcResult.dominantP >= 0.70 ? COLORS.viability : COLORS.gold}20` }}>
                  <div style={{ fontSize: 9, fontFamily: fonts.mono, color: mcResult.dominantP >= 0.70 ? COLORS.viability : COLORS.gold, fontWeight: 700 }}>
                    {mcResult.dominantP >= 0.70 ? t('mc.robust') : mcResult.dominantP >= 0.55 ? t('mc.moderate') : t('mc.fragile')}
                  </div>
                  <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 4, lineHeight: 1.5 }}>
                    {t('mc.dominantVerdict', { verdict: mcResult.dominantVerdict, p: (mcResult.dominantP * 100).toFixed(1) })}
                    {mcResult.dominantP >= 0.70 ? t('mc.robustNote') : t('mc.uncertainNote')}
                    {mcResult.pNegAttractor > 0.05 ? t('mc.negAttractorRisk', { p: (mcResult.pNegAttractor * 100).toFixed(1) }) : ""}
                  </div>
                </div>
              </div>
            )}
            
            {!mcResult && !mcRunning && (
              <div style={{ textAlign: "center", padding: 30, color: COLORS.textMuted, fontSize: 10, fontFamily: fonts.mono }}>
                {t('mc.pressRun', { n: mcN.toLocaleString() })}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ THEORY TAB ═══ */}
      {activeTab === "teoria" && (subscription.isPremium || subscription.loading) && (
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "220px 1fr", gap: 16, maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <TabAIGuide panel="teoria" accent="#06B6D4" autoExpand={!initialSeenTabs.includes('teoria')} data={{ section: theorySection, lambda: Lambda }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          </div>
          <Card style={{ position: "sticky", top: 20, alignSelf: "start" }}>
            <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.logos, letterSpacing: 1.5, marginBottom: 12, textTransform: "uppercase" }}>{t('theory.frameworkTitle')}</div>
            {THEORY.map(sec => (
              <button key={sec.id} onClick={() => setTheorySection(sec.id)} style={{
                display: "flex", alignItems: "center", gap: 6, width: "100%",
                padding: "6px 8px", marginBottom: 2, borderRadius: 6,
                background: theorySection === sec.id ? `${COLORS.logos}15` : "transparent",
                border: theorySection === sec.id ? `1px solid ${COLORS.logos}25` : "1px solid transparent",
                color: theorySection === sec.id ? COLORS.logos : COLORS.textDim,
                fontFamily: fonts.mono, fontSize: 9, cursor: "pointer", textAlign: "left", transition: "all 0.2s",
              }}>
                <span style={{ fontSize: 12, width: 16 }}>{sec.icon}</span>{sec.title}
              </button>
            ))}
          </Card>
          <Card accent={COLORS.logos}>
            {(() => {
              const sec = THEORY.find(s => s.id === theorySection);
              if (!sec) return null;
              return (
                <div style={{ animation: "fadeInUp 0.3s ease" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                    <span style={{ fontSize: 24 }}>{sec.icon}</span>
                    <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: COLORS.text, fontFamily: fonts.display }}>{sec.title}</h2>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {sec.content.map((p, i) => (
                      <div key={i} style={{
                        fontSize: 11, lineHeight: 1.7, color: COLORS.textDim, fontFamily: fonts.sans,
                        animation: `slideIn 0.3s ease ${i * 0.05}s both`,
                        ...(/^[A-ZÁÉÍÓÚÑÜ¿Λ][A-ZÁÉÍÓÚÑÜ0-9 ()⁺⁻—\-·_:]{3,}/.test(p.slice(0, 30))
                          ? { padding: "10px 14px", background: COLORS.surface2, borderRadius: 8, borderLeft: `3px solid ${COLORS.logos}40` }
                          : {}),
                        ...(p.startsWith("ECUACIÓN:") || p.startsWith("EQUATION:") ? { fontFamily: fonts.mono, fontSize: 10 } : {}),
                      }}>
                        {p}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </Card>
        </div>
      )}

      {/* ═══ ALIMENTO SAGRADO TAB ═══ */}
      {activeTab === "alimento" && (subscription.isPremium || subscription.loading) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <TabAIGuide panel="alimento" accent="#84CC16" autoExpand={!initialSeenTabs.includes('alimento')} data={{ lambda: Lambda, weakest: Object.entries(watson.domainScores).sort((a,b)=>a[1]-b[1])[0]?.[0], nourishment: state.nourishment, foodHarmony: state.food_harmony }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          <Card>
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 14, fontFamily: fonts.display, color: COLORS.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>⚘</span>
                <span>{t('alimento.title')}</span>
              </div>
              <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
                {i18n.language === 'en' ? alimentoRecs.generalAdvice : alimentoRecs.generalAdviceEs}
              </div>

              {alimentoRecs.deficientDimensions.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontFamily: fonts.display, color: COLORS.accent, marginBottom: 8, letterSpacing: 1, textTransform: "uppercase" }}>{t('alimento.deficientDimensions')}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {alimentoRecs.deficientDimensions.map((d, i) => (
                      <div key={i} style={{ background: `${COLORS.entropy}15`, border: `1px solid ${COLORS.entropy}30`, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontFamily: fonts.mono }}>
                        <span style={{ color: COLORS.entropy }}>{d.dimension}</span>
                        <span style={{ color: COLORS.textMuted, marginLeft: 6 }}>{(d.value * 100).toFixed(0)}%</span>
                        <span style={{ color: COLORS.textMuted, fontSize: 9, marginLeft: 4 }}>/ {(d.threshold * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {alimentoRecs.recommendedFoods.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontFamily: fonts.display, color: COLORS.viability, marginBottom: 8, letterSpacing: 1, textTransform: "uppercase" }}>{t('alimento.recommendedFoods')}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {alimentoRecs.recommendedFoods.map((rec, i) => (
                      <div key={i} style={{ background: `${COLORS.viability}08`, border: `1px solid ${COLORS.viability}20`, borderRadius: 10, padding: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontFamily: fonts.display, color: COLORS.text }}>{i18n.language === 'en' ? rec.food.name : rec.food.nameEs}</span>
                          <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.viability, background: `${COLORS.viability}15`, padding: "2px 8px", borderRadius: 20 }}>
                            +{(rec.predictedChange * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, lineHeight: 1.5 }}>
                          {i18n.language === 'en' ? rec.reason : rec.reasonEs}
                        </div>
                        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.accent, background: `${COLORS.accent}10`, padding: "2px 6px", borderRadius: 4 }}>
                            {rec.food.register}
                          </span>
                          <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.gold, background: `${COLORS.gold}10`, padding: "2px 6px", borderRadius: 4 }}>
                            {rec.food.tradition}
                          </span>
                          <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.logos, background: `${COLORS.logos}10`, padding: "2px 6px", borderRadius: 4 }}>
                            {rec.food.evidenceStrength}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div style={{ fontSize: 11, fontFamily: fonts.display, color: COLORS.entropy, marginBottom: 8, letterSpacing: 1, textTransform: "uppercase" }}>{t('alimento.avoidTitle')}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(i18n.language === 'en' ? alimentoRecs.avoidFoods : alimentoRecs.avoidFoodsEs).map((food, i) => (
                    <span key={i} style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.entropy, background: `${COLORS.entropy}10`, padding: "3px 8px", borderRadius: 6, border: `1px solid ${COLORS.entropy}20` }}>
                      ⚠ {food}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ SEGUIMIENTO TAB ═══ */}
      {activeTab === "seguimiento" && (subscription.isPremium || subscription.loading) && (
        <div>
          <TabAIGuide panel="seguimiento" accent="#F97316" autoExpand={!initialSeenTabs.includes('seguimiento')} data={{ lambda: Lambda }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          <SessionTracker
            currentMetrics={{ lambda: Lambda, omega: Omega, V, S, C, F, Q, verdict: pi.verdict }}
            currentDomains={domains}
          />
        </div>
      )}

      {/* ═══ PESOS TAB — Weight Configuration ═══ */}
      {activeTab === "pesos" && (() => {
        const metricSections = [
          {
            id: "lambda", label: "Λ Lambda — Alineación Logos", color: COLORS.logos,
            desc: "Pesos de las 12 dimensiones de señal (Λ_señal). Las 16 dims de canal (Φ) se calculan automáticamente.",
            keys: Object.keys(customWeights.weights.lambda),
            defaults: DEFAULT_WEIGHTS.lambda,
            current: customWeights.weights.lambda,
          },
          {
            id: "entropy", label: "S Entropía — Desorden Interno", color: COLORS.entropy,
            desc: "Pesos de las 12 dimensiones cuyo déficit genera entropía (ruido interno).",
            keys: Object.keys(customWeights.weights.entropy),
            defaults: DEFAULT_WEIGHTS.entropy,
            current: customWeights.weights.entropy,
          },
          {
            id: "viability", label: "V Viabilidad — Capacidad Funcional", color: COLORS.viability,
            desc: "Pesos de las 28 dimensiones que definen tu capacidad de operar en el mundo.",
            keys: Object.keys(customWeights.weights.viability),
            defaults: DEFAULT_WEIGHTS.viability,
            current: customWeights.weights.viability,
          },
        ];
        const dimLabel = (key) => {
          for (const d of Object.values(tDomains)) {
            if (d.dims[key]) return d.dims[key].label;
          }
          return key;
        };
        return (
          <div style={{ animation: "fadeInUp 0.4s ease" }}>
            <Card title={t('misc.weightsCardTitle')} accent={COLORS.accent}>
              {/* Toggle */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, padding: "10px 14px", background: customWeights.enabled ? `${COLORS.logos}12` : COLORS.surface2, borderRadius: 10, border: `1px solid ${customWeights.enabled ? COLORS.logos + "40" : COLORS.border}`, transition: "all 0.3s" }}>
                <div>
                  <div style={{ fontSize: 12, fontFamily: fonts.display, color: COLORS.text, fontWeight: 600 }}>
                    {customWeights.enabled ? t('misc.weightsCustomActive') : t('misc.weightsDefaultActive')}
                  </div>
                  <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>
                    {customWeights.enabled
                      ? t('misc.weightsCustomDesc')
                      : t('misc.weightsDefaultDesc')}
                  </div>
                </div>
                <button onClick={() => customWeights.setEnabled(!customWeights.enabled)} style={{
                  padding: "6px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1, transition: "all 0.3s",
                  background: customWeights.enabled ? COLORS.logos : COLORS.surface3,
                  color: customWeights.enabled ? "#fff" : COLORS.textMuted,
                }}>
                  {customWeights.enabled ? t('misc.activated') : t('misc.activate')}
                </button>
              </div>

              {/* ═══ BAYESIAN LEARNING ENGINE ═══ */}
              <div style={{ marginBottom: 16, padding: 14, background: `${COLORS.gold}08`, borderRadius: 12, border: `1px solid ${COLORS.gold}25` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, fontFamily: fonts.display, color: COLORS.gold, fontWeight: 600 }}>
                      {t('misc.bayesianTitle')}
                    </div>
                    <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>
                      {t('misc.bayesianDesc')}
                    </div>
                  </div>
                  <button
                    onClick={() => bayesian.runLearning(
                      customWeights.weights.lambda,
                      customWeights.weights.viability,
                      customWeights.weights.entropy
                    )}
                    disabled={bayesian.loading}
                    style={{
                      padding: "6px 14px", borderRadius: 8, border: "none", cursor: bayesian.loading ? "wait" : "pointer",
                      fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1, transition: "all 0.3s",
                      background: bayesian.loading ? COLORS.surface3 : `${COLORS.gold}30`,
                      color: bayesian.loading ? COLORS.textMuted : COLORS.gold,
                    }}>
                    {bayesian.loading ? t('misc.bayesianAnalyzing') : t('misc.bayesianAnalyze')}
                  </button>
                </div>

                {bayesian.error && (
                  <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.entropy, marginBottom: 8 }}>
                    ⚠ {bayesian.error}
                  </div>
                )}

                {bayesian.cbwaResult && (
                  <div>
                    {/* Status indicators */}
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                      <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                        Sesiones: <span style={{ color: COLORS.text, fontWeight: 600 }}>{bayesian.cbwaResult.sessionsUsed}</span>
                      </div>
                      <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                        Confianza: <span style={{
                          color: bayesian.cbwaResult.confidence > 0.6 ? COLORS.viability : bayesian.cbwaResult.confidence > 0.3 ? COLORS.gold : COLORS.entropy,
                          fontWeight: 600,
                        }}>{(bayesian.cbwaResult.confidence * 100).toFixed(0)}%</span>
                      </div>
                      <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                        Estado: <span style={{
                          color: bayesian.cbwaResult.convergenceStatus === 'converged' ? COLORS.viability
                            : bayesian.cbwaResult.convergenceStatus === 'converging' ? COLORS.gold
                            : bayesian.cbwaResult.convergenceStatus === 'learning' ? COLORS.accent
                            : COLORS.entropy,
                          fontWeight: 600,
                        }}>
                          {{
                            insufficient: '⏳ Insuficiente (< 10 sesiones)',
                            learning: '📊 Aprendiendo...',
                            converging: '🔄 Convergiendo',
                            converged: '✅ Convergido',
                          }[bayesian.cbwaResult.convergenceStatus]}
                        </span>
                      </div>
                      <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                        Δ Teoría: <span style={{ color: COLORS.accent, fontWeight: 600 }}>{bayesian.cbwaResult.deltaFromTheory.toFixed(3)}</span>
                      </div>
                    </div>

                    {/* Top attributions */}
                    {Object.keys(bayesian.cbwaResult.attributions).length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 4 }}>
                          Top contribuciones a Λ (aprendidas):
                        </div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {Object.entries(bayesian.cbwaResult.attributions)
                            .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
                            .slice(0, 6)
                            .map(([k, v]) => (
                              <span key={k} style={{
                                fontSize: 8, fontFamily: fonts.mono, padding: "2px 6px", borderRadius: 4,
                                background: v > 0 ? `${COLORS.viability}15` : `${COLORS.entropy}15`,
                                color: v > 0 ? COLORS.viability : COLORS.entropy,
                                border: `1px solid ${v > 0 ? COLORS.viability : COLORS.entropy}20`,
                              }}>
                                {dimLabel(k)} {v > 0 ? "↑" : "↓"}{Math.abs(v).toFixed(3)}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Apply button */}
                    {bayesian.cbwaResult.convergenceStatus !== 'insufficient' && (
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => {
                            const lw = bayesian.cbwaResult.learnedWeights;
                            for (const [k, v] of Object.entries(lw.lambda)) customWeights.updateWeight('lambda', k, v);
                            for (const [k, v] of Object.entries(lw.viability)) customWeights.updateWeight('viability', k, v);
                            for (const [k, v] of Object.entries(lw.entropy)) customWeights.updateWeight('entropy', k, v);
                            if (!customWeights.enabled) customWeights.setEnabled(true);
                          }}
                          style={{
                            padding: "6px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                            fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1,
                            background: `linear-gradient(135deg, ${COLORS.gold}40, ${COLORS.viability}30)`,
                            color: COLORS.text, fontWeight: 600,
                          }}>
                          {t('misc.applyLearnedWeights')}
                        </button>
                        <span style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, alignSelf: "center" }}>
                          {t('misc.applyLearnedDesc')}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Reset all button */}
              {customWeights.enabled && (
                <div style={{ textAlign: "right", marginBottom: 12 }}>
                  <button onClick={customWeights.resetAll} style={{
                    padding: "4px 12px", borderRadius: 6, border: `1px solid ${COLORS.entropy}40`, cursor: "pointer",
                    fontFamily: fonts.mono, fontSize: 9, color: COLORS.entropy, background: "transparent",
                  }}>
                    {t('misc.restoreWeights')}
                  </button>
                </div>
              )}

              {/* Metric Sections */}
              {metricSections.map(section => {
                const sum = section.keys.reduce((s, k) => s + (section.current[k] || 0), 0);
                const defaultSum = section.keys.reduce((s, k) => s + (section.defaults[k] || 0), 0);
                const sumDiff = Math.abs(sum - defaultSum);
                return (
                  <div key={section.id} style={{ marginBottom: 20, padding: 14, background: `${section.color}06`, borderRadius: 12, border: `1px solid ${section.color}20` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 12, fontFamily: fonts.display, color: section.color, fontWeight: 600 }}>{section.label}</div>
                        <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>{section.desc}</div>
                      </div>
                      {customWeights.enabled && (
                        <button onClick={() => customWeights.resetMetric(section.id)} style={{
                          padding: "3px 10px", borderRadius: 6, border: `1px solid ${section.color}30`, cursor: "pointer",
                          fontFamily: fonts.mono, fontSize: 8, color: section.color, background: "transparent",
                        }}>
                          ⟲ Reset
                        </button>
                      )}
                    </div>
                    {/* Sum indicator */}
                    <div style={{ display: "flex", gap: 12, marginBottom: 10, fontSize: 9, fontFamily: fonts.mono }}>
                      <span style={{ color: COLORS.textMuted }}>Σw = <span style={{ color: sumDiff > 0.005 ? COLORS.entropy : section.color, fontWeight: 600 }}>{sum.toFixed(3)}</span></span>
                      <span style={{ color: COLORS.textMuted }}>Teórico: {defaultSum.toFixed(3)}</span>
                      {sumDiff > 0.005 && <span style={{ color: COLORS.entropy }}>Δ {sumDiff > 0 ? "+" : ""}{(sum - defaultSum).toFixed(3)}</span>}
                    </div>
                    {/* Weight sliders */}
                    {section.keys.map(key => {
                      const val = section.current[key] || 0;
                      const def = section.defaults[key] || 0;
                      const diff = val - def;
                      const pct = val * 100;
                      return (
                        <div key={key} style={{ marginBottom: 6 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                            <span style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, minWidth: 120 }}>
                              {dimLabel(key)}
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9, fontFamily: fonts.mono }}>
                              {Math.abs(diff) > 0.001 && (
                                <span style={{ color: diff > 0 ? COLORS.viability : COLORS.entropy, fontSize: 8 }}>
                                  {diff > 0 ? "↑" : "↓"}{Math.abs(diff * 100).toFixed(1)}
                                </span>
                              )}
                              <span style={{ color: section.color, fontWeight: 600, minWidth: 36, textAlign: "right" }}>
                                {pct.toFixed(1)}%
                              </span>
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <input
                              type="range" min="0" max="30" step="0.1"
                              value={val * 100}
                              disabled={!customWeights.enabled}
                              onChange={e => customWeights.updateWeight(section.id, key, parseFloat(e.target.value) / 100)}
                              style={{
                                width: "100%", height: 4, appearance: "none",
                                background: `linear-gradient(to right, ${section.color}70 ${pct / 0.3}%, ${COLORS.surface3} ${pct / 0.3}%)`,
                                borderRadius: 2, outline: "none",
                                cursor: customWeights.enabled ? "pointer" : "not-allowed",
                                opacity: customWeights.enabled ? 1 : 0.5,
                                accentColor: section.color,
                              }}
                            />
                            {/* Theory marker */}
                            <span style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, minWidth: 28, textAlign: "right", opacity: 0.6 }}>
                              ({(def * 100).toFixed(1)})
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              {/* Info box */}
              <div style={{ padding: 12, background: `${COLORS.logos}08`, borderRadius: 10, border: `1px solid ${COLORS.logos}20`, fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, lineHeight: 1.6 }}>
                <div style={{ color: COLORS.logos, fontWeight: 600, marginBottom: 4, fontSize: 10 }}>¿Cómo funcionan los pesos?</div>
                <strong>Λ Lambda:</strong> Define qué dimensiones contribuyen más a tu alineación con el Logos. Fe (faith) tiene el peso más alto porque es la apertura del canal.<br />
                <strong>S Entropía:</strong> Define qué déficits generan más ruido interno. Falta de paz y claridad son los mayores generadores de entropía.<br />
                <strong>V Viabilidad:</strong> Define qué dimensiones contribuyen más a tu capacidad funcional. Los 28 parámetros participan.<br /><br />
                <span style={{ color: COLORS.gold }}>⚠ Los pesos teóricos están calibrados por el framework LOGOS basado en 5 científicos de clase mundial. Modifícalos solo si tienes una razón clara.</span>
              </div>
            </Card>
          </div>
        );
      })()}

      {/* ═══ APPLE WATCH / BIOMETRICS TAB ═══ */}
      {activeTab === "biometrics" && (subscription.isPremium || subscription.loading) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, maxWidth: 800, margin: "0 auto" }}>
          <TabAIGuide panel="biometrics" accent="#8b5cf6" autoExpand={!initialSeenTabs.includes('biometrics')} data={{ lambda: Lambda, connected: false }} aiConsent={aiConsentGiven} onRequestConsent={() => setShowAiConsent(true)} />
          {/* Connection Status */}
          <Card title="Apple Watch" icon="⌚" accent="#8b5cf6">
            {!healthKit.isNative ? (
              <div style={{ textAlign: "center", padding: 20 }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📱</div>
                <div style={{ fontSize: 12, fontFamily: fonts.mono, color: COLORS.text, marginBottom: 8 }}>Disponible en la app iOS</div>
                <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, lineHeight: 1.6 }}>
                  La integración con Apple Watch y HealthKit está disponible cuando usas LOGOS HUMANO desde la app nativa de iOS.
                  Los datos biométricos auto-pueblan tus dimensiones de consciencia y enriquecen las 7 capas del motor.
                </div>
              </div>
            ) : !healthKit.authorized ? (
              <div style={{ padding: 16 }}>
                {/* HealthKit Disclosure Header — Guideline 2.5.1 compliance */}
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⌚🔒</div>
                  <div style={{ fontSize: 13, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 600, marginBottom: 4 }}>{t('healthData.sectionTitle')}</div>
                  <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, lineHeight: 1.6 }}>
                    {t('healthData.description')}
                  </div>
                </div>

                {/* Data Categories */}
                <div style={{ marginBottom: 12, padding: 12, background: `${COLORS.accent}08`, borderRadius: 10, border: `1px solid ${COLORS.accent}20` }}>
                  <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.accent, fontWeight: 600, marginBottom: 8 }}>📋 {t('healthData.dataWeRead')}</div>
                  {['vitals', 'fitness', 'sleep', 'mental', 'cardiac', 'mobility', 'environment'].map(cat => (
                    <div key={cat} style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, lineHeight: 1.5, padding: "2px 0" }}>
                      • {t(`healthData.categories.${cat}`)}
                    </div>
                  ))}
                </div>

                {/* Write disclosure */}
                <div style={{ marginBottom: 12, padding: 10, background: `#10b98108`, borderRadius: 8, border: `1px solid #10b98120` }}>
                  <div style={{ fontSize: 10, fontFamily: fonts.mono, color: "#10b981", fontWeight: 600, marginBottom: 4 }}>✏️ {t('healthData.dataWeWrite')}</div>
                  <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('healthData.writeDescription')}</div>
                </div>

                {/* Purpose */}
                <div style={{ marginBottom: 12, padding: 12, background: `${COLORS.logos}08`, borderRadius: 10, border: `1px solid ${COLORS.logos}20` }}>
                  <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 600, marginBottom: 8 }}>🎯 {t('healthData.purpose')}</div>
                  {['autoPop', 'levin', 'hoffman', 'engine', 'policy'].map(item => (
                    <div key={item} style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, lineHeight: 1.5, padding: "2px 0" }}>
                      • {t(`healthData.purposeItems.${item}`)}
                    </div>
                  ))}
                </div>

                {/* Privacy */}
                <div style={{ marginBottom: 16, padding: 12, background: `${COLORS.gold}08`, borderRadius: 10, border: `1px solid ${COLORS.gold}20` }}>
                  <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.gold, fontWeight: 600, marginBottom: 8 }}>🛡 {t('healthData.privacy')}</div>
                  {['local', 'noShare', 'noServer', 'revoke', 'optional'].map(item => (
                    <div key={item} style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, lineHeight: 1.5, padding: "2px 0" }}>
                      ✓ {t(`healthData.privacyItems.${item}`)}
                    </div>
                  ))}
                </div>

                {/* Connect button */}
                <button onClick={() => healthKit.requestAuth().then(ok => ok && healthKit.syncBiometrics())} style={{
                  width: "100%", padding: "14px 28px", borderRadius: 10, border: "none", cursor: "pointer",
                  fontFamily: fonts.mono, fontSize: 12, fontWeight: 600,
                  background: "linear-gradient(135deg, #8b5cf6, #a855f7)", color: "#fff",
                  boxShadow: "0 4px 20px rgba(139,92,246,0.3)",
                }}>
                  {t('healthData.connectNow')}
                </button>

                {/* Error state */}
                {healthKit.error && (
                  <div style={{ marginTop: 10, padding: 10, background: "#ef444410", borderRadius: 8, border: "1px solid #ef444430", fontSize: 9, fontFamily: fonts.mono, color: "#ef4444", lineHeight: 1.5 }}>
                    {t('healthData.denied')}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981", boxShadow: "0 0 8px #10b98180" }} />
                    <span style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.text }}>Conectado</span>
                  </div>
                  <button onClick={() => healthKit.syncBiometrics()} disabled={healthKit.loading} style={{
                    padding: "6px 16px", borderRadius: 8, border: `1px solid ${COLORS.accent}40`, cursor: "pointer",
                    fontFamily: fonts.mono, fontSize: 9, color: COLORS.accent, background: "transparent",
                    opacity: healthKit.loading ? 0.5 : 1,
                  }}>
                    {healthKit.loading ? "Sincronizando..." : "⟲ Sincronizar"}
                  </button>
                </div>
                {healthKit.lastSync && (
                  <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                    Última sincronización: {new Date(healthKit.lastSync).toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Biometric Data Grid */}
          {healthKit.biometrics && (
            <Card title="Datos Biométricos" icon="💓" accent="#ef4444">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
                {[
                  { label: "FC", value: healthKit.biometrics.heartRate, unit: "bpm", icon: "❤️", good: v => v > 0 && v < 100 },
                  { label: "HRV", value: healthKit.biometrics.hrv, unit: "ms", icon: "📈", good: v => v > 40 },
                  { label: "FC Reposo", value: healthKit.biometrics.restingHeartRate, unit: "bpm", icon: "💤", good: v => v > 0 && v < 65 },
                  { label: "SpO₂", value: healthKit.biometrics.spo2, unit: "%", icon: "🫁", good: v => v > 95 },
                  { label: "VO₂ Max", value: healthKit.biometrics.vo2Max, unit: "mL/kg·min", icon: "🏃", good: v => v > 35 },
                  { label: "Resp", value: healthKit.biometrics.respiratoryRate, unit: "/min", icon: "🌬", good: v => v > 10 && v < 20 },
                  { label: "Temp", value: healthKit.biometrics.bodyTemperature, unit: "°C", icon: "🌡", good: v => v > 36 && v < 37.5 },
                  { label: "Sueño", value: healthKit.biometrics.sleepHours, unit: "h", icon: "😴", good: v => v >= 7, decimals: 1 },
                  { label: "Ejercicio", value: healthKit.biometrics.exerciseMinutes, unit: "min", icon: "🏋️", good: v => v >= 30 },
                  { label: "Pasos", value: healthKit.biometrics.stepCount, unit: "", icon: "👣", good: v => v >= 8000, decimals: 0 },
                  { label: "Energía", value: healthKit.biometrics.activeEnergy, unit: "kcal", icon: "⚡", good: v => v >= 200, decimals: 0 },
                  { label: "Mindful", value: healthKit.biometrics.mindfulMinutes, unit: "min", icon: "🧘", good: v => v >= 10 },
                  { label: "Ruido", value: healthKit.biometrics.noiseExposure, unit: "dB", icon: "🔊", good: v => v > 0 && v < 80 },
                  { label: "Asimetría", value: healthKit.biometrics.walkingAsymmetry, unit: "%", icon: "🚶", good: v => v > 0 && v < 10 },
                ].map((m, i) => (
                  <div key={i} style={{
                    padding: 10, background: COLORS.surface2, borderRadius: 10, textAlign: "center",
                    border: `1px solid ${m.value > 0 && m.good(m.value) ? "#10b98130" : m.value > 0 ? "#ef444430" : COLORS.border}`,
                    animation: `fadeInUp 0.3s ease ${i * 0.03}s both`,
                  }}>
                    <div style={{ fontSize: 14, marginBottom: 2 }}>{m.icon}</div>
                    <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 2 }}>{m.label}</div>
                    <div style={{
                      fontSize: 16, fontWeight: 700, fontFamily: fonts.mono,
                      color: m.value <= 0 ? COLORS.textMuted : m.good(m.value) ? "#10b981" : "#f59e0b",
                    }}>
                      {m.value <= 0 ? "—" : (m.decimals != null ? m.value.toFixed(m.decimals) : m.value.toFixed(1))}
                    </div>
                    <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textMuted }}>{m.unit}</div>
                  </div>
                ))}
              </div>
              {/* Sleep stages */}
              {healthKit.biometrics.sleepStages && healthKit.biometrics.sleepHours > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 6 }}>Etapas de Sueño</div>
                  <div style={{ display: "flex", gap: 4, height: 20, borderRadius: 6, overflow: "hidden" }}>
                    {[
                      { key: "deep", label: "Profundo", color: "#3b82f6" },
                      { key: "core", label: "Core", color: "#8b5cf6" },
                      { key: "rem", label: "REM", color: "#ec4899" },
                      { key: "awake", label: "Despierto", color: "#f59e0b" },
                    ].map(stage => {
                      const hours = healthKit.biometrics.sleepStages[stage.key] || 0;
                      const pct = healthKit.biometrics.sleepHours > 0 ? (hours / healthKit.biometrics.sleepHours) * 100 : 0;
                      return pct > 0 ? (
                        <div key={stage.key} style={{ width: `${pct}%`, background: stage.color, position: "relative" }} title={`${stage.label}: ${(hours * 60).toFixed(0)} min`} />
                      ) : null;
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 4, justifyContent: "center" }}>
                    {[
                      { key: "deep", label: "Profundo", color: "#3b82f6" },
                      { key: "core", label: "Core", color: "#8b5cf6" },
                      { key: "rem", label: "REM", color: "#ec4899" },
                      { key: "awake", label: "Despierto", color: "#f59e0b" },
                    ].map(stage => (
                      <div key={stage.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <div style={{ width: 6, height: 6, borderRadius: 2, background: stage.color }} />
                        <span style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                          {stage.label}: {((healthKit.biometrics.sleepStages[stage.key] || 0) * 60).toFixed(0)}m
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Auto-populated Dimensions */}
          {healthKit.autoPopulatedDimensions && (
            <Card title="Dimensiones Auto-Pobladas" icon="🔄" accent="#06b6d4">
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
                {t('misc.watchSuggested')}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 8 }}>
                {Object.entries(healthKit.autoPopulatedDimensions).filter(([, v]) => v !== null).map(([key, value]) => {
                  const current = state[key] || 0;
                  const diff = value - current;
                  return (
                    <div key={key} style={{
                      padding: 10, background: COLORS.surface2, borderRadius: 8,
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      border: `1px solid ${Math.abs(diff) > 0.15 ? "#f59e0b40" : COLORS.border}`,
                    }}>
                      <div>
                        <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 500 }}>{key}</div>
                        <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                          {t('misc.actualToWatch', { actual: (current * 100).toFixed(0), watch: (value * 100).toFixed(0) })}
                        </div>
                      </div>
                      <button onClick={() => updateDim(key, value)} style={{
                        padding: "4px 10px", borderRadius: 6, border: `1px solid #06b6d440`, cursor: "pointer",
                        fontFamily: fonts.mono, fontSize: 8, color: "#06b6d4", background: "#06b6d410",
                      }}>
                        {t('misc.apply')}
                      </button>
                    </div>
                  );
                })}
              </div>
              {Object.entries(healthKit.autoPopulatedDimensions).filter(([, v]) => v !== null).length > 0 && (
                <button onClick={() => {
                  Object.entries(healthKit.autoPopulatedDimensions).forEach(([key, value]) => {
                    if (value !== null) updateDim(key, value);
                  });
                }} style={{
                  marginTop: 12, width: "100%", padding: "10px 20px", borderRadius: 10, border: "none", cursor: "pointer",
                  fontFamily: fonts.mono, fontSize: 11, fontWeight: 600,
                  background: "linear-gradient(135deg, #06b6d430, #8b5cf620)", color: "#06b6d4",
                }}>
                  {t('misc.applyAllWatch')}
                </button>
              )}
            </Card>
          )}

          {/* Watch Engine Results */}
          {watchEngine && (
            <>
              {/* Policy Override (critical health) */}
              {watchEngine.policyOverride.active && (
                <Card title="⚠ ALERTA BIOMÉTRICA" accent="#ef4444" style={{ borderColor: "#ef444440", background: "#ef444410" }}>
                  <div style={{ textAlign: "center", padding: 12 }}>
                    <div style={{ fontSize: 28, fontWeight: 700, fontFamily: fonts.mono, color: "#ef4444" }}>{watchEngine.policyOverride.verdict}</div>
                    <div style={{ fontSize: 11, fontFamily: fonts.mono, color: "#ef4444", marginTop: 8 }}>{watchEngine.policyOverride.reason}</div>
                    <div style={{ marginTop: 12 }}>
                      {watchEngine.policyOverride.sources.map((src, i) => (
                        <div key={i} style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, padding: "2px 0" }}>• {src}</div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* Levin Biometric Signals */}
              {watchEngine.levinSignals.length > 0 && (
                <Card title="Señales Levin Biométricas" icon="⚡" accent="#f59e0b">
                  {watchEngine.levinSignals.map((sig, i) => (
                    <div key={sig.id} style={{
                      padding: 10, background: COLORS.surface2, borderRadius: 8, marginBottom: 8,
                      borderLeft: `3px solid ${sig.severity > 0.7 ? "#ef4444" : sig.severity > 0.4 ? "#f59e0b" : "#06b6d4"}`,
                      animation: `slideIn 0.3s ease ${i * 0.05}s both`,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 500 }}>{sig.label}</span>
                        <span style={{
                          fontSize: 9, fontFamily: fonts.mono, padding: "2px 8px", borderRadius: 4,
                          background: sig.severity > 0.7 ? "#ef444420" : sig.severity > 0.4 ? "#f59e0b20" : "#06b6d420",
                          color: sig.severity > 0.7 ? "#ef4444" : sig.severity > 0.4 ? "#f59e0b" : "#06b6d4",
                        }}>
                          {(sig.severity * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 4 }}>{sig.description}</div>
                      <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textDim, marginTop: 2 }}>Fuente: {sig.source}</div>
                    </div>
                  ))}
                </Card>
              )}

              {/* Hoffman Biometric Biases */}
              {watchEngine.hoffmanBiases.length > 0 && (
                <Card title="Sesgos Hoffman — Subjetivo vs Objetivo" icon="👁" accent="#a855f7">
                  <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 10, lineHeight: 1.5 }}>
                    Incongruencias detectadas entre tu percepción subjetiva y los datos objetivos del Watch.
                  </div>
                  {watchEngine.hoffmanBiases.map((bias, i) => (
                    <div key={bias.id} style={{
                      padding: 10, background: COLORS.surface2, borderRadius: 8, marginBottom: 8,
                      borderLeft: `3px solid #a855f7`,
                      animation: `slideIn 0.3s ease ${i * 0.05}s both`,
                    }}>
                      <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 500 }}>{bias.label}</div>
                      <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>Subjetivo</div>
                          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: fonts.mono, color: "#f59e0b" }}>{(bias.subjectiveValue * 100).toFixed(0)}%</div>
                        </div>
                        <div style={{ alignSelf: "center", fontSize: 12, color: COLORS.textMuted }}>≠</div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>Watch</div>
                          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: fonts.mono, color: "#06b6d4" }}>{(bias.objectiveValue * 100).toFixed(0)}%</div>
                        </div>
                        <div style={{ alignSelf: "center", flex: 1 }}>
                          <div style={{ height: 4, background: COLORS.surface3, borderRadius: 2, overflow: "hidden" }}>
                            <div style={{ width: `${bias.magnitude * 100}%`, height: "100%", background: "#a855f7", borderRadius: 2 }} />
                          </div>
                          <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>Magnitud: {(bias.magnitude * 100).toFixed(0)}%</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 6 }}>{bias.description}</div>
                    </div>
                  ))}
                </Card>
              )}

              {/* Engine Layer Modulators */}
              <Card title={t('misc.engineModulators')} icon="⚙" accent={COLORS.textDim}>
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: 10 }}>
                  <div style={{ padding: 10, background: COLORS.surface2, borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>Free Energy Mod</div>
                    <div style={{ fontSize: 18, fontWeight: 700, fontFamily: fonts.mono, color: watchEngine.freeEnergyMod.totalMod >= 0 ? "#f59e0b" : "#10b981" }}>
                      {watchEngine.freeEnergyMod.totalMod >= 0 ? "+" : ""}{(watchEngine.freeEnergyMod.totalMod * 100).toFixed(1)}%
                    </div>
                    <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textMuted }}>SpO₂ + HR Recovery</div>
                  </div>
                  <div style={{ padding: 10, background: COLORS.surface2, borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>Coherence Mod</div>
                    <div style={{ fontSize: 18, fontWeight: 700, fontFamily: fonts.mono, color: watchEngine.coherenceMod.totalMod >= 0 ? "#10b981" : "#f59e0b" }}>
                      {watchEngine.coherenceMod.totalMod >= 0 ? "+" : ""}{(watchEngine.coherenceMod.totalMod * 100).toFixed(1)}%
                    </div>
                    <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textMuted }}>Ruido ambiental</div>
                  </div>
                  <div style={{ padding: 10, background: COLORS.surface2, borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>Watson Shifts</div>
                    <div style={{ fontSize: 18, fontWeight: 700, fontFamily: fonts.mono, color: COLORS.text }}>
                      {watchEngine.watsonShifts.length}
                    </div>
                    <div style={{ fontSize: 7, fontFamily: fonts.mono, color: COLORS.textMuted }}>Desplazamientos</div>
                  </div>
                </div>
                {watchEngine.watsonShifts.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    {watchEngine.watsonShifts.map((ws, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", fontSize: 9, fontFamily: fonts.mono, borderBottom: `1px solid ${COLORS.border}` }}>
                        <span style={{ color: COLORS.textDim }}>{ws.domain} ← {ws.source}</span>
                        <span style={{ color: ws.shift >= 0 ? "#10b981" : "#ef4444", fontWeight: 600 }}>
                          {ws.shift >= 0 ? "+" : ""}{(ws.shift * 100).toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}

          {/* ECG Results */}
          {healthKit.ecg && healthKit.ecg.ecgResults.length > 0 && (
            <Card title="Resultados ECG" icon="📉" accent="#3b82f6">
              {healthKit.ecg.ecgResults.map((ecg, i) => (
                <div key={i} style={{
                  padding: 8, background: COLORS.surface2, borderRadius: 8, marginBottom: 6,
                  borderLeft: `3px solid ${ecg.classification === "atrialFibrillation" ? "#ef4444" : ecg.classification === "sinusRhythm" ? "#10b981" : "#f59e0b"}`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.text }}>
                      {ecg.classification === "sinusRhythm" ? "✅ Ritmo Sinusal" :
                       ecg.classification === "atrialFibrillation" ? "⚠️ Fibrilación Auricular" :
                       `⚡ ${ecg.classification}`}
                    </span>
                    <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>
                      {ecg.averageHeartRate > 0 ? `${ecg.averageHeartRate.toFixed(0)} bpm` : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>
                    {new Date(ecg.date).toLocaleString()}
                  </div>
                </div>
              ))}
            </Card>
          )}

          {/* Info Box */}
          <div style={{ padding: 12, background: `${COLORS.accent}08`, borderRadius: 10, border: `1px solid ${COLORS.accent}20`, fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, lineHeight: 1.6 }}>
            <div style={{ color: COLORS.accent, fontWeight: 600, marginBottom: 4, fontSize: 10 }}>{t('misc.watchIntegrationTitle')}</div>
            <strong>{t('misc.autoPopulation')}:</strong> {t('misc.watchAutoPopulation')}<br />
            <strong>{t('dashboard.signals')}:</strong> {t('misc.watchLevinSignals')}<br />
            <strong>{t('dashboard.biases')}:</strong> {t('misc.watchHoffmanBiases')}<br />
            <strong>{t('misc.modulators')}:</strong> {t('misc.watchModulators')}<br /><br />
            <span style={{ color: COLORS.gold }}>{t('misc.watchDisclaimer')}</span>
          </div>
        </div>
      )}

      {/* METRIC DETAIL PANEL OVERLAY */}
      {detailPanel && <MetricDetailPanel metric={detailPanel} onClose={() => setDetailPanel(null)} />}

      {/* LOGOS VOICE AGENT */}
      <LogosVoiceAgent
        sliderValues={state}
        metrics={{ lambda: Lambda, omega: Omega, V, S, C, F, Q }}
        verdict={pi.verdict}
        activeTab={activeTab}
        history={history}
        sessionHistory={sessionHistory}
        user={user}
        accessToken={accessToken}
        onUpdateSlider={(variable, value) => updateDim(variable, value)}
        onNavigateTab={(tab) => setActiveTab(tab)}
        onHighlightDomain={(domain) => {
          setHighlightedDomain(domain);
          if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
          highlightTimerRef.current = setTimeout(() => setHighlightedDomain(null), 3000);
        }}
        onRunSimulation={handleRunMC}
      />

      {/* LOGOS v5.0: Emergence Reporter Modal */}
      <EmergenceReporter
        visible={showEmergenceReporter}
        onSubmit={async (category, impact, description, goalAssociation, latencyS) => {
          try {
            const stateSnapshot = {
              ...state,
              lambda: Lambda, omega: Omega, viability: V, entropy: S,
              coherence: C, freeEnergy: F, trajectory: Q, ers,
            };
            const { supabase } = await import('../services/supabase');
            await supabase.from('emergence_events').insert({
              user_id: user?.id,
              category, had_emergence: true, impact,
              description: description || null,
              goal_association: goalAssociation || null,
              state_snapshot: stateSnapshot,
              ers_at_event: ers,
              lambda_at_event: Lambda || 0,
              report_latency_s: latencyS,
            });
            console.log('✓ Emergence event recorded:', category, 'impact:', impact);
          } catch (e) {
            console.error('Failed to record emergence:', e);
          }
          setShowEmergenceReporter(false);
        }}
        onSkip={async (latencyS) => {
          try {
            const stateSnapshot = {
              ...state,
              lambda: Lambda, omega: Omega, viability: V, entropy: S,
              coherence: C, freeEnergy: F, ers,
            };
            const { supabase } = await import('../services/supabase');
            await supabase.from('emergence_events').insert({
              user_id: user?.id,
              category: null, had_emergence: false, impact: 0,
              state_snapshot: stateSnapshot,
              ers_at_event: ers,
              lambda_at_event: Lambda || 0,
              report_latency_s: latencyS,
            });
            console.log('✓ No-emergence event recorded');
          } catch (e) {
            console.error('Failed to record no-emergence:', e);
          }
          setShowEmergenceReporter(false);
        }}
        onClose={() => setShowEmergenceReporter(false)}
        currentState={{
          lambda: Lambda || 0.5, viability: V || 0.5,
          coherence: C || 0.5, entropy: S || 0.5,
        }}
      />

      {/* FOOTER */}
      <div style={{ marginTop: 32, textAlign: "center", fontFamily: fonts.mono, fontSize: 8, color: COLORS.textMuted, letterSpacing: 1, lineHeight: 2.2, animation: "fadeInUp 0.5s ease 0.5s both" }}>
        {t('footer.line1')}<br />
        {t('footer.line2')}<br />
        {t('footer.line3')}<br />
        {t('footer.line4')}<br />
        {t('footer.line5')}<br />
        <span style={{ color: COLORS.logos, opacity: 0.7 }}>{t('footer.author')}</span><br />
        <span style={{ color: COLORS.gold, opacity: 0.5 }}>{t('footer.quote')}</span>
      </div>
    </div>
  );
}
